<!-- Insurance Console - Dashboard Principal de Seguros -->
<div class="min-h-screen bg-gray-50 p-4 lg:p-6">

  <!-- Header Section -->
  <div class="mb-8">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between">

      <!-- Title and Description -->
      <div class="mb-4 lg:mb-0">
        <h1 class="text-3xl font-bold text-gray-900 mb-2">
          📊 Consola Administrativa de Seguros
        </h1>
        <p class="text-gray-600">
          Panel completo de métricas, analíticas y gestión del sistema de seguros
        </p>
      </div>

      <!-- Action Buttons -->
      <div class="flex flex-col sm:flex-row gap-3">

        <!-- Insurer Filter Button -->
        <div class="relative">
          <button (click)="toggleInsurerDropdown()"
            class="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            [class.ring-2]="selectedInsurerCode" [class.ring-blue-500]="selectedInsurerCode">
            <i class="fas fa-building text-gray-500"></i>
            <span class="text-sm font-medium text-gray-700">
              {{ getSelectedInsurerName() }}
            </span>
            <i class="fas fa-chevron-down text-gray-400 text-xs"></i>
          </button>

          <!-- Insurer Dropdown -->
          <div *ngIf="showInsurerDropdown"
            class="absolute left-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto">
            <div class="p-2">
              <h3 class="text-sm font-semibold text-gray-900 mb-2 px-2">Filtrar por aseguradora</h3>

              <!-- All insurers option -->
              <button (click)="selectInsurer(null)"
                [class]="!selectedInsurerCode 
                  ? 'w-full px-3 py-2 text-sm text-left bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors duration-200'
                  : 'w-full px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 rounded-md transition-colors duration-200'">
                <i class="fas fa-globe mr-2"></i>
                Todas las aseguradoras
              </button>

              <!-- Loading state -->
              <div *ngIf="isLoadingInsurers" class="px-3 py-4 text-center">
                <i class="fas fa-spinner fa-spin text-gray-400"></i>
                <p class="text-xs text-gray-500 mt-2">Cargando aseguradoras...</p>
              </div>

              <!-- Insurer list -->
              <div *ngIf="!isLoadingInsurers && insurers.length > 0" class="mt-1">
                <button *ngFor="let insurer of insurers" (click)="selectInsurer(insurer.code)"
                  [class]="selectedInsurerCode === insurer.code 
                    ? 'w-full px-3 py-2 text-sm text-left bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100 transition-colors duration-200'
                    : 'w-full px-3 py-2 text-sm text-left text-gray-700 hover:bg-gray-100 rounded-md transition-colors duration-200'">
                  <i class="fas fa-building mr-2"></i>
                  {{ insurer.name }}
                </button>
              </div>

              <!-- Empty state -->
              <div *ngIf="!isLoadingInsurers && insurers.length === 0" class="px-3 py-4 text-center">
                <i class="fas fa-exclamation-circle text-gray-400 text-2xl"></i>
                <p class="text-xs text-gray-500 mt-2">No hay aseguradoras disponibles</p>
              </div>

              <!-- Clear filter button -->
              <div *ngIf="selectedInsurerCode" class="mt-2 border-t pt-2">
                <button (click)="clearInsurerFilter()"
                  class="w-full px-3 py-2 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors duration-200">
                  <i class="fas fa-times mr-2"></i>
                  Limpiar filtro
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Date Filter Button -->
        <div class="relative">
          <button (click)="toggleDatePicker()"
            class="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-200">
            <i class="fas fa-calendar-alt text-gray-500"></i>
            <span class="text-sm font-medium text-gray-700">
              {{ getCurrentDateRangeText() }}
            </span>
            <i class="fas fa-chevron-down text-gray-400 text-xs"></i>
          </button>

          <!-- Date Picker Dropdown -->
          <div *ngIf="showDatePicker"
            class="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            <div class="p-4">
              <h3 class="text-sm font-semibold text-gray-900 mb-3">Filtrar por fecha</h3>

              <!-- Date Presets -->
              <div class="grid grid-cols-2 gap-2 mb-4">
                <button *ngFor="let preset of datePresets | keyvalue"
                  (click)="applyDatePreset(preset.value, preset.key)" [class]="selectedPreset === preset.key 
                    ? 'px-3 py-2 text-xs bg-blue-500 text-white rounded-md transition-colors duration-200'
                    : 'px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-md transition-colors duration-200'">
                  {{ preset.value.label }}
                </button>
              </div>

              <!-- Custom Date Range -->
              <div class="border-t pt-3">
                <div class="grid grid-cols-2 gap-2 mb-3">
                  <div>
                    <label class="block text-xs text-gray-600 mb-1">Fecha inicio</label>
                    <input #startDate type="date"
                      class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  </div>
                  <div>
                    <label class="block text-xs text-gray-600 mb-1">Fecha fin</label>
                    <input #endDate type="date"
                      class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  </div>
                </div>
                <div class="flex gap-2">
                  <button (click)="applyCustomDateRange(startDate.value, endDate.value)"
                    class="flex-1 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors duration-200">
                    Aplicar
                  </button>
                  <button (click)="clearDateFilter()"
                    class="px-3 py-1 text-xs text-gray-600 hover:text-gray-800 transition-colors duration-200">
                    Limpiar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Refresh Button -->
        <button (click)="refreshAllData()"
          class="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
          [disabled]="(loading$ | async)">
          <i class="fas fa-sync-alt" [class.fa-spin]="(loading$ | async)"></i>
          <span class="text-sm font-medium">Actualizar</span>
        </button>

        <!-- Export Button -->
      </div>
    </div>

    <!-- Last Update Info -->
    <div class="mt-4 flex items-center gap-2 text-xs text-gray-500">
      <i class="fas fa-clock"></i>
      <span>Última actualización: {{ formatDateTime(currentTime) }}</span>
    </div>
  </div>

  <!-- Navigation Tabs -->
  <div class="mb-6">
    <div class="border-b border-gray-200">
      <nav class="-mb-px flex space-x-8">

        <button (click)="setActiveTab('dashboard')"
          [class]="activeTab === 'dashboard' 
            ? 'border-blue-500 text-blue-600 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm'">
          <i class="fas fa-tachometer-alt mr-2"></i>
          Dashboard Principal
        </button>

        <!-- Pólizas Dropdown -->
        <div class="relative inline-block">
          <button (click)="togglePoliciesDropdown()"
            [class]="(activeTab === 'policies' || activeTab === 'annulled_policies')
              ? 'border-blue-500 text-blue-600 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center'">
            <i class="fas fa-file-alt mr-2"></i>
            Pólizas
            <i class="fas fa-chevron-down ml-1 text-xs"></i>
          </button>
          
          <div *ngIf="showPoliciesDropdown"
            class="absolute left-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            <div class="py-1">
              <button (click)="setActiveTab('policies')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                <i class="fas fa-file-alt mr-2 text-blue-500"></i>
                Gestión de Pólizas
              </button>
              <button (click)="setActiveTab('annulled_policies')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                <i class="fas fa-ban mr-2 text-red-500"></i>
                Pólizas Anuladas
              </button>
              <button (click)="setActiveTab('default_address')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                <i class="fas fa-map-marker-alt mr-2 text-green-500"></i>
                Emitidas con Dirección Por Defecto
              </button>
            </div>
          </div>
        </div>

        <!-- Errores Dropdown -->
        <div class="relative inline-block">
          <button (click)="toggleErrorsDropdown()"
            [class]="(activeTab === 'emission_errors' || activeTab === 'payment_errors')
              ? 'border-red-500 text-red-600 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center'">
            <i class="fas fa-exclamation-circle mr-2"></i>
            Errores
            <i class="fas fa-chevron-down ml-1 text-xs"></i>
          </button>
          
          <div *ngIf="showErrorsDropdown"
            class="absolute left-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            <div class="py-1">
              <button (click)="setActiveTab('emission_errors')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                <i class="fas fa-exclamation-triangle mr-2 text-red-500"></i>
                Errores de Emisión
              </button>
              <button (click)="setActiveTab('payment_errors')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                <i class="fas fa-dollar-sign mr-2 text-orange-500"></i>
                Errores de Pagos
              </button>
              <button (click)="setActiveTab('verification_errors')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                <i class="fas fa-user-times mr-2 text-yellow-500"></i>
                Errores de Verificación
              </button>
              <button (click)="setActiveTab('vehicle_use_errors')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                <i class="fas fa-car mr-2 text-purple-500"></i>
                Errores de Uso
              </button>
            </div>
          </div>
        </div>

        <!-- Sincronización Dropdown — las opciones se filtran según la
             aseguradora seleccionada del filtro general (selectedInsurerCode).
             Si no hay ninguna opción visible para el filtro actual, el botón
             completo se oculta (caso futuro: aseguradora sin sync propio). -->
        <div *ngIf="hasAnyVisibleSyncTab()" class="relative inline-block">
          <button (click)="toggleSyncDropdown()"
            [class]="(activeTab === 'sync_mercantil' || activeTab === 'sync_caracas')
              ? 'border-purple-500 text-purple-600 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center'">
            <i class="fas fa-sync-alt mr-2"></i>
            Sincronización
            <i class="fas fa-chevron-down ml-1 text-xs"></i>
          </button>

          <div *ngIf="showSyncDropdown"
            class="absolute left-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            <div class="py-1">
              <button *ngIf="isSyncTabVisible('sync_mercantil')"
                (click)="setActiveTab('sync_mercantil')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                <i class="fas fa-university mr-2 text-blue-500"></i>
                Cuotas Mercantil
              </button>
              <button *ngIf="isSyncTabVisible('sync_caracas')"
                (click)="setActiveTab('sync_caracas')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                <i class="fas fa-file-invoice-dollar mr-2 text-purple-500"></i>
                Relaciones Caracas
              </button>
              <p *ngIf="!isSyncTabVisible('sync_mercantil') && !isSyncTabVisible('sync_caracas')"
                 class="px-4 py-2 text-xs text-gray-400 italic">
                Sin sincronización disponible para esta aseguradora.
              </p>
            </div>
          </div>
        </div>

        <button (click)="setActiveTab('analytics')"
          [class]="activeTab === 'analytics' 
            ? 'border-blue-500 text-blue-600 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm'">
          <i class="fas fa-chart-bar mr-2"></i>
          Analítica Avanzada
        </button>

        <!-- Reporte de Pagos a la Aseguradora -->
        <button *ngIf="_auth.hasAnyPermission(['seguros.reporte_pagos.read', 'config.seguros.read'])"
          (click)="setActiveTab('payment_reports')"
          [class]="activeTab === 'payment_reports'
            ? 'border-blue-500 text-blue-600 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm'
            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm'">
          <i class="fas fa-file-invoice-dollar mr-2"></i>
          Reporte de Pagos
        </button>

        <!-- Configuración Dropdown -->
        <div class="relative inline-block">
          <button (click)="toggleConfigDropdown()"
            [class]="(activeTab === 'config' || activeTab === 'products')
              ? 'border-gray-500 text-gray-600 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center'">
            <i class="fas fa-cog mr-2"></i>
            Configuración
            <i class="fas fa-chevron-down ml-1 text-xs"></i>
          </button>
          
          <div *ngIf="showConfigDropdown"
            class="absolute left-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            <div class="py-1">
              <button (click)="setActiveTab('config')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                <i class="fas fa-building mr-2 text-blue-500"></i>
                Aseguradoras
              </button>
              <button (click)="setActiveTab('products')"
                class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                <i class="fas fa-box mr-2 text-green-500"></i>
                Productos
              </button>
            </div>
          </div>
        </div>

      </nav>
    </div>
  </div>

  <!-- Content Area -->
  <div class="space-y-6">

    <!-- ===================================================================== -->
    <!-- Reporte de Pagos de Seguros (reporte manual + trazabilidad)           -->
    <!-- ===================================================================== -->
    <!-- =================================================================== -->
    <!-- Contacts Tab — drill-down de "Distribución por Estado"               -->
    <!-- Vista que lista personas en un policy_status con sus datos de        -->
    <!-- contacto y acciones rápidas (llamar / WhatsApp / email).             -->
    <!-- =================================================================== -->
    <div *ngIf="activeTab === 'contacts'" class="space-y-4">
      <!-- Header con back button + título contextual -->
      <div class="flex items-center justify-between flex-wrap gap-2">
        <div class="flex items-center gap-2">
          <button (click)="setActiveTab('dashboard')" type="button"
                  class="text-blue-600 hover:text-blue-800 text-sm font-medium">
            <i class="fas fa-arrow-left mr-1"></i>Dashboard
          </button>
          <span class="text-gray-300">·</span>
          <h2 class="text-lg font-semibold text-gray-800">
            <i class="fas fa-address-book mr-2 text-blue-600"></i>Contactos · {{ contactsActiveTitle }}
          </h2>
        </div>
        <button (click)="exportContactsCsv()" type="button"
                [disabled]="!contactsData?.data?.length"
                class="px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed">
          <i class="fas fa-file-csv mr-1"></i>Exportar CSV
        </button>
      </div>

      <!-- Banner con conteo + filtros activos -->
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
        <ng-container *ngIf="contactsData">
          <strong>{{ contactsData.pagination.totalItems | number }}</strong> personas
          (página {{ contactsData.pagination.page }} de {{ contactsData.pagination.totalPages }})
        </ng-container>
        <ng-container *ngIf="!contactsData && !contactsLoading">—</ng-container>
        <ng-container *ngIf="contactsFilters.notification_status">
          <span class="ml-2 px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full">
            notif: {{ contactsFilters.notification_status }}
          </span>
        </ng-container>
        <ng-container *ngIf="contactsFilters.plan_type">
          <span class="ml-2 px-2 py-0.5 bg-purple-100 text-purple-800 rounded-full">
            tipo: {{ contactsFilters.plan_type }}
          </span>
        </ng-container>
        <ng-container *ngIf="contactsFilters.frequency">
          <span class="ml-2 px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded-full">
            freq: {{ contactsFilters.frequency }}
          </span>
        </ng-container>
      </div>

      <!-- Filtros adicionales -->
      <div class="bg-white rounded-lg shadow p-3 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
        <div>
          <label class="block text-gray-600 mb-1">Búsqueda (nombre/cédula/email)</label>
          <input type="text" [(ngModel)]="contactsFilters.search" (keyup.enter)="loadContacts()"
                 class="w-full border rounded px-2 py-1.5">
        </div>
        <div>
          <label class="block text-gray-600 mb-1">Días sin actividad ≥</label>
          <input type="number" min="0" [(ngModel)]="contactsFilters.min_days_inactive"
                 (change)="loadContacts()"
                 class="w-full border rounded px-2 py-1.5">
        </div>
        <div class="flex items-center gap-3 mt-5">
          <label class="flex items-center gap-1.5">
            <input type="checkbox" [(ngModel)]="contactsFilters.has_phone" (change)="loadContacts()">
            <span class="text-gray-700">Solo con teléfono</span>
          </label>
          <label class="flex items-center gap-1.5">
            <input type="checkbox" [(ngModel)]="contactsFilters.has_email" (change)="loadContacts()">
            <span class="text-gray-700">Solo con email</span>
          </label>
        </div>
        <div class="flex items-end">
          <button (click)="loadContacts()" type="button"
                  class="w-full bg-blue-600 text-white py-1.5 rounded hover:bg-blue-700">
            <i class="fas fa-search mr-1"></i>Buscar
          </button>
        </div>
      </div>

      <!-- Loading / Error -->
      <div *ngIf="contactsLoading" class="text-center py-8 text-gray-400">
        <i class="fas fa-circle-notch fa-spin mr-2"></i>Cargando...
      </div>
      <div *ngIf="contactsError" class="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">
        {{ contactsError }}
      </div>

      <!-- Tabla -->
      <div *ngIf="!contactsLoading && contactsData?.data?.length" class="bg-white rounded-lg shadow overflow-x-auto">
        <table class="min-w-full text-xs">
          <thead class="bg-gray-50 border-b">
            <tr class="text-left text-gray-600">
              <th class="p-2">Cliente</th>
              <th class="p-2">Cédula</th>
              <th class="p-2">Teléfono</th>
              <th class="p-2">Email</th>
              <th class="p-2">Aseguradora</th>
              <th class="p-2">Días inactivo</th>
              <th class="p-2 text-center">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of contactsData?.data" class="border-b hover:bg-gray-50">
              <td class="p-2">
                <p class="font-medium text-gray-800">{{ row.insured?.full_name || '—' }}</p>
                <p class="text-gray-500 text-[10px]">{{ row.policy_code }}</p>
              </td>
              <td class="p-2">{{ row.insured?.identity || '—' }}</td>
              <td class="p-2">{{ row.insured?.phone || '—' }}</td>
              <td class="p-2 truncate max-w-[180px]" [title]="row.insured?.email">{{ row.insured?.email || '—' }}</td>
              <td class="p-2">{{ row.insurer_name || '—' }}</td>
              <td class="p-2">
                <span class="px-2 py-0.5 rounded-full"
                      [ngClass]="row.days_inactive >= 30 ? 'bg-red-100 text-red-700' : (row.days_inactive >= 7 ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700')">
                  {{ row.days_inactive }}d
                </span>
              </td>
              <td class="p-2">
                <div class="flex items-center gap-1 justify-center">
                  <a *ngIf="row.insured?.phone" [href]="'tel:' + row.insured.phone"
                     class="px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200" title="Llamar">
                    <i class="fas fa-phone"></i>
                  </a>
                  <a *ngIf="row.insured?.phone" [href]="buildWhatsAppLink(row.insured, row.policy_status)"
                     target="_blank" rel="noopener"
                     class="px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200" title="WhatsApp">
                    <i class="fab fa-whatsapp"></i>
                  </a>
                  <a *ngIf="row.insured?.email" [href]="buildMailtoLink(row.insured, row.policy_status)"
                     class="px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200" title="Email">
                    <i class="fas fa-envelope"></i>
                  </a>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Sin resultados -->
      <div *ngIf="!contactsLoading && contactsData && !contactsData.data.length"
           class="bg-white rounded-lg shadow p-8 text-center text-gray-400">
        <i class="fas fa-inbox text-3xl mb-2"></i>
        <p>No hay contactos que cumplan los filtros.</p>
      </div>

      <!-- Paginación -->
      <ng-container *ngIf="contactsData as cd">
        <div *ngIf="cd.data?.length" class="flex items-center justify-between text-xs">
          <p class="text-gray-500">
            Mostrando {{ ((cd.pagination.page - 1) * cd.pagination.limit) + 1 }}-{{
              mathMin(cd.pagination.page * cd.pagination.limit, cd.pagination.totalItems) | number
            }} de {{ cd.pagination.totalItems | number }}
          </p>
          <div class="flex items-center gap-2">
            <button (click)="contactsGoToPage(contactsPage - 1)" [disabled]="contactsPage <= 1"
                    class="px-2 py-1 bg-gray-100 rounded disabled:opacity-40">
              <i class="fas fa-chevron-left"></i>
            </button>
            <span>Página {{ cd.pagination.page }} / {{ cd.pagination.totalPages }}</span>
            <button (click)="contactsGoToPage(contactsPage + 1)"
                    [disabled]="contactsPage >= cd.pagination.totalPages"
                    class="px-2 py-1 bg-gray-100 rounded disabled:opacity-40">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </ng-container>
    </div>

    <div *ngIf="activeTab === 'payment_reports'" class="space-y-6">

      <!-- Gating: contenido solo aplica a Mercantil. SC redirige a empty state. -->
      <ng-container *ngIf="selectedInsurerCode !== 'SC'; else paymentReportsSC">

      <!-- Encabezado + actor logueado -->
      <div class="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 class="text-lg font-semibold text-gray-800">
            <i class="fas fa-file-invoice-dollar mr-2 text-blue-600"></i>Reporte de Pagos a la Aseguradora
          </h2>
          <p class="text-sm text-gray-500">Gestión y reporte manual de pagos de domiciliación de seguros (Mercantil).</p>
        </div>
        <div class="text-xs text-gray-500">
          Sesión: <span class="font-semibold text-gray-700">{{ _auth.sessionUser?.name || '—' }}</span>
        </div>
      </div>

      <!-- Tarjetas de resumen -->
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <button (click)="prSetStatus('pending')"
          class="text-left bg-white border rounded-lg p-4 hover:shadow"
          [class.ring-2]="prFilters.status === 'pending'" [class.ring-yellow-400]="prFilters.status === 'pending'">
          <div class="text-xs text-gray-500 uppercase">Pendientes</div>
          <div class="text-2xl font-bold text-yellow-600">{{ prStats?.pending ?? '—' }}</div>
        </button>
        <button (click)="prSetStatus('reported')"
          class="text-left bg-white border rounded-lg p-4 hover:shadow"
          [class.ring-2]="prFilters.status === 'reported'" [class.ring-green-400]="prFilters.status === 'reported'">
          <div class="text-xs text-gray-500 uppercase">Reportados</div>
          <div class="text-2xl font-bold text-green-600">{{ prStats?.reported ?? '—' }}</div>
        </button>
        <button (click)="prSetStatus('failed')"
          class="text-left bg-white border rounded-lg p-4 hover:shadow"
          [class.ring-2]="prFilters.status === 'failed'" [class.ring-red-400]="prFilters.status === 'failed'">
          <div class="text-xs text-gray-500 uppercase">Fallidos</div>
          <div class="text-2xl font-bold text-red-600">{{ prStats?.permanently_failed ?? '—' }}</div>
        </button>
      </div>

      <!-- ================= PANEL DE AUDITORÍA TRANSVERSAL ================= -->
      <div class="bg-white border rounded-lg">
        <button (click)="prToggleAuditPanel()"
                class="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-gray-50 rounded-lg">
          <i class="fas fa-clipboard-check text-purple-600"></i>
          <span class="font-semibold text-gray-800">Auditoría de reportes</span>
          <span class="text-xs text-gray-500">— quién reportó qué y cuándo</span>
          <i class="fas ml-auto text-gray-400" [ngClass]="prAuditPanelOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </button>

        <div *ngIf="prAuditPanelOpen" class="border-t p-4">
          <!-- Rango -->
          <div class="flex flex-wrap items-end gap-3 mb-4">
            <div class="flex flex-wrap gap-1">
              <button *ngFor="let p of prDatePresetList; trackBy: prTrackByKey" type="button"
                      (click)="prApplyAuditPreset(p)"
                      class="px-2 py-1 text-xs rounded border border-gray-300 bg-white hover:border-purple-400 text-gray-700">
                {{ p.label }}
              </button>
            </div>
            <div>
              <label class="block text-xs text-gray-500 mb-1">Desde</label>
              <input type="date" [(ngModel)]="prAuditRange.dateFrom" (ngModelChange)="prLoadAuditSummary()"
                     class="border rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label class="block text-xs text-gray-500 mb-1">Hasta</label>
              <input type="date" [(ngModel)]="prAuditRange.dateTo" (ngModelChange)="prLoadAuditSummary()"
                     class="border rounded px-2 py-1.5 text-sm" />
            </div>
            <button (click)="prExportAuditSummary()" class="px-3 py-1.5 bg-green-600 text-white rounded text-sm">
              <i class="fas fa-file-excel mr-1"></i>Exportar
            </button>
          </div>

          <div *ngIf="prAuditSummaryLoading" class="text-center text-gray-500 py-6">
            <i class="fas fa-spinner fa-spin mr-2"></i>Cargando auditoría…
          </div>

          <ng-container *ngIf="!prAuditSummaryLoading && prAuditSummary">
            <!-- Advertencia de semántica: los conteos son de INTENTOS, no de pagos.
                 Un pago reportado tras 3 reintentos suma 1 éxito y 2 fallos. -->
            <div class="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2 mb-3">
              <i class="fas fa-info-circle mt-0.5"></i>
              <span>
                Los conteos son de <strong>intentos de reporte</strong>, no de pagos únicos:
                un pago reportado tras 3 reintentos suma 1 exitoso y 2 fallidos.
                En este periodo hubo <strong>{{ prAuditSummary.totals?.total || 0 }}</strong> intentos
                sobre <strong>{{ prAuditSummary.totals?.payments || 0 }}</strong> pagos distintos.
              </span>
            </div>

            <div class="overflow-x-auto">
              <table class="min-w-full text-sm">
                <thead class="bg-gray-50 text-gray-600">
                  <tr>
                    <th class="text-left px-3 py-2">Ejecutivo</th>
                    <th class="text-center px-3 py-2">Exitosos</th>
                    <th class="text-center px-3 py-2">Fallidos</th>
                    <th class="text-center px-3 py-2">Total</th>
                    <th class="text-left px-3 py-2">Último reporte</th>
                    <th class="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let r of prAuditSummary.by_actor" class="border-t hover:bg-gray-50">
                    <td class="px-3 py-2">
                      <span [ngClass]="r._id ? 'text-gray-800 font-medium' : 'text-gray-400 italic'">
                        {{ r.name || 'Sistema (automático)' }}
                      </span>
                      <div class="text-xs text-gray-400">{{ r.email }}</div>
                    </td>
                    <td class="px-3 py-2 text-center text-green-600 font-semibold tabular-nums">{{ r.success || 0 }}</td>
                    <td class="px-3 py-2 text-center tabular-nums" [ngClass]="r.failure ? 'text-red-600 font-semibold' : 'text-gray-300'">{{ r.failure || 0 }}</td>
                    <td class="px-3 py-2 text-center text-gray-700 tabular-nums">{{ r.total || 0 }}</td>
                    <td class="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{{ r.last | date:'dd/MM/yyyy HH:mm' }}</td>
                    <td class="px-3 py-2 text-right">
                      <button (click)="prDrillDownReporter(r)" class="text-blue-600 hover:underline text-xs whitespace-nowrap">
                        Ver sus reportes <i class="fas fa-arrow-right ml-1"></i>
                      </button>
                    </td>
                  </tr>
                  <tr *ngIf="!prAuditSummary.by_actor?.length">
                    <td colspan="6" class="px-3 py-6 text-center text-gray-400">Sin actividad en este periodo.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Origen: manual vs automático -->
            <div *ngIf="prAuditSummary.by_origin?.length" class="flex flex-wrap gap-3 mt-3 text-xs text-gray-600">
              <span *ngFor="let o of prAuditSummary.by_origin" class="px-2 py-1 bg-gray-100 rounded">
                {{ o._id === 'MANUAL' ? 'Manual' : 'Automático / sin contexto' }}: <strong>{{ o.count }}</strong>
              </span>
            </div>
          </ng-container>
        </div>
      </div>

      <!-- ================= BARRA DE FILTROS (3 capas) ================= -->
      <div class="bg-white border rounded-lg">

        <!-- CAPA 1: vistas operativas — un clic arma el filtro completo -->
        <div class="flex flex-wrap items-center gap-2 px-3 py-2 border-b bg-gray-50 rounded-t-lg">
          <span class="text-xs text-gray-500 uppercase tracking-wide mr-1">Vistas rápidas</span>
          <button (click)="prViewCriticos()"
                  class="px-3 py-1.5 rounded-full text-xs font-medium border transition"
                  [ngClass]="prActiveView === 'criticos' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-700 border-gray-300 hover:border-red-400'"
                  title="Pendientes con más de 7 días sin reportar, más antiguo primero">
            <i class="fas fa-fire mr-1"></i>Críticos por reportar
          </button>
          <button (click)="prViewPorFallar()"
                  class="px-3 py-1.5 rounded-full text-xs font-medium border transition"
                  [ngClass]="prActiveView === 'por_fallar' ? 'bg-orange-600 text-white border-orange-600' : 'bg-white text-gray-700 border-gray-300 hover:border-orange-400'"
                  title="Pendientes con 8 o más intentos: a punto de marcarse Fallido automáticamente">
            <i class="fas fa-exclamation-triangle mr-1"></i>A punto de fallar
          </button>
        </div>

        <!-- CAPA 2: barra compacta siempre visible -->
        <div class="flex flex-wrap items-center gap-2 p-3">
          <div class="flex gap-1">
            <button (click)="prSetStatus('pending')" class="px-3 py-1.5 rounded text-sm"
              [ngClass]="prFilters.status === 'pending' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'">Pendientes</button>
            <button (click)="prSetStatus('reported')" class="px-3 py-1.5 rounded text-sm"
              [ngClass]="prFilters.status === 'reported' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'">Reportados</button>
            <button (click)="prSetStatus('failed')" class="px-3 py-1.5 rounded text-sm"
              [ngClass]="prFilters.status === 'failed' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700'">Fallidos</button>
            <button (click)="prSetStatus('all')" class="px-3 py-1.5 rounded text-sm"
              [ngClass]="prFilters.status === 'all' ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-700'">Todos</button>
          </div>

          <!-- Búsqueda: aplica sola con debounce, sin botón Filtrar -->
          <div class="flex-1 min-w-[200px] relative">
            <i class="fas fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
            <input [(ngModel)]="prFilters.search" (ngModelChange)="prApplyFilters()"
              placeholder="Buscar por código, póliza, cliente o cédula…"
              class="w-full border rounded pl-7 pr-2 py-1.5 text-sm" />
          </div>

          <!-- Orden -->
          <button (click)="prToggleSort()"
                  class="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm whitespace-nowrap"
                  [title]="'Invertir orden — ' + prSortLabel">
            <i class="fas" [ngClass]="prSortIcon"></i>
            <span class="ml-1">{{ prSortLabel }}</span>
          </button>

          <!-- Toggle del panel avanzado, con contador de filtros activos -->
          <button (click)="prToggleFiltersPanel()"
                  class="px-3 py-1.5 rounded text-sm border whitespace-nowrap"
                  [ngClass]="prFiltersOpen || prActiveFilterCount ? 'border-blue-500 text-blue-700 bg-blue-50' : 'border-gray-300 text-gray-700'">
            <i class="fas fa-sliders-h mr-1"></i>Filtros
            <span *ngIf="prActiveFilterCount"
                  class="ml-1 inline-flex items-center justify-center bg-blue-600 text-white rounded-full text-xs"
                  style="min-width:18px;height:18px;">{{ prActiveFilterCount }}</span>
            <i class="fas ml-1 text-xs" [ngClass]="prFiltersOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
          </button>
        </div>

        <!-- CAPA 3: panel contextual — solo los filtros que aplican al estado actual -->
        <div *ngIf="prFiltersOpen" class="px-3 pb-3 border-t pt-3 bg-gray-50">
          <div class="flex flex-wrap items-end gap-3">

            <!-- Presets de fecha -->
            <div class="min-w-[150px]">
              <label class="block text-xs text-gray-500 mb-1">Periodo</label>
              <div class="flex flex-wrap gap-1">
                <button *ngFor="let p of prDatePresetList; trackBy: prTrackByKey" type="button"
                        (click)="prApplyDatePreset(p)"
                        class="px-2 py-1 text-xs rounded border bg-white text-gray-700"
                        [ngClass]="prIsActivePreset(p) ? 'border-blue-500 text-blue-700 bg-blue-50' : 'border-gray-300 hover:border-blue-400'">
                  {{ p.label }}
                </button>
              </div>
            </div>

            <!-- Qué fecha se filtra. La semántica es contraintuitiva respecto a los
                 nombres de campo, por eso los labels son explícitos. -->
            <div>
              <label class="block text-xs text-gray-500 mb-1">Filtrar por</label>
              <select [(ngModel)]="prFilters.dateField" (ngModelChange)="prOnDateRangeChange()"
                      class="border rounded px-2 py-1.5 text-sm bg-white">
                <option value="payment">Fecha del pago</option>
                <option value="register">Fecha de registro</option>
                <option *ngIf="prShowsReporterFilters" value="reported">Fecha de reporte</option>
              </select>
            </div>

            <div>
              <label class="block text-xs text-gray-500 mb-1">Desde</label>
              <input type="date" [(ngModel)]="prFilters.dateFrom" (ngModelChange)="prOnDateRangeChange()"
                     class="border rounded px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label class="block text-xs text-gray-500 mb-1">Hasta</label>
              <input type="date" [(ngModel)]="prFilters.dateTo" (ngModelChange)="prOnDateRangeChange()"
                     class="border rounded px-2 py-1.5 text-sm" />
            </div>

            <!-- Método de pago -->
            <div>
              <label class="block text-xs text-gray-500 mb-1">Método de pago</label>
              <select [(ngModel)]="prFilters.paymentMethod" (ngModelChange)="prApplyFilters()"
                      class="border rounded px-2 py-1.5 text-sm bg-white">
                <option *ngFor="let m of prPaymentMethodOptions" [value]="m">
                  {{ prPaymentMethodLabel(m) }}<ng-container *ngIf="prHasMethodCounts()"> ({{ prMethodCounts[m] || 0 }})</ng-container>
                </option>
              </select>
            </div>

            <!-- Banco emisor -->
            <div *ngIf="prFacets.by_bank?.length">
              <label class="block text-xs text-gray-500 mb-1">Banco emisor</label>
              <select [(ngModel)]="prFilters.bankCode" (ngModelChange)="prApplyFilters()"
                      class="border rounded px-2 py-1.5 text-sm bg-white">
                <option value="">Todos los bancos</option>
                <option *ngFor="let b of prFacets.by_bank" [value]="b._id">
                  {{ b.name || b._id }} ({{ b.count }})
                </option>
              </select>
            </div>

            <!-- Reportado por — solo sobre lo ya reportado -->
            <div *ngIf="prShowsReporterFilters">
              <label class="block text-xs text-gray-500 mb-1">Reportado por</label>
              <select [(ngModel)]="prFilters.reportedBy" (ngModelChange)="prApplyFilters()"
                      class="border rounded px-2 py-1.5 text-sm bg-white">
                <option value="">Cualquiera</option>
                <option *ngFor="let r of prFacets.by_reporter" [value]="r._id || 'SYSTEM'">
                  {{ r.name || 'Sistema (automático)' }} ({{ r.count }})
                </option>
              </select>
            </div>

            <!-- Intentos mínimos — no aporta en la vista de reportados -->
            <div *ngIf="prShowsAttemptsFilter">
              <label class="block text-xs text-gray-500 mb-1">Intentos mínimos</label>
              <select [(ngModel)]="prFilters.minAttempts" (ngModelChange)="prApplyFilters()"
                      class="border rounded px-2 py-1.5 text-sm bg-white">
                <option [ngValue]="null">Cualquiera</option>
                <option [ngValue]="1">1 o más</option>
                <option [ngValue]="5">5 o más</option>
                <option [ngValue]="8">8 o más (por fallar)</option>
              </select>
            </div>
          </div>
        </div>

        <!-- Chips de filtros activos: se quitan de UNO EN UNO -->
        <div *ngIf="prActiveFilterCount" class="flex flex-wrap items-center gap-2 px-3 py-2 border-t bg-blue-50/50">
          <span class="text-xs text-gray-500">Filtros activos:</span>
          <span *ngFor="let chip of prActiveFilterChips; trackBy: prTrackByKey"
                class="inline-flex items-center gap-1.5 bg-white border border-blue-200 text-blue-800 rounded-full px-2.5 py-1 text-xs">
            {{ chip.label }}
            <button type="button" (click)="prRemoveFilter(chip.key)" class="text-blue-400 hover:text-blue-700" [title]="'Quitar: ' + chip.label">
              <i class="fas fa-times"></i>
            </button>
          </span>
          <button type="button" (click)="prClearFilters()" class="text-xs text-gray-500 hover:text-gray-800 underline ml-1">
            Limpiar todo
          </button>
        </div>
      </div>

      <!-- Tabla -->
      <div class="bg-white border rounded-lg overflow-hidden">
        <div *ngIf="prLoading" class="p-6 text-center text-gray-500">
          <i class="fas fa-spinner fa-spin mr-2"></i>Cargando…
        </div>
        <!-- Vista AGRUPADA: cliente → póliza, ordenado de lo más viejo a lo más nuevo -->
        <div *ngIf="!prLoading">
          <!-- Error explícito: un fallo del backend NO debe parecer "no hay resultados". -->
          <div *ngIf="prError" class="m-4 p-4 border border-red-200 bg-red-50 rounded-lg">
            <div class="flex items-start gap-3">
              <i class="fas fa-exclamation-circle text-red-500 mt-0.5"></i>
              <div class="flex-1">
                <p class="font-semibold text-red-800 text-sm mb-1">No se pudo consultar el listado</p>
                <p class="text-xs text-red-700 font-mono break-all">{{ prError }}</p>
                <button type="button" (click)="prApplyFilters()"
                        class="mt-2 px-3 py-1.5 bg-red-600 text-white rounded text-xs">
                  <i class="fas fa-redo mr-1"></i>Reintentar
                </button>
              </div>
            </div>
          </div>

          <!-- Estado vacío inteligente: además de "sin resultados", dice QUÉ está
               restringiendo y permite quitarlo sin resetear todo. -->
          <div *ngIf="prGroups.length === 0 && !prError" class="p-8 text-center">
            <i class="fas fa-filter text-4xl text-gray-200 mb-3"></i>
            <p class="text-gray-500 font-medium">No hay pagos que cumplan estos filtros.</p>
            <div *ngIf="prActiveFilterCount" class="mt-3">
              <p class="text-sm text-gray-400 mb-2">Estás filtrando por:</p>
              <div class="flex flex-wrap justify-center gap-2">
                <button *ngFor="let chip of prActiveFilterChips; trackBy: prTrackByKey" type="button"
                        (click)="prRemoveFilter(chip.key)"
                        class="inline-flex items-center gap-1.5 bg-white border border-gray-300 hover:border-red-400 hover:text-red-600 rounded-full px-3 py-1 text-xs text-gray-700">
                  {{ chip.label }} <i class="fas fa-times"></i>
                </button>
              </div>
              <button (click)="prClearFilters()" class="mt-3 text-sm text-blue-600 hover:underline">
                Quitar todos los filtros
              </button>
            </div>
            <p *ngIf="!prActiveFilterCount" class="text-sm text-gray-400 mt-1">
              No hay pagos en estado "{{ prFilters.status === 'pending' ? 'Pendientes' : prFilters.status === 'reported' ? 'Reportados' : prFilters.status === 'failed' ? 'Fallidos' : 'Todos' }}".
            </p>
          </div>

          <div *ngFor="let client of prGroups" class="border-b last:border-b-0">
            <!-- Encabezado del cliente -->
            <div class="flex items-center gap-3 px-3 py-3 hover:bg-gray-50 cursor-pointer" (click)="prToggleClient(client._id)">
              <i class="fas w-3 text-gray-400" [ngClass]="prIsExpanded(client._id) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
              <img [src]="personPhotoG(client.person)" class="rounded-circle border"
                   style="object-fit: cover; width:36px; height:36px;" alt="cliente" />
              <div class="flex-1 min-w-0">
                <div class="font-medium text-gray-800 truncate">{{ client.person?.name || '—' }}</div>
                <div class="text-xs text-gray-400">{{ client.person?.cedula || client.person?.code || '' }}</div>
              </div>
              <div class="text-xs text-gray-500 text-right whitespace-nowrap">
                <div>{{ client.policies?.length || 0 }} póliza(s) · {{ client.client_count }} cuota(s)</div>
                <div class="flex items-center justify-end gap-1">
                  <span>Más antiguo: {{ client.client_oldest | date:'dd/MM/yyyy' }}</span>
                  <ng-container *ngIf="prAgeInfoFromDate(client.client_oldest) as age">
                    <span *ngIf="age.days >= 0" class="inline-flex items-center gap-1 font-semibold"
                          [ngClass]="age.textCls" title="Días del pendiente más antiguo de este cliente">
                      <span class="inline-block rounded-full" style="width:6px;height:6px;" [ngClass]="age.dotCls"></span>{{ age.text }}
                    </span>
                  </ng-container>
                </div>
              </div>
              <button *ngIf="prFilters.status === 'pending' && _auth.hasAnyPermission(['seguros.reporte_pagos.manage', 'config.seguros.read'])"
                      (click)="$event.stopPropagation(); prReportNext(client)"
                      class="px-2 py-1 bg-blue-600 text-white rounded text-xs whitespace-nowrap">
                <i class="fas fa-paper-plane mr-1"></i>Reportar siguiente
              </button>
            </div>

            <!-- Pólizas del cliente (más vieja primero) -->
            <div *ngIf="prIsExpanded(client._id)" class="px-3 pb-3 pl-10">
              <div *ngFor="let pol of client.policies" class="mb-3 border rounded-lg overflow-hidden">
                <div class="bg-gray-50 px-3 py-2 text-sm flex flex-wrap items-center gap-x-2 gap-y-1 border-b">
                  <span class="font-semibold text-gray-800">Póliza N° {{ pol.policy_number || '—' }}</span>
                  <span class="text-gray-500">{{ prProductLabelG(pol.product) }}</span>
                  <span class="text-gray-400">· {{ pol.count }} cuota(s) · más antigua {{ pol.oldest | date:'dd/MM/yyyy' }}</span>
                </div>
                <table class="min-w-full text-sm">
                  <tbody>
                    <tr *ngFor="let pm of pol.payments" class="border-t align-top" [class.bg-blue-50]="prIsNext(client, pm)">
                      <td class="px-3 py-2">
                        <div *ngIf="prIsNext(client, pm)" class="text-blue-600 text-xs font-semibold mb-0.5">
                          <i class="fas fa-arrow-right mr-1"></i>Siguiente a reportar
                        </div>
                        <div class="text-xs"><span class="text-gray-400">Crédito:</span> {{ pm.credit_identifier || '—' }}</div>
                        <div class="text-xs"><span class="text-gray-400">Cuota:</span> {{ pm.cuotas_label || '—' }}</div>
                        <div class="text-xs mt-0.5" *ngIf="pm.bank?.name || pm.bank?.code">
                          <i class="fas fa-university text-gray-300 mr-1"></i><span class="text-gray-600">{{ pm.bank?.name || pm.bank?.code }}</span>
                        </div>
                        <div class="text-xs text-gray-400 font-mono" *ngIf="pm.reference">Ref: {{ pm.reference }}</div>
                      </td>
                      <td class="px-3 py-2 text-right whitespace-nowrap">
                        <div class="font-semibold text-gray-800 tabular-nums">{{ pm.amount | number:'1.2-2' }}</div>
                        <div class="text-xs text-gray-400 tabular-nums" *ngIf="pm.rate">tasa {{ pm.rate | number:'1.2-4' }}</div>
                      </td>
                      <td class="px-3 py-2 whitespace-nowrap">
                        <div>{{ pm.eff_date | date:'dd/MM/yyyy' }}</div>
                        <ng-container *ngIf="prAgeInfo(pm) as age">
                          <div *ngIf="age.days >= 0" class="inline-flex items-center gap-1 text-xs mt-0.5"
                               [ngClass]="age.textCls" title="Antigüedad del pago sin reportar">
                            <span class="inline-block rounded-full" style="width:6px;height:6px;" [ngClass]="age.dotCls"></span>{{ age.text }}
                          </div>
                        </ng-container>
                      </td>
                      <td class="px-3 py-2">
                        <span class="badge" [ngClass]="prRowState(pm).cls">{{ prRowState(pm).label }}</span>
                        <!-- El cron solo procesa domiciliaciones: los pagos AUTO (botón de
                             pagos / débito inmediato) NO se auto-reportan nunca. -->
                        <div *ngIf="prRequiresManualReport(pm) && !pm.insurance_report?.reportado"
                             class="text-xs text-orange-600 mt-1 whitespace-nowrap"
                             title="El reporte automático no procesa este tipo de pago: debe reportarse manualmente">
                          <i class="fas fa-hand-paper mr-1"></i>Requiere reporte manual
                        </div>
                        <!-- Trazabilidad: quién reportó y cuándo -->
                        <div *ngIf="pm.insurance_report?.reportado" class="text-xs text-gray-500 mt-1">
                          <div [ngClass]="prIsSystemReported(pm) ? 'text-gray-400 italic' : 'text-gray-600'">
                            <i class="fas fa-user-check mr-1 text-gray-300"></i>{{ prReportedByName(pm) }}
                          </div>
                          <div *ngIf="pm.insurance_report?.reported_at" class="text-gray-400">
                            {{ pm.insurance_report.reported_at | date:'dd/MM/yyyy HH:mm' }}
                          </div>
                        </div>
                      </td>
                      <td class="px-3 py-2 text-center text-xs whitespace-nowrap tabular-nums"
                          [ngClass]="prTriesHot(pm) ? 'text-red-600 font-semibold' : 'text-gray-600'"
                          title="Intentos de reporte realizados / límite antes de marcar Fallido">
                        {{ pm.insurance_report?.current_intento || 0 }}/{{ pm.insurance_report?.limite || 10 }}
                      </td>
                      <td class="px-3 py-2 text-center whitespace-nowrap">
                        <button (click)="openPaymentViewer(paymentViewer, pm)" class="text-blue-600 hover:underline text-xs mr-2">
                          <i class="fas fa-external-link-alt mr-1"></i>Ver
                        </button>
                        <button *ngIf="!pm.insurance_report?.reportado && _auth.hasAnyPermission(['seguros.reporte_pagos.manage', 'config.seguros.read'])"
                                (click)="reportPayment(pm)" class="px-2 py-1 bg-blue-600 text-white rounded text-xs">
                          <i class="fas fa-paper-plane"></i> Reportar
                        </button>
                        <button (click)="openAuditModal(pm)" class="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs ml-1">
                          <i class="fas fa-history"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Paginación -->
        <div *ngIf="!prLoading && prPagination.totalPages > 1"
          class="flex items-center justify-between px-3 py-2 border-t bg-gray-50 text-sm">
          <span class="text-gray-500">
            {{ prGroups.length }} de {{ prPagination.total }} cliente(s) · página {{ prPagination.page }}/{{ prPagination.totalPages }}
          </span>
          <div class="flex gap-1">
            <button (click)="prGoToPage(prPagination.page - 1)" [disabled]="prPagination.page <= 1"
              class="px-2 py-1 border rounded disabled:opacity-40">Anterior</button>
            <button (click)="prGoToPage(prPagination.page + 1)" [disabled]="!prPagination.hasMore"
              class="px-2 py-1 border rounded disabled:opacity-40">Siguiente</button>
          </div>
        </div>
      </div>

      </ng-container>

      <ng-template #paymentReportsSC>
        <div class="bg-white rounded-lg shadow-sm p-12 text-center">
          <i class="fas fa-info-circle text-5xl text-gray-300 mb-4"></i>
          <h3 class="text-lg font-semibold text-gray-800 mb-2">
            No disponible para Seguros Caracas
          </h3>
          <p class="text-sm text-gray-500 max-w-md mx-auto">
            El Reporte de Pagos a la Aseguradora está implementado únicamente
            para Seguros Mercantil. Para conciliar pagos con Seguros Caracas
            use el tab <strong>Sincronización → Relaciones Caracas</strong>.
          </p>
        </div>
      </ng-template>
    </div>

    <!-- Modal de Auditoría -->
    <div *ngIf="prAuditModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
      <div class="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <div class="flex items-center justify-between px-4 py-3 border-b">
          <div>
            <h3 class="font-semibold text-gray-800">Auditoría del reporte</h3>
            <p class="text-xs text-gray-500 font-mono">{{ prAuditPayment?.code }}</p>
          </div>
          <button (click)="closeAuditModal()" class="text-gray-400 hover:text-gray-700"><i class="fas fa-times"></i></button>
        </div>
        <div class="p-4 overflow-auto">
          <div *ngIf="prAuditLoading" class="text-center text-gray-500 py-6">
            <i class="fas fa-spinner fa-spin mr-2"></i>Cargando…
          </div>
          <table *ngIf="!prAuditLoading" class="min-w-full text-sm">
            <thead class="bg-gray-50 text-gray-600">
              <tr>
                <th class="text-left px-2 py-1">Fecha</th>
                <th class="text-left px-2 py-1">Acción</th>
                <th class="text-left px-2 py-1">Resultado</th>
                <th class="text-left px-2 py-1">Origen</th>
                <th class="text-left px-2 py-1">Quién</th>
                <th class="text-center px-2 py-1">Intento</th>
                <th class="text-left px-2 py-1">Detalle</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let a of prAuditRows" class="border-t">
                <td class="px-2 py-1 whitespace-nowrap">{{ a.timestamp | date:'dd/MM/yyyy HH:mm' }}</td>
                <td class="px-2 py-1">{{ a.action }}</td>
                <td class="px-2 py-1">
                  <span [ngClass]="a.result === 'SUCCESS' ? 'text-green-600' : 'text-red-600'">{{ a.result }}</span>
                </td>
                <td class="px-2 py-1">{{ a.origin }}</td>
                <td class="px-2 py-1">
                  <div>{{ a.actor?.name || '—' }}</div>
                  <div class="text-xs text-gray-400">{{ a.actor?.email || '' }}</div>
                </td>
                <td class="px-2 py-1 text-center">{{ a.attempt }}</td>
                <td class="px-2 py-1 text-xs text-gray-500">{{ a.errorMessage || '' }}</td>
              </tr>
              <tr *ngIf="prAuditRows.length === 0">
                <td colspan="7" class="px-2 py-6 text-center text-gray-400">Sin registros de auditoría.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Visor de Pago (offcanvas) — reutiliza el mismo componente de distribuciones -->
    <ng-template #paymentViewer let-offcanvas>
      <div class="offcanvas-header border-bottom">
        <h5 class="offcanvas-title">Detalle del pago</h5>
        <button type="button" class="btn-close" (click)="offcanvas.dismiss()"></button>
      </div>
      <div class="offcanvas-body">
        <div class="container mx-auto" style="max-width: 820px;">
          <app-person-payments
            [code_person]="prViewerParams?.person_code"
            [code_payment]="prViewerParams?.code_payment">
          </app-person-payments>
        </div>
      </div>
    </ng-template>

    <!-- Modal compartido "Confirmar reporte a la aseguradora" (mismo en la vista individual del pago) -->
    <app-insurance-report-confirm
      [payment]="prReportPayment"
      [open]="prReportModalOpen"
      (openChange)="prReportModalOpen = $event"
      (reported)="onInsuranceReported()">
    </app-insurance-report-confirm>

    <!-- Dashboard Tab -->
    <div *ngIf="activeTab === 'dashboard'" class="space-y-6">

      <!-- =================================================================== -->
      <!-- Grid de widgets por aseguradora                                      -->
      <!-- =================================================================== -->
      <div *ngIf="!isLoadingInsurers && visibleInsurers.length > 0; else widgetGridEmpty"
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        <button *ngFor="let insurer of visibleInsurers; trackBy: trackByInsurerCode"
          (click)="selectInsurerWidget(insurer.code)"
          type="button"
          class="insurer-widget-card text-left bg-white rounded-lg shadow-md p-4 border border-transparent transition-all"
          [class.is-selected]="isWidgetSelected(insurer.code)">

          <!-- Header: avatar de iniciales + nombre + check de seleccionado -->
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-center space-x-2 min-w-0">
              <div class="w-9 h-9 rounded-full bg-blue-100 text-blue-600 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                {{ getInsurerInitials(insurer.name) }}
              </div>
              <div class="min-w-0">
                <p class="text-sm font-semibold text-gray-800 truncate">{{ insurer.name }}</p>
                <p class="text-[10px] text-gray-400 uppercase tracking-wide">{{ insurer.code }}</p>
              </div>
            </div>
            <i *ngIf="isWidgetSelected(insurer.code)" class="fas fa-check-circle text-blue-600 text-sm" aria-hidden="true"></i>
          </div>

          <!-- Body: loading / error / datos -->
          <ng-container *ngIf="getWidgetSummary(insurer.code)?.loading; else widgetBodyTpl">
            <div class="space-y-2 animate-pulse">
              <div class="h-7 bg-gray-200 rounded w-20"></div>
              <div class="grid grid-cols-3 gap-2">
                <div class="h-10 bg-gray-100 rounded"></div>
                <div class="h-10 bg-gray-100 rounded"></div>
                <div class="h-10 bg-gray-100 rounded"></div>
              </div>
              <div class="h-2 bg-gray-200 rounded"></div>
            </div>
          </ng-container>

          <ng-template #widgetBodyTpl>
            <ng-container *ngIf="getWidgetSummary(insurer.code)?.error; else widgetDataTpl">
              <div class="text-center py-2">
                <i class="fas fa-exclamation-triangle text-amber-400 text-lg"></i>
                <p class="text-xs text-gray-400 mt-1">Sin datos</p>
              </div>
            </ng-container>
            <ng-template #widgetDataTpl>
              <div class="space-y-3">
                <!-- Total grande -->
                <div>
                  <p class="text-2xl font-bold text-gray-900 leading-tight">{{ getWidgetTotal(insurer.code) | number }}</p>
                  <p class="text-xs text-gray-500">pólizas en el período</p>
                </div>

                <!-- Mini KPIs: En proceso / Completadas / Tasa de finalización -->
                <div class="grid grid-cols-3 gap-2">
                  <div class="bg-yellow-50 rounded p-2 text-center">
                    <p class="text-sm font-bold text-yellow-700">{{ getWidgetInProcess(insurer.code) | number }}</p>
                    <p class="text-[10px] text-gray-500 uppercase tracking-tight">Proceso</p>
                  </div>
                  <div class="bg-green-50 rounded p-2 text-center">
                    <p class="text-sm font-bold text-green-700">{{ getWidgetCompleted(insurer.code) | number }}</p>
                    <p class="text-[10px] text-gray-500 uppercase tracking-tight">Compl.</p>
                  </div>
                  <div class="bg-blue-50 rounded p-2 text-center">
                    <p class="text-sm font-bold text-blue-700">{{ getWidgetCompletionRate(insurer.code) | number:'1.0-1' }}%</p>
                    <p class="text-[10px] text-gray-500 uppercase tracking-tight">Tasa</p>
                  </div>
                </div>

                <!-- Mini barra apilada de status distribution -->
                <ng-container *ngIf="getWidgetSummary(insurer.code)?.statusDistribution as dist">
                  <div class="flex h-2 rounded-full overflow-hidden bg-gray-100">
                    <div *ngFor="let item of dist.distribution || []"
                      [style.width.%]="(item.count / (dist.totalPolicies || 1) * 100)"
                      [class]="getStatusBarClass(item.status)"
                      class="transition-all duration-300"></div>
                  </div>
                </ng-container>

                <!-- Badge de errores si > 0 -->
                <div *ngIf="getWidgetErrorCount(insurer.code) > 0"
                  class="flex items-center text-xs text-red-600 font-medium">
                  <i class="fas fa-exclamation-circle mr-1"></i>
                  {{ getWidgetErrorCount(insurer.code) | number }} con errores
                </div>
              </div>
            </ng-template>
          </ng-template>

        </button>
      </div>

      <!-- Estado vacío del grid (loading aseguradoras o lista vacía) -->
      <ng-template #widgetGridEmpty>
        <div *ngIf="isLoadingInsurers" class="bg-white rounded-lg shadow-md p-8 text-center">
          <i class="fas fa-spinner fa-spin text-2xl text-blue-500"></i>
          <p class="text-sm text-gray-500 mt-2">Cargando aseguradoras…</p>
        </div>
        <div *ngIf="!isLoadingInsurers" class="bg-white rounded-lg shadow-md p-8 text-center">
          <i class="fas fa-building text-3xl text-gray-300 mb-2"></i>
          <p class="text-sm text-gray-500">No hay aseguradoras para mostrar.</p>
          <button *ngIf="selectedInsurerCode" (click)="clearInsurerFilter()" class="mt-2 text-xs text-blue-600 hover:underline">
            Limpiar filtro de aseguradora
          </button>
        </div>
      </ng-template>

      <!-- Hint cuando hay widgets visibles pero ninguno está seleccionado.
           Guía al usuario a hacer click para abrir el detalle. -->
      <div *ngIf="!selectedDashboardInsurerCode && visibleInsurers.length > 0"
        class="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
        <i class="fas fa-hand-pointer text-3xl text-blue-400 mb-2"></i>
        <p class="text-sm font-medium text-blue-800">Selecciona una aseguradora para ver el detalle</p>
        <p class="text-xs text-blue-600 mt-1">Haz click en cualquier widget de arriba para cargar sus KPIs, gráficos y métricas.</p>
      </div>

      <!-- =================================================================== -->
      <!-- Panel detalle de la aseguradora seleccionada                         -->
      <!-- =================================================================== -->
      <div *ngIf="selectedDashboardInsurerCode" class="space-y-6 dashboard-detail-panel">

        <!-- Header del panel -->
        <div class="bg-white rounded-xl shadow-lg p-4 flex items-center justify-between flex-wrap gap-2">
          <ng-container *ngIf="getInsurerByCode(selectedDashboardInsurerCode) as selectedInsurer">
            <div class="flex items-center space-x-3">
              <div class="w-11 h-11 rounded-full bg-blue-100 text-blue-600 text-sm font-semibold flex items-center justify-center flex-shrink-0">
                {{ getInsurerInitials(selectedInsurer.name) }}
              </div>
              <div>
                <h2 class="text-lg font-semibold text-gray-800">{{ selectedInsurer.name }}</h2>
                <p class="text-xs text-gray-500">{{ selectedInsurer.code }} · {{ getCurrentDateRangeText() }}</p>
              </div>
            </div>
            <button (click)="refreshDashboard()" class="text-xs text-blue-600 hover:underline">
              <i class="fas fa-sync-alt mr-1"></i>Actualizar
            </button>
          </ng-container>
        </div>

        <!-- Banner de error del panel -->
        <div *ngIf="currentDashboardData?.error" class="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
          <p class="text-sm text-red-700"><i class="fas fa-exclamation-triangle mr-2"></i>{{ currentDashboardData?.error }}</p>
          <button (click)="retryDetailPanel()" class="px-3 py-1 bg-red-600 text-white rounded text-xs">
            Reintentar
          </button>
        </div>

        <!-- KPIs scoped a la aseguradora seleccionada -->
        <div *ngIf="currentDashboardData?.kpis as kpis" class="grid grid-cols-2 md:grid-cols-4 gap-4">

          <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-indigo-500">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-xs font-medium text-gray-600">Total Pólizas</p>
                <p class="text-xl font-bold text-gray-900">{{ kpis.totalPolicies || 0 | number }}</p>
              </div>
              <div class="p-2 bg-indigo-100 rounded-full">
                <i class="fas fa-layer-group text-indigo-600 text-sm"></i>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-yellow-500">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-xs font-medium text-gray-600">En Proceso</p>
                <p class="text-xl font-bold text-gray-900">{{ kpis.inProcessPolicies || 0 | number }}</p>
              </div>
              <div class="p-2 bg-yellow-100 rounded-full">
                <i class="fas fa-spinner text-yellow-600 text-sm"></i>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-xs font-medium text-gray-600">Completadas</p>
                <p class="text-xl font-bold text-gray-900">{{ kpis.completedPolicies || 0 | number }}</p>
              </div>
              <div class="p-2 bg-green-100 rounded-full">
                <i class="fas fa-check-circle text-green-600 text-sm"></i>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-red-500">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-xs font-medium text-gray-600">Errores</p>
                <p class="text-xl font-bold text-gray-900">{{ kpis.policiesInError || 0 | number }}</p>
              </div>
              <div class="p-2 bg-red-100 rounded-full">
                <i class="fas fa-exclamation-triangle text-red-600 text-sm"></i>
              </div>
            </div>
          </div>

        </div>

        <!-- Loading inicial si todavía no hay KPIs (primera carga) -->
        <div *ngIf="currentDashboardData?.loading && !currentDashboardData?.kpis" class="bg-white rounded-xl shadow p-8 text-center">
          <i class="fas fa-spinner fa-spin text-2xl text-blue-500"></i>
          <p class="text-sm text-gray-500 mt-2">Cargando detalle…</p>
        </div>

        <!-- Charts -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

          <!-- Status Distribution -->
          <div class="bg-white rounded-xl shadow-lg p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-1">Distribución por Estado</h3>
            <p class="text-xs text-gray-500 mb-4">Cantidad de pólizas agrupadas por su estado actual en el flujo de gestión.</p>

            <div *ngIf="currentDashboardData?.statusDistribution; else noDataTemplate" class="space-y-4">
              <div class="text-center mb-4">
                <p class="text-2xl font-bold text-gray-900">{{ currentDashboardData?.statusDistribution?.totalPolicies || 0 | number }}</p>
                <p class="text-sm text-gray-600">Total de Pólizas</p>
              </div>
              <!-- Cards narrativas: ordenadas por funnelStep para leerse como
                   embudo (CREATED → ... → COMPLETED/ANNULLED). Cada card lleva
                   badge + label amigable + count/% + "Hasta:" con la acción
                   del usuario en la pantalla del móvil correspondiente.
                   Si el item trae `breakdown`, se renderiza un acordeón con las
                   sub-dimensiones (by_plan_kind, by_notification, by_frequency). -->
              <div class="space-y-2">
                <div *ngFor="let item of getSortedStatusDistribution()"
                  class="status-funnel-card p-3 bg-gray-50 rounded-lg border-l-4"
                  [ngClass]="getStatusBorderClass(item.status)">
                  <!-- Header: clickable. Click corto = toggle acordeón si hay
                       breakdown / abrir sub-tab Contactos si no. Doble-click
                       siempre abre Contactos. Botón "Ver contactos →" expuesto
                       a la derecha como alternativa explícita. -->
                  <div class="cursor-pointer"
                       (click)="hasBreakdown(item) ? toggleBreakdown(item.status) : onStatusBucketClick(item.status)">
                    <div class="flex items-center justify-between mb-1">
                      <div class="flex items-center space-x-2 min-w-0">
                        <i *ngIf="hasBreakdown(item)" class="fas text-gray-400 text-xs flex-shrink-0"
                           [class.fa-chevron-right]="!isBreakdownExpanded(item.status)"
                           [class.fa-chevron-down]="isBreakdownExpanded(item.status)"></i>
                        <span [class]="getStatusBadgeClass(item.status)" class="px-2 py-1 text-xs font-medium rounded-full flex-shrink-0">
                          {{ item.status }}
                        </span>
                        <span class="text-sm font-semibold text-gray-800 truncate">{{ getStatusLabel(item.status) }}</span>
                        <!-- Badges de alerta visibles sin expandir -->
                        <span *ngIf="getNotificationErrorCount(item) > 0"
                              class="px-2 py-0.5 text-[10px] font-medium rounded-full bg-amber-100 text-amber-800 flex-shrink-0"
                              [title]="getNotificationErrorCount(item) + ' con error de notificación'">
                          <i class="fas fa-exclamation-triangle mr-1"></i>{{ getNotificationErrorCount(item) | number }}
                        </span>
                        <span *ngIf="getFullyPaidStuckCount(item) > 0"
                              class="px-2 py-0.5 text-[10px] font-medium rounded-full bg-red-100 text-red-800 flex-shrink-0"
                              [title]="getFullyPaidStuckCount(item) + ' requieren saneamiento'">
                          <i class="fas fa-wrench mr-1"></i>{{ getFullyPaidStuckCount(item) | number }}
                        </span>
                      </div>
                      <div class="text-right flex-shrink-0 ml-2">
                        <span class="text-base font-semibold text-gray-900">{{ item.count | number }}</span>
                        <span class="text-xs text-gray-500 ml-1">
                          ({{ (item.count / (currentDashboardData?.statusDistribution?.totalPolicies || 1) * 100) | number:'1.0-1' }}%)
                        </span>
                      </div>
                    </div>
                    <p class="text-xs text-gray-600 mt-1 flex items-center">
                      <i class="fas fa-arrow-right text-gray-400 mr-1.5" aria-hidden="true"></i>
                      <span>En la app hasta: <span class="font-medium text-gray-700">{{ getStatusUserAction(item.status) }}</span></span>
                    </p>
                  </div>

                  <!-- Acordeón: sub-segmentos del bucket -->
                  <div *ngIf="hasBreakdown(item) && isBreakdownExpanded(item.status)"
                       class="mt-3 pt-3 border-t border-gray-200 space-y-3">
                    <div *ngFor="let dim of getBreakdownDimensions(item)">
                      <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                        {{ getBreakdownDimensionTitle(dim) }}
                      </p>
                      <div class="space-y-1.5">
                        <div *ngFor="let bk of getBreakdownItems(item, dim)"
                             (click)="bk.count > 0 && onBreakdownItemClick(item.status, dim, bk.key, bk.label)"
                             class="flex items-center justify-between text-xs px-2 py-1.5 rounded transition-colors"
                             [class.cursor-pointer]="bk.count > 0"
                             [class.hover:opacity-80]="bk.count > 0"
                             [ngClass]="getBreakdownItemBgClass(bk.key)">
                          <span class="flex items-center min-w-0">
                            <i class="fas text-[10px] mr-1.5 flex-shrink-0" [ngClass]="getBreakdownItemIconClass(bk.key)"></i>
                            <span class="truncate">{{ getBreakdownLabel(dim, bk.key, bk.label) }}</span>
                          </span>
                          <span class="font-semibold flex-shrink-0 ml-2" [ngClass]="getBreakdownItemTextClass(bk.key)">
                            {{ bk.count | number }}
                            <span class="text-[10px] opacity-75 ml-1">
                              ({{ item.count > 0 ? (bk.count / item.count * 100 | number:'1.0-1') : '0' }}%)
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="mt-4">
                <div class="flex h-3 bg-gray-200 rounded-full overflow-hidden">
                  <div *ngFor="let item of currentDashboardData?.statusDistribution?.distribution || []; let i = index"
                    [style.width.%]="(item.count / (currentDashboardData?.statusDistribution?.totalPolicies || 1) * 100)"
                    [class]="getStatusBarClass(item.status)" class="transition-all duration-300"></div>
                </div>
              </div>
            </div>

            <ng-template #noDataTemplate>
              <div class="flex items-center justify-center h-64 text-gray-400">
                <div class="text-center">
                  <i class="fas fa-chart-pie text-4xl mb-2"></i>
                  <p>No hay datos de distribución</p>
                  <p class="text-sm">Los datos se cargarán automáticamente</p>
                </div>
              </div>
            </ng-template>

          </div>

          <!-- Creation Trend -->
          <div class="bg-white rounded-xl shadow-lg p-6">
            <h3 class="text-lg font-semibold text-gray-900 mb-1">Tendencia de Creación</h3>
            <p class="text-xs text-gray-500 mb-4">Volumen de pólizas creadas por día o mes en el período seleccionado.</p>

            <div *ngIf="currentDashboardData?.creationTrend?.tooManyRecords; else trendContentTemplate">
              <div class="flex items-center justify-center h-56 text-gray-400">
                <div class="text-center px-4">
                  <i class="fas fa-database text-4xl mb-3 text-amber-400"></i>
                  <p class="text-sm font-medium text-gray-700 mb-1">Demasiados registros para generar la tendencia</p>
                  <p class="text-xs text-gray-500 mb-3">
                    El período actual contiene <strong class="text-gray-700">{{ currentDashboardData?.creationTrend?.totalRecords | number }}</strong> pólizas.
                    El límite para esta vista es de <strong class="text-gray-700">{{ currentDashboardData?.creationTrend?.limit | number }}</strong>.
                  </p>
                  <p class="text-xs text-amber-600 font-medium">
                    <i class="fas fa-filter mr-1"></i>
                    Acote el filtro de fecha (ej: Este mes, Últimos 30 días) para visualizar la tendencia.
                  </p>
                </div>
              </div>
            </div>

            <ng-template #trendContentTemplate>
              <div *ngIf="currentDashboardData?.creationTrend?.data && currentDashboardData?.creationTrend?.data?.length! > 0; else noTrendDataTemplate"
                class="space-y-4">
                <div class="text-center mb-2">
                  <p class="text-2xl font-bold text-gray-900">{{ getTotalCreations() | number }}</p>
                  <p class="text-sm text-gray-600">Pólizas Creadas en el Período</p>
                </div>
                <!-- Gráfico de líneas Chart.js. Eje Y = cantidad, Eje X = fecha
                     (granularidad la decide el backend: día si rango ≤90d, mes si >90d).
                     trendChartConfig se reconstruye en buildTrendChartConfig() cuando
                     cambian los datos (filtro de fecha, aseguradora, etc.).
                     El canvas vive DENTRO del *ngIf para que se destruya y recree
                     cuando el config cambia — si no, Chart.js v4 reutiliza el canvas
                     viejo y el chart no se actualiza. -->
                <ng-container *ngIf="trendChartConfig">
                  <div class="relative w-full" style="height: 280px;">
                    <canvas #trendCanvas></canvas>
                    <app-line-chart [config]="trendChartConfig"
                                    [mychart]="trendCanvas"></app-line-chart>
                  </div>
                </ng-container>
              </div>

              <ng-template #noTrendDataTemplate>
                <div class="flex items-center justify-center h-56 text-gray-400">
                  <div class="text-center">
                    <i class="fas fa-chart-line text-4xl mb-2"></i>
                    <p>No hay datos de tendencia para el período seleccionado</p>
                    <p class="text-sm">Pruebe acotando el rango de fechas</p>
                  </div>
                </div>
              </ng-template>
            </ng-template>

          </div>

        </div>

        <!-- =================================================================== -->
        <!-- Card "Vendí · Cobré · Falta cobrar" scoped a la aseguradora selec.   -->
        <!-- Vive dentro del panel detalle para que sus stats sean coherentes con -->
        <!-- el resto de los KPIs del panel (todos filtrados por insurer).        -->
        <!-- =================================================================== -->
        <app-sales-collection-summary
          [stats]="getSelectedInsurerCobranza()"
          [hierarchy]="getSelectedInsurerCobranzaHierarchy()"
          [paymentErrorsActiveTotal]="getPaymentErrorsActiveTotal()"
          variant="dashboard"
          [periodLabel]="getCurrentDateRangeText()"
          [insurerLabel]="getInsurerByCode(selectedDashboardInsurerCode)?.name || selectedDashboardInsurerCode"
          (clickBucket)="onSalesBucketClick($event)"
          (goToPaymentErrors)="setActiveTab('payment_errors')">
        </app-sales-collection-summary>

        <!--
          Actividad Reciente — ocultado por decisión del cliente (no aporta valor
          al flujo actual). Se preserva el bloque para reactivación rápida si vuelve
          a pedirse en el futuro. Para reactivar: descomentar este bloque tal cual.
        -->
        <!--
        <div class="bg-white rounded-xl shadow-lg p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Actividad Reciente</h3>
          <div *ngIf="(currentDashboardData?.recentActivity?.length || 0) > 0; else noActivityTemplate" class="space-y-4">
            <div *ngFor="let activity of currentDashboardData?.recentActivity; let i = index"
              class="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
              <div class="flex items-center space-x-4">
                <div class="flex-shrink-0">
                  <div [class]="getStatusBadgeClass(activity.params.policy_status)">
                    {{ getStatusText(activity.params.policy_status) }}
                  </div>
                </div>
                <div>
                  <p class="text-sm font-medium text-gray-900">
                    <span *ngIf="activity.params.insured.identity && activity.params.insured.identity.trim()">{{
                      activity.params.insured.identity }} - </span>{{ activity.params.insured.name }}
                  </p>
                  <p class="text-xs text-gray-500">{{ activity.params.code }}</p>
                </div>
              </div>
              <div class="text-right">
                <p class="text-sm text-gray-900">{{ activity.params.insurance.name }}</p>
                <p class="text-xs text-gray-500">{{ formatDateTime(activity.params.update_date) }}</p>
              </div>
            </div>
          </div>
          <ng-template #noActivityTemplate>
            <div class="text-center py-6 text-gray-400">
              <i class="fas fa-history text-3xl mb-2"></i>
              <p class="text-sm">No hay actividad reciente para esta aseguradora.</p>
            </div>
          </ng-template>
        </div>
        -->

      </div>

    </div>

    <!-- Analytics Tab -->
    <div *ngIf="activeTab === 'analytics'" class="space-y-6">

      <!-- =================================================================== -->
      <!-- Card "Cobranza y Comisión" (variant analytics)                       -->
      <!-- Sub-componente reusable — la versión "dashboard" vive en el tab      -->
      <!-- dashboard con labels Vendí/Cobré/Falta cobrar.                       -->
      <!-- =================================================================== -->
      <app-sales-collection-summary
        [stats]="getGlobalCobranza()"
        [hierarchy]="getGlobalCobranzaHierarchy()"
        [paymentErrorsActiveTotal]="getPaymentErrorsActiveTotal()"
        variant="analytics"
        [periodLabel]="getCurrentDateRangeText()"
        [insurerLabel]="getSelectedInsurerName()"
        (goToPaymentErrors)="setActiveTab('payment_errors')">
      </app-sales-collection-summary>

        <!-- Desempeño por aseguradora -->
        <div>
          <h4 class="text-sm font-semibold text-gray-700 mb-2">Desempeño por aseguradora</h4>
          <ng-container *ngIf="getCobranzaByInsurer() as rows">
            <div *ngIf="rows.length > 0; else noBreakdownTpl" class="space-y-2">
              <div *ngFor="let row of rows"
                class="bg-gray-50 rounded-lg p-3 flex items-center justify-between flex-wrap gap-3">
                <div class="flex items-center gap-2 min-w-0 flex-1">
                  <div class="w-9 h-9 rounded-full bg-blue-100 text-blue-600 text-xs font-semibold flex items-center justify-center flex-shrink-0">
                    {{ getInsurerInitials(getInsurerNameByCode(row.insurerCode)) }}
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-gray-800 truncate">{{ getInsurerNameByCode(row.insurerCode) }}</p>
                    <p class="text-[10px] text-gray-400 uppercase tracking-wide">{{ row.insurerCode }}</p>
                  </div>
                </div>
                <div class="text-xs text-gray-600 whitespace-nowrap">
                  <span class="font-semibold text-gray-800">{{ row.emitidas | number }}</span>
                  <span class="text-gray-400 mx-1">emit ·</span>
                  <span class="font-semibold text-green-700">{{ row.pagadas | number }}</span>
                  <span class="text-gray-400 ml-1">pag</span>
                  <span *ngIf="row.pagadasConError > 0" class="text-amber-700 ml-2" title="Pólizas con pago registrado pero error al notificar a la aseguradora">
                    <i class="fas fa-exclamation-triangle text-[10px] mr-0.5"></i>
                    <span class="font-semibold">{{ row.pagadasConError | number }}</span>
                    <span class="text-amber-500 ml-1">err notif</span>
                  </span>
                </div>
                <span class="px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap"
                  [class]="getCobranzaTierClass(row.tasaCobranza)">
                  <ng-container *ngIf="!isNaNValue(row.tasaCobranza); else rowNaTier">
                    {{ row.tasaCobranza | number:'1.0-1' }}%
                  </ng-container>
                  <ng-template #rowNaTier>—</ng-template>
                </span>
                <div class="hidden md:block w-28 bg-gray-200 rounded-full h-2 overflow-hidden">
                  <div class="h-full bg-green-500 transition-all duration-500"
                    [style.width.%]="isNaNValue(row.tasaCobranza) ? 0 : row.tasaCobranza"></div>
                </div>
              </div>
            </div>
            <ng-template #noBreakdownTpl>
              <p class="text-xs text-gray-400 text-center py-4">
                No hay aseguradoras con pólizas emitidas en este período.
              </p>
            </ng-template>
          </ng-container>
        </div>

        <!-- Banner informativo de comisión -->
        <div *ngIf="showCommissionInfoBanner"
          class="bg-blue-50 border border-blue-200 rounded-md p-3 flex items-start justify-between">
          <p class="text-xs text-blue-800 pr-2">
            <i class="fas fa-info-circle mr-1"></i>
            La <strong>comisión de la empresa</strong> se calcula sobre las pólizas <strong>cobradas</strong> (las que ya tienen pago registrado). Los montos en $ no se muestran todavía — se incorporarán cuando el backend exponga el agregado financiero (premium × brokerage).
          </p>
          <button (click)="dismissCommissionBanner()"
            class="text-blue-500 hover:text-blue-700 text-xs ml-2 flex-shrink-0"
            title="Ocultar este aviso">
            <i class="fas fa-times"></i>
          </button>
        </div>

      <!-- =================================================================== -->
      <!-- Sección colapsable: las 6 cards demográficas existentes              -->
      <!-- =================================================================== -->
      <section class="bg-white rounded-xl shadow-lg overflow-hidden">
        <button (click)="toggleDemoSection()" type="button"
          class="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left">
          <span class="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <i class="fas demo-chevron"
              [class.fa-chevron-right]="isDemoSectionCollapsed"
              [class.fa-chevron-down]="!isDemoSectionCollapsed"
              aria-hidden="true"></i>
            Más análisis demográfico
            <span class="text-xs font-normal text-gray-400">(geografía, edad, profesión, producto, vehículo)</span>
          </span>
          <span class="text-xs text-gray-400">{{ isDemoSectionCollapsed ? 'Expandir' : 'Colapsar' }}</span>
        </button>

        <div *ngIf="!isDemoSectionCollapsed" class="p-6 pt-2 border-t border-gray-100">

          <!-- Chips de status para filtrar la demografía. Mapean a
               policy_status / notification_status en el endpoint. Estado
               persistido en localStorage (Plan C analytics). -->
          <div class="flex flex-wrap items-center gap-2 mb-2">
            <span class="text-xs text-gray-500 mr-1 w-32">Etapa del proceso:</span>
            <button *ngFor="let chip of demoStatusChips"
                    (click)="toggleDemoStatusChip(chip.key)"
                    type="button"
                    class="px-3 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1"
                    [ngClass]="isDemoStatusActive(chip.key)
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'">
              {{ chip.label }}
              <span class="opacity-75">({{ getDemoStatusChipCount(chip.key) | number }})</span>
            </button>
            <button *ngIf="demoStatusFilter.size > 0"
                    (click)="clearDemoStatusFilter()" type="button"
                    class="text-xs text-blue-600 hover:underline ml-2">
              Limpiar
            </button>
          </div>

          <!-- Chips de frecuencia: filtran por params.frequency.
               Vacío = todas las frecuencias. -->
          <div class="flex flex-wrap items-center gap-2 mb-4 pb-3 border-b border-gray-100">
            <span class="text-xs text-gray-500 mr-1 w-32">Frecuencia de pago:</span>
            <button *ngFor="let chip of demoFrequencyChips"
                    (click)="toggleDemoFrequencyChip(chip.key)"
                    type="button"
                    class="px-3 py-1 rounded-full text-xs font-medium transition-colors"
                    [ngClass]="isDemoFrequencyActive(chip.key)
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'">
              {{ chip.label }}
            </button>
            <button *ngIf="demoFrequencyFilter.size > 0"
                    (click)="clearDemoFrequencyFilter()" type="button"
                    class="text-xs text-blue-600 hover:underline ml-2">
              Limpiar
            </button>
            <span *ngIf="demoStatusFilter.size > 0 || demoFrequencyFilter.size > 0"
                  class="text-xs text-gray-500 ml-auto">
              Mostrando demografía de
              <strong class="text-gray-700">{{ getDemoStatusFilterTotal() | number }}</strong>
              pólizas filtradas
            </span>
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">

        <!-- Por Geografía -->
        <app-demographic-bar-chart
          title="Por Geografía"
          [items]="currentAnalyticsData?.geography || null"
          labelKey="state"
          [color]="DEMO_COLORS.geography"
          totalLabel="Total por Estados"
          emptyIcon="fa-map"
          emptyText="No hay datos geográficos">
        </app-demographic-bar-chart>

        <!-- Por Edad -->
        <app-demographic-bar-chart
          title="Por Edad"
          [items]="currentAnalyticsData?.age || null"
          labelKey="group"
          [color]="DEMO_COLORS.age"
          totalLabel="Total por Edades"
          emptyIcon="fa-users"
          emptyText="No hay datos por edad"
          [formatLabel]="ageLabelFormatter">
        </app-demographic-bar-chart>

        <!-- Por Profesión -->
        <app-demographic-bar-chart
          title="Por Profesión"
          [items]="currentAnalyticsData?.profession || null"
          labelKey="profession"
          [color]="DEMO_COLORS.profession"
          totalLabel="Total por Profesiones"
          emptyIcon="fa-briefcase"
          emptyText="No hay datos por profesión"
          [formatLabel]="formatProfession">
        </app-demographic-bar-chart>

        <!-- Por Producto -->
        <app-demographic-bar-chart
          title="Por Producto"
          [items]="currentAnalyticsData?.product || null"
          labelKey="product"
          [color]="DEMO_COLORS.product"
          totalLabel="Total por Productos"
          emptyIcon="fa-tags"
          emptyText="No hay datos por producto">
        </app-demographic-bar-chart>

        <!-- Por Marca de Vehículo -->
        <app-demographic-bar-chart
          title="Por Marca de Vehículo"
          [items]="currentAnalyticsData?.vehicleBrand || null"
          labelKey="brand"
          [color]="DEMO_COLORS.vehicleBrand"
          totalLabel="Total por Marcas"
          emptyIcon="fa-car"
          emptyText="No hay datos por marca">
        </app-demographic-bar-chart>

        <!-- Por Edad de Vehículo -->
        <app-demographic-bar-chart
          title="Por Edad de Vehículo"
          [items]="currentAnalyticsData?.vehicleAge || null"
          labelKey="group"
          [color]="DEMO_COLORS.vehicleAge"
          totalLabel="Total por Antigüedad"
          emptyIcon="fa-calendar-alt"
          emptyText="No hay datos por antigüedad">
        </app-demographic-bar-chart>

          </div>
        </div>
      </section>

    </div>

    <!-- Policies Tab -->
    <div *ngIf="activeTab === 'policies'" class="space-y-6">
      <div class="bg-white rounded-xl shadow-lg p-6">
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-lg font-semibold text-gray-900">Gestión de Pólizas</h3>
          <div class="flex items-center space-x-4">
            <span class="text-sm text-gray-500">
              Total: {{ currentPoliciesData?.pagination?.totalItems || 0 }} pólizas
            </span>
            <button (click)="exportPoliciesData()"
              [disabled]="isExportingPolicies"
              class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
              <i [class]="isExportingPolicies ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'"></i>
              <span class="text-sm font-medium">{{ isExportingPolicies ? 'Exportando...' : 'Exportar' }}</span>
            </button>
          </div>
        </div>

        <!-- Banner caveat: la agrupación es por página -->
        <div *ngIf="showGroupingCaveat" class="mb-4 bg-blue-50 border border-blue-200 rounded-md p-3 flex items-start justify-between">
          <p class="text-xs text-blue-800 pr-2">
            <i class="fas fa-info-circle mr-1"></i>
            <strong>Agrupación por página:</strong> las pólizas duplicadas del mismo asegurado, producto y frecuencia se agrupan en un solo row con acordeón. Si los duplicados están distribuidos en páginas distintas, no aparecen juntos — ampliá el rango de fechas o usá búsqueda específica para ver todo el historial agrupado. Cuando los intentos no llegaron a emitirse, por default solo se muestra el más reciente con un badge clickeable; podés cambiarlo con el toggle "Ver intentos previos abandonados" abajo.
          </p>
          <button (click)="dismissGroupingCaveat()" class="text-blue-500 hover:text-blue-700 text-xs ml-2 flex-shrink-0"
            title="Ocultar este aviso">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Toggle global: mostrar/ocultar intentos previos abandonados -->
        <div class="mb-4 flex items-center justify-between bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
          <div class="flex items-center gap-2 text-xs text-gray-700">
            <i class="fas fa-eye-slash text-gray-500"></i>
            <span>
              <strong>Intentos previos abandonados:</strong>
              <ng-container *ngIf="!showAbandonedAttempts">
                ocultos por default
                <span *ngIf="hiddenAbandonedAttemptsCount > 0" class="ml-1 text-orange-600 font-medium">
                  ({{ hiddenAbandonedAttemptsCount }} oculto{{ hiddenAbandonedAttemptsCount === 1 ? '' : 's' }} en esta página)
                </span>
              </ng-container>
              <ng-container *ngIf="showAbandonedAttempts">se muestran todos en acordeón</ng-container>
            </span>
          </div>
          <button (click)="toggleShowAbandonedAttempts()"
            class="text-xs font-medium px-2 py-1 rounded transition-colors"
            [class.bg-blue-100]="showAbandonedAttempts"
            [class.text-blue-800]="showAbandonedAttempts"
            [class.hover:bg-blue-200]="showAbandonedAttempts"
            [class.bg-white]="!showAbandonedAttempts"
            [class.text-gray-700]="!showAbandonedAttempts"
            [class.border]="!showAbandonedAttempts"
            [class.border-gray-300]="!showAbandonedAttempts"
            [class.hover:bg-gray-100]="!showAbandonedAttempts">
            <i class="fas mr-1" [class.fa-toggle-on]="showAbandonedAttempts" [class.fa-toggle-off]="!showAbandonedAttempts"></i>
            {{ showAbandonedAttempts ? 'Mostrando intentos previos' : 'Ver intentos previos abandonados' }}
          </button>
        </div>

        <!-- Search Section -->
        <div class="mb-6 space-y-4">
          <!-- Search Mode Toggle -->
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <button (click)="toggleAdvancedSearch()"
                [class]="showAdvancedSearch 
                  ? 'px-3 py-1 text-xs bg-blue-500 text-white rounded-md transition-colors duration-200'
                  : 'px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors duration-200'">
                <i class="fas fa-sliders-h mr-1"></i>
                {{ showAdvancedSearch ? 'Búsqueda Básica' : 'Búsqueda Avanzada' }}
              </button>
              <button *ngIf="isSearchActive" (click)="clearSearch()"
                class="px-3 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded-md transition-colors duration-200">
                <i class="fas fa-times mr-1"></i>
                Limpiar Búsqueda
              </button>
            </div>
            <span *ngIf="isSearchActive" class="text-sm text-blue-600 font-medium">
              <i class="fas fa-search mr-1"></i>
              Búsqueda activa
            </span>
          </div>

          <!-- General Search -->
          <div *ngIf="!showAdvancedSearch" class="flex gap-4">
            <div class="flex-1">
              <input type="text" [(ngModel)]="searchQuery" (keypress)="onSearchKeyPress($event)"
                placeholder="Buscar por código de póliza, número de la póliza, cédula, estado o texto general..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            </div>
            <button (click)="performSearch()"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200">
              <i class="fas fa-search mr-2"></i>
              Buscar
            </button>
          </div>

          <!-- Advanced Search -->
          <div *ngIf="showAdvancedSearch" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Código de Póliza</label>
                <input type="text" [(ngModel)]="advancedSearchParams.policy_code" placeholder="UUID de la póliza..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Cédula del Asegurado</label>
                <input type="text" [(ngModel)]="advancedSearchParams.identity" placeholder="V12345678 o 12345678..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Estado de la Póliza</label>
                <select [(ngModel)]="advancedSearchParams.policy_status"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="">Todos los estados</option>
                  <option value="CREATED">CREATED</option>
                  <option value="PLAN_SELECTED">PLAN_SELECTED</option>
                  <option value="DATA_CLIENT_CONFIRMED">DATA_CLIENT_CONFIRMED</option>
                  <option value="LICENSE_UPLOADED">LICENSE_UPLOADED</option>
                  <option value="VERIFIED">VERIFIED</option>
                  <option value="ISSUED">ISSUED</option>
                  <option value="PAYMENT_RECORDED">PAYMENT_RECORDED</option>
                  <option value="COMPLETED">COMPLETED</option>
                </select>
              </div>
            </div>
            <div class="flex justify-end space-x-2">
              <button (click)="clearSearch()"
                class="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors duration-200">
                <i class="fas fa-times mr-2"></i>
                Limpiar
              </button>
              <button (click)="performSearch()"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200">
                <i class="fas fa-search mr-2"></i>
                Buscar
              </button>
            </div>
          </div>
        </div>

        <!-- Policies Table -->
        <div *ngIf="currentPoliciesData?.data?.length > 0; else noPoliciesTemplate" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Código
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nro. Póliza
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Asegurado
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th class="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aseg.
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  F. Creación
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  F. Emisión
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  F. Actualiz.
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <!-- Iteración por grupos: si el grupo tiene 1 sola póliza se
                   renderiza como row normal; si tiene varias se marca como
                   primario y debajo aparecen los sub-rows del acordeón. -->
              <ng-container *ngFor="let group of policyGroups; trackBy: trackByGroupKey">

                <!-- Row primario -->
                <tr [class.policy-group-row]="group.isGroup && !shouldShowAbandonedBadge(group)"
                    [class.cursor-pointer]="group.isGroup && !shouldShowAbandonedBadge(group)"
                    [class.hover:bg-gray-50]="!group.isGroup"
                    (click)="group.isGroup && !shouldShowAbandonedBadge(group) && togglePolicyGroup(group.key)">
                  <td class="px-3 py-3">
                    <div class="flex items-start space-x-2">
                      <!-- Caso A: grupo "todo abandonado" colapsado por default → badge ámbar clickeable -->
                      <ng-container *ngIf="shouldShowAbandonedBadge(group)">
                        <button type="button"
                          (click)="$event.stopPropagation(); togglePolicyGroup(group.key)"
                          class="inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-100 text-orange-800 hover:bg-orange-200 text-[10px] rounded font-semibold whitespace-nowrap border border-orange-200 transition-colors"
                          [title]="'Hay ' + group.hiddenAttemptsCount + ' intento(s) previo(s) del mismo asegurado y producto que no llegaron a emitirse. Click para verlos.'">
                          <i class="fas fa-history"></i>
                          +{{ group.hiddenAttemptsCount }} intento{{ group.hiddenAttemptsCount === 1 ? '' : 's' }} previo{{ group.hiddenAttemptsCount === 1 ? '' : 's' }}
                        </button>
                      </ng-container>
                      <!-- Caso B: grupo normal con duplicados (alguno alcanzó la vía exitosa) → chevron + badge azul -->
                      <ng-container *ngIf="group.isGroup && !shouldShowAbandonedBadge(group)">
                        <i class="fas text-blue-600 mt-0.5"
                          [class.fa-chevron-right]="!isPolicyGroupExpanded(group.key)"
                          [class.fa-chevron-down]="isPolicyGroupExpanded(group.key)"
                          aria-hidden="true"></i>
                        <span class="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] rounded font-semibold whitespace-nowrap"
                          [title]="group.count + ' pólizas del mismo asegurado con este producto'">
                          {{ group.count }}
                        </span>
                      </ng-container>
                      <div class="min-w-0">
                        <div class="text-xs font-mono text-gray-900">
                          {{ group.primary.params?.code || group.primary.code || 'N/A' }}
                        </div>
                        <div class="text-xs text-gray-500">{{ group.primary.params?.insurance_product?.name ||
                          group.primary.insurance_product?.name || 'N/A' }}{{ (group.primary.params?.frequency || group.primary.frequency) ? ' - ' + (group.primary.params?.frequency || group.primary.frequency) : '' }}</div>
                      </div>
                    </div>
                  </td>
                  <td class="px-3 py-3 whitespace-nowrap">
                    <div class="text-xs text-gray-900">{{ group.primary.policy_number || group.primary.params?.number || group.primary.number || group.primary.params?.integration_data?.number || group.primary.params?.policy_number || 'N/A' }}</div>
                  </td>
                  <td class="px-3 py-3 whitespace-nowrap">
                    <div class="text-sm text-gray-900">{{ (group.primary.params?.insured?.name || group.primary.insured?.name || '') + ' ' + (group.primary.params?.insured?.apellidos || group.primary.insured?.apellidos || '') || 'N/A' }}
                    </div>
                    <div class="text-xs text-gray-500">{{ group.primary.params?.insured?.identity || group.primary.insured?.identity ||
                      'N/A' }}</div>
                  </td>
                  <td class="px-3 py-3 whitespace-nowrap">
                    <div class="flex flex-col items-start">
                      <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" [ngClass]="{
                              'bg-gray-100 text-gray-800': (group.primary.params?.policy_status || group.primary.policy_status) === 'CREATED' || !(group.primary.params?.policy_status || group.primary.policy_status),
                              'bg-blue-100 text-blue-800': (group.primary.params?.policy_status || group.primary.policy_status) === 'PLAN_SELECTED',
                              'bg-cyan-100 text-cyan-800': (group.primary.params?.policy_status || group.primary.policy_status) === 'DATA_CLIENT_CONFIRMED',
                              'bg-purple-100 text-purple-800': (group.primary.params?.policy_status || group.primary.policy_status) === 'LICENSE_UPLOADED',
                              'bg-indigo-100 text-indigo-800': (group.primary.params?.policy_status || group.primary.policy_status) === 'VERIFIED',
                              'bg-orange-100 text-orange-800': (group.primary.params?.policy_status || group.primary.policy_status) === 'ISSUED',
                              'bg-yellow-100 text-yellow-800': (group.primary.params?.policy_status || group.primary.policy_status) === 'PAYMENT_RECORDED',
                              'bg-green-100 text-green-800': (group.primary.params?.policy_status || group.primary.policy_status) === 'COMPLETED',
                              'bg-red-100 text-red-800': (group.primary.params?.policy_status || group.primary.policy_status) === 'ANNULLED'
                            }">
                        {{ translatePolicyStatus(group.primary.params?.policy_status || group.primary.policy_status || 'Sin Estado') }}
                      </span>
                      <span class="text-xs text-gray-500 mt-1">{{ group.primary.params?.policy_status || group.primary.policy_status || 'N/A' }}</span>
                    </div>
                  </td>
                  <td class="px-2 py-3 whitespace-nowrap">
                    <div class="text-sm text-gray-900" [title]="group.primary.params?.insurance?.name || group.primary.insurance?.name || 'N/A'">
                      {{ group.primary.params?.insurance?.code || group.primary.insurance?.code || 'N/A' }}
                    </div>
                  </td>
                  <td class="px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                    {{ group.primary.date ? formatDateTime(group.primary.date) : (group.primary.createdAt ? formatDateTime(group.primary.createdAt) : 'N/A') }}
                  </td>
                  <td class="px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                    {{ group.primary.params?.issue_date ? formatDateTime(group.primary.params.issue_date) : 'N/A' }}
                  </td>
                  <td class="px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                    {{ group.primary.params?.update_date ? formatDateTime(group.primary.params.update_date) : 'N/A' }}
                  </td>
                  <td class="px-3 py-3 whitespace-nowrap text-xs font-medium">
                    <button (click)="$event.stopPropagation(); viewPolicyDetail(group.primary._id)" class="text-blue-600 hover:text-blue-900 flex flex-col items-center">
                      <i class="fas fa-eye"></i>
                      <span class="mt-1">Ver Detalle</span>
                    </button>
                  </td>
                </tr>

                <!-- Sub-rows del acordeón (otras pólizas del grupo) -->
                <ng-container *ngIf="group.isGroup && isPolicyGroupExpanded(group.key)">
                  <tr *ngFor="let sub of group.others" class="policy-group-subrow">
                    <td class="px-3 py-3 pl-10">
                      <div class="flex items-start space-x-2">
                        <i class="fas fa-level-up-alt fa-rotate-90 text-gray-400 text-xs mt-1" aria-hidden="true"></i>
                        <div class="min-w-0">
                          <div class="text-xs font-mono text-gray-900">
                            {{ sub.params?.code || sub.code || 'N/A' }}
                          </div>
                          <div class="text-xs text-gray-500">{{ sub.params?.insurance_product?.name ||
                            sub.insurance_product?.name || 'N/A' }}{{ (sub.params?.frequency || sub.frequency) ? ' - ' + (sub.params?.frequency || sub.frequency) : '' }}</div>
                        </div>
                      </div>
                    </td>
                    <td class="px-3 py-3 whitespace-nowrap">
                      <div class="text-xs text-gray-900">{{ sub.policy_number || sub.params?.number || sub.number || sub.params?.integration_data?.number || sub.params?.policy_number || 'N/A' }}</div>
                    </td>
                    <td class="px-3 py-3 whitespace-nowrap">
                      <div class="text-sm text-gray-900">{{ (sub.params?.insured?.name || sub.insured?.name || '') + ' ' + (sub.params?.insured?.apellidos || sub.insured?.apellidos || '') || 'N/A' }}
                      </div>
                      <div class="text-xs text-gray-500">{{ sub.params?.insured?.identity || sub.insured?.identity ||
                        'N/A' }}</div>
                    </td>
                    <td class="px-3 py-3 whitespace-nowrap">
                      <div class="flex flex-col items-start">
                        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" [ngClass]="{
                                'bg-gray-100 text-gray-800': (sub.params?.policy_status || sub.policy_status) === 'CREATED' || !(sub.params?.policy_status || sub.policy_status),
                                'bg-blue-100 text-blue-800': (sub.params?.policy_status || sub.policy_status) === 'PLAN_SELECTED',
                                'bg-cyan-100 text-cyan-800': (sub.params?.policy_status || sub.policy_status) === 'DATA_CLIENT_CONFIRMED',
                                'bg-purple-100 text-purple-800': (sub.params?.policy_status || sub.policy_status) === 'LICENSE_UPLOADED',
                                'bg-indigo-100 text-indigo-800': (sub.params?.policy_status || sub.policy_status) === 'VERIFIED',
                                'bg-orange-100 text-orange-800': (sub.params?.policy_status || sub.policy_status) === 'ISSUED',
                                'bg-yellow-100 text-yellow-800': (sub.params?.policy_status || sub.policy_status) === 'PAYMENT_RECORDED',
                                'bg-green-100 text-green-800': (sub.params?.policy_status || sub.policy_status) === 'COMPLETED',
                                'bg-red-100 text-red-800': (sub.params?.policy_status || sub.policy_status) === 'ANNULLED'
                              }">
                          {{ translatePolicyStatus(sub.params?.policy_status || sub.policy_status || 'Sin Estado') }}
                        </span>
                        <span class="text-xs text-gray-500 mt-1">{{ sub.params?.policy_status || sub.policy_status || 'N/A' }}</span>
                      </div>
                    </td>
                    <td class="px-2 py-3 whitespace-nowrap">
                      <div class="text-sm text-gray-900" [title]="sub.params?.insurance?.name || sub.insurance?.name || 'N/A'">
                        {{ sub.params?.insurance?.code || sub.insurance?.code || 'N/A' }}
                      </div>
                    </td>
                    <td class="px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                      {{ sub.date ? formatDateTime(sub.date) : (sub.createdAt ? formatDateTime(sub.createdAt) : 'N/A') }}
                    </td>
                    <td class="px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                      {{ sub.params?.issue_date ? formatDateTime(sub.params.issue_date) : 'N/A' }}
                    </td>
                    <td class="px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                      {{ sub.params?.update_date ? formatDateTime(sub.params.update_date) : 'N/A' }}
                    </td>
                    <td class="px-3 py-3 whitespace-nowrap text-xs font-medium">
                      <button (click)="viewPolicyDetail(sub._id)" class="text-blue-600 hover:text-blue-900 flex flex-col items-center">
                        <i class="fas fa-eye"></i>
                        <span class="mt-1">Ver Detalle</span>
                      </button>
                    </td>
                  </tr>
                </ng-container>

              </ng-container>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div *ngIf="currentPoliciesData?.pagination" class="mt-6 flex items-center justify-between">
          <div class="text-sm text-gray-700">
            Mostrando {{ (currentPoliciesData.pagination.currentPage - 1) * currentPoliciesData.pagination.limit + 1 }}
            -
            {{ Math.min(currentPoliciesData.pagination.currentPage * currentPoliciesData.pagination.limit,
            currentPoliciesData.pagination.totalItems) }}
            de {{ currentPoliciesData.pagination.totalItems }} resultados
          </div>
          <div class="flex space-x-2">
            <button [disabled]="currentPoliciesData.pagination.currentPage <= 1"
              (click)="loadPoliciesPage(currentPoliciesData.pagination.currentPage - 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="px-3 py-2 bg-blue-600 text-white rounded-md">
              {{ currentPoliciesData.pagination.currentPage }}
            </span>
            <button [disabled]="currentPoliciesData.pagination.currentPage >= currentPoliciesData.pagination.totalPages"
              (click)="loadPoliciesPage(currentPoliciesData.pagination.currentPage + 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <!-- No Policies Template -->
        <ng-template #noPoliciesTemplate>
          <div class="text-center py-12 text-gray-400">
            <i class="fas fa-file-contract text-4xl mb-4"></i>
            <p class="text-lg">No hay pólizas</p>
            <p class="text-sm">No se encontraron pólizas con los filtros aplicados</p>
          </div>
        </ng-template>
      </div>
    </div>

    <!-- Emission Errors Tab -->
    <div *ngIf="activeTab === 'emission_errors'" class="space-y-6">
      <div class="bg-white rounded-xl shadow-lg p-6">
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-lg font-semibold text-gray-900">Gestión de Errores de Emisión</h3>
          <div class="flex items-center space-x-4">
            <span class="text-sm text-gray-500">
              Total: {{ currentErrorPoliciesData?.pagination?.totalItems || 0 }} errores
            </span>
            <button (click)="exportErrorsData()"
              [disabled]="isExportingErrors"
              class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
              <i [class]="isExportingErrors ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'"></i>
              <span class="text-sm font-medium">{{ isExportingErrors ? 'Exportando...' : 'Exportar' }}</span>
            </button>
          </div>
        </div>

        <!-- Search Section -->
        <div class="mb-6 space-y-4">
          <!-- Search Mode Toggle -->
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <button (click)="toggleEmissionErrorsAdvancedSearch()"
                [class]="showEmissionErrorsAdvancedSearch 
                  ? 'px-3 py-1 text-xs bg-blue-500 text-white rounded-md transition-colors duration-200'
                  : 'px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors duration-200'">
                <i class="fas fa-sliders-h mr-1"></i>
                {{ showEmissionErrorsAdvancedSearch ? 'Búsqueda Básica' : 'Búsqueda Avanzada' }}
              </button>
              <button *ngIf="isEmissionErrorsSearchActive" (click)="clearEmissionErrorsSearch()"
                class="px-3 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded-md transition-colors duration-200">
                <i class="fas fa-times mr-1"></i>
                Limpiar Búsqueda
              </button>
            </div>
            <span *ngIf="isEmissionErrorsSearchActive" class="text-sm text-blue-600 font-medium">
              <i class="fas fa-search mr-1"></i>
              Búsqueda activa
            </span>
          </div>

          <!-- General Search -->
          <div *ngIf="!showEmissionErrorsAdvancedSearch" class="flex gap-4">
            <div class="flex-1">
              <input type="text" [(ngModel)]="emissionErrorsSearchQuery" (keypress)="onEmissionErrorsSearchKeyPress($event)"
                placeholder="Buscar por código de póliza, cédula, estado o texto general..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            </div>
            <button (click)="performEmissionErrorsSearch()"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200">
              <i class="fas fa-search mr-2"></i>
              Buscar
            </button>
          </div>

          <!-- Advanced Search -->
          <div *ngIf="showEmissionErrorsAdvancedSearch" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Código de Póliza</label>
                <input type="text" [(ngModel)]="emissionErrorsAdvancedSearchParams.policy_code" placeholder="UUID de la póliza..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Cédula del Asegurado</label>
                <input type="text" [(ngModel)]="emissionErrorsAdvancedSearchParams.identity" placeholder="V12345678 o 12345678..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Estado de la Póliza</label>
                <select [(ngModel)]="emissionErrorsAdvancedSearchParams.policy_status"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="">Todos los estados</option>
                  <option value="CREATED">CREATED</option>
                  <option value="PLAN_SELECTED">PLAN_SELECTED</option>
                  <option value="DATA_CLIENT_CONFIRMED">DATA_CLIENT_CONFIRMED</option>
                  <option value="LICENSE_UPLOADED">LICENSE_UPLOADED</option>
                  <option value="VERIFIED">VERIFIED</option>
                  <option value="ISSUED">ISSUED</option>
                  <option value="PAYMENT_RECORDED">PAYMENT_RECORDED</option>
                  <option value="COMPLETED">COMPLETED</option>
                </select>
              </div>
            </div>
            <div class="flex justify-end space-x-2">
              <button (click)="clearEmissionErrorsSearch()"
                class="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors duration-200">
                <i class="fas fa-times mr-2"></i>
                Limpiar
              </button>
              <button (click)="performEmissionErrorsSearch()"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200">
                <i class="fas fa-search mr-2"></i>
                Buscar
              </button>
            </div>
          </div>
        </div>

        <!-- Error Policies Table -->
        <div *ngIf="currentErrorPoliciesData?.data?.length > 0; else noErrorsTemplate" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Código
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Asegurado
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th class="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aseg.
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado Emisión
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Error
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr *ngFor="let policy of currentErrorPoliciesData.data" class="hover:bg-gray-50">
                <td class="px-3 py-3">
                  <div class="text-xs font-mono text-gray-900">
                    {{ policy.params?.code || policy.code || 'N/A' }}
                  </div>
                  <div class="text-xs text-gray-500">{{ policy.params?.insurance_product?.name ||
                    policy.insurance_product?.name || 'N/A' }}{{ (policy.params?.frequency || policy.frequency) ? ' - ' + (policy.params?.frequency || policy.frequency) : '' }}</div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ (policy.params?.insured?.name || policy.insured?.name || '') + ' ' + (policy.params?.insured?.apellidos || policy.insured?.apellidos || '') || 'N/A' }}
                  </div>
                  <div class="text-xs text-gray-500">{{ policy.params?.insured?.identity || policy.insured?.identity ||
                    'N/A' }}</div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="flex flex-col items-start">
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" [ngClass]="{
                            'bg-gray-100 text-gray-800': (policy.params?.policy_status || policy.policy_status) === 'CREATED' || !(policy.params?.policy_status || policy.policy_status),
                            'bg-blue-100 text-blue-800': (policy.params?.policy_status || policy.policy_status) === 'PLAN_SELECTED',
                            'bg-cyan-100 text-cyan-800': (policy.params?.policy_status || policy.policy_status) === 'DATA_CLIENT_CONFIRMED',
                            'bg-purple-100 text-purple-800': (policy.params?.policy_status || policy.policy_status) === 'LICENSE_UPLOADED',
                            'bg-indigo-100 text-indigo-800': (policy.params?.policy_status || policy.policy_status) === 'VERIFIED',
                            'bg-orange-100 text-orange-800': (policy.params?.policy_status || policy.policy_status) === 'ISSUED',
                            'bg-yellow-100 text-yellow-800': (policy.params?.policy_status || policy.policy_status) === 'PAYMENT_RECORDED',
                            'bg-green-100 text-green-800': (policy.params?.policy_status || policy.policy_status) === 'COMPLETED',
                            'bg-red-100 text-red-800': (policy.params?.policy_status || policy.policy_status) === 'ANNULLED'
                          }">
                      {{ translatePolicyStatus(policy.params?.policy_status || policy.policy_status || 'Sin Estado') }}
                    </span>
                    <span class="text-xs text-gray-500 mt-1">{{ policy.params?.policy_status || policy.policy_status || 'N/A' }}</span>
                  </div>
                </td>
                <td class="px-2 py-3 whitespace-nowrap">
                  <div class="text-sm text-gray-900" [title]="policy.params?.insurance?.name || policy.insurance?.name || 'N/A'">
                    {{ policy.params?.insurance?.code || policy.insurance?.code || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" [ngClass]="{
                          'bg-yellow-100 text-yellow-800': (policy.params?.integration_data?.emission_status || policy.integration_data?.emission_status) === 'in_progress',
                          'bg-blue-100 text-blue-800': (policy.params?.integration_data?.emission_status || policy.integration_data?.emission_status) === 'quoted',
                          'bg-green-100 text-green-800': (policy.params?.integration_data?.emission_status || policy.integration_data?.emission_status) === 'emitted',
                          'bg-emerald-100 text-emerald-800': (policy.params?.integration_data?.emission_status || policy.integration_data?.emission_status) === 'paid',
                          'bg-red-100 text-red-800': (policy.params?.integration_data?.emission_status || policy.integration_data?.emission_status) === 'emission_error',
                          'bg-gray-100 text-gray-800': (policy.params?.integration_data?.emission_status || policy.integration_data?.emission_status) === 'cancelled' || !(policy.params?.integration_data?.emission_status || policy.integration_data?.emission_status)
                        }">
                    {{ policy.params?.integration_data?.emission_status || policy.integration_data?.emission_status ||
                    'Sin Estado' }}
                  </span>
                </td>
                <td class="px-3 py-3">
                  <div class="text-xs text-gray-900">{{ policy.params?.integration_data?.error_id ||
                    policy.integration_data?.error_id || 'N/A' }}</div>
                  <div class="text-xs text-gray-500">Tipo: {{ policy.params?.integration_data?.error_type ||
                    policy.integration_data?.error_type || 'N/A' }}</div>
                  <div class="text-xs text-gray-400 mt-1"
                    [title]="policy.params?.integration_data?.extra_data || policy.integration_data?.extra_data || ''">
                    {{ (policy.params?.integration_data?.extra_data || policy.integration_data?.extra_data || '') |
                    slice:0:50 }}{{ (policy.params?.integration_data?.extra_data || policy.integration_data?.extra_data
                    || '').length > 50 ? '...' : '' }}
                  </div>
                </td>
                <td class="px-2 py-3 whitespace-nowrap">
                  <div class="flex flex-col space-y-1">
                    <button
                      *ngIf="(policy.params?.integration_data?.emission_status || policy.integration_data?.emission_status) === 'in_progress' || (policy.params?.integration_data?.emission_status || policy.integration_data?.emission_status) === 'quoted'"
                      (click)="retryPolicy(policy.params?.code || policy.code || policy._id)"
                      class="text-xs text-blue-600 hover:text-blue-900 text-left">
                      <i class="fas fa-sync-alt mr-1"></i>
                      Reintentar
                    </button>
                    <button (click)="viewPolicyDetail(policy._id)" class="text-xs text-blue-600 hover:text-blue-900 text-left">
                      <i class="fas fa-eye mr-1"></i>
                      Ver Detalle
                    </button>
                    <button (click)="openResolveErrorModal(policy, 'emission')" class="text-xs text-green-600 hover:text-green-900 text-left">
                      <i class="fas fa-check-circle mr-1"></i>
                      Marcar Resuelto
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Error Policies Pagination -->
        <div *ngIf="currentErrorPoliciesData?.pagination" class="mt-6 flex items-center justify-between">
          <div class="text-sm text-gray-700">
            Mostrando {{ (currentErrorPoliciesData.pagination.currentPage - 1) *
            currentErrorPoliciesData.pagination.limit + 1 }} -
            {{ Math.min(currentErrorPoliciesData.pagination.currentPage * currentErrorPoliciesData.pagination.limit,
            currentErrorPoliciesData.pagination.totalItems) }}
            de {{ currentErrorPoliciesData.pagination.totalItems }} errores
          </div>
          <div class="flex space-x-2">
            <button [disabled]="currentErrorPoliciesData.pagination.currentPage <= 1"
              (click)="loadErrorPoliciesPage(currentErrorPoliciesData.pagination.currentPage - 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="px-3 py-2 bg-red-600 text-white rounded-md">
              {{ currentErrorPoliciesData.pagination.currentPage }}
            </span>
            <button
              [disabled]="currentErrorPoliciesData.pagination.currentPage >= currentErrorPoliciesData.pagination.totalPages"
              (click)="loadErrorPoliciesPage(currentErrorPoliciesData.pagination.currentPage + 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <!-- No Errors Template -->
        <ng-template #noErrorsTemplate>
          <div class="text-center py-12 text-gray-400">
            <i class="fas fa-check-circle text-4xl mb-4 text-green-400"></i>
            <p class="text-lg">No hay errores</p>
            <p class="text-sm">Todas las pólizas se han procesado correctamente</p>
          </div>
        </ng-template>
      </div>
    </div>

    <!-- Payment Errors Tab -->
    <div *ngIf="activeTab === 'payment_errors'" class="space-y-6">
      <div class="bg-white rounded-xl shadow-lg p-6">
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-lg font-semibold text-gray-900">Gestión de Errores de Pagos</h3>
          <div class="flex items-center space-x-4">
            <span class="text-sm text-gray-500">
              Total: {{ currentPaymentErrorsData?.pagination?.totalItems || 0 }} errores
            </span>
            <button (click)="exportPaymentErrorsData()"
              [disabled]="isExportingPaymentErrors"
              class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
              <i [class]="isExportingPaymentErrors ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'"></i>
              <span class="text-sm font-medium">{{ isExportingPaymentErrors ? 'Exportando...' : 'Exportar' }}</span>
            </button>
          </div>
        </div>

        <!-- Search Section -->
        <div class="mb-6 space-y-4">
          <!-- Search Mode Toggle -->
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-4">
              <button (click)="togglePaymentErrorsAdvancedSearch()"
                [class]="showPaymentErrorsAdvancedSearch 
                  ? 'px-3 py-1 text-xs bg-blue-500 text-white rounded-md transition-colors duration-200'
                  : 'px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors duration-200'">
                <i class="fas fa-sliders-h mr-1"></i>
                {{ showPaymentErrorsAdvancedSearch ? 'Búsqueda Básica' : 'Búsqueda Avanzada' }}
              </button>
              <button *ngIf="isPaymentErrorsSearchActive" (click)="clearPaymentErrorsSearch()"
                class="px-3 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 rounded-md transition-colors duration-200">
                <i class="fas fa-times mr-1"></i>
                Limpiar Búsqueda
              </button>
            </div>
            <span *ngIf="isPaymentErrorsSearchActive" class="text-sm text-blue-600 font-medium">
              <i class="fas fa-search mr-1"></i>
              Búsqueda activa
            </span>
          </div>

          <!-- General Search -->
          <div *ngIf="!showPaymentErrorsAdvancedSearch" class="flex gap-4">
            <div class="flex-1">
              <input type="text" [(ngModel)]="paymentErrorsSearchQuery" (keypress)="onPaymentErrorsSearchKeyPress($event)"
                placeholder="Buscar por código de póliza, número de la póliza, cédula, estado o texto general..."
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            </div>
            <button (click)="performPaymentErrorsSearch()"
              class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200">
              <i class="fas fa-search mr-2"></i>
              Buscar
            </button>
          </div>

          <!-- Advanced Search -->
          <div *ngIf="showPaymentErrorsAdvancedSearch" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Código de Póliza</label>
                <input type="text" [(ngModel)]="paymentErrorsAdvancedSearchParams.policy_code" placeholder="UUID de la póliza..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Cédula del Asegurado</label>
                <input type="text" [(ngModel)]="paymentErrorsAdvancedSearchParams.identity" placeholder="V12345678 o 12345678..."
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Estado de la Póliza</label>
                <select [(ngModel)]="paymentErrorsAdvancedSearchParams.policy_status"
                  class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="">Todos los estados</option>
                  <option value="CREATED">CREATED</option>
                  <option value="PLAN_SELECTED">PLAN_SELECTED</option>
                  <option value="DATA_CLIENT_CONFIRMED">DATA_CLIENT_CONFIRMED</option>
                  <option value="LICENSE_UPLOADED">LICENSE_UPLOADED</option>
                  <option value="VERIFIED">VERIFIED</option>
                  <option value="ISSUED">ISSUED</option>
                  <option value="PAYMENT_RECORDED">PAYMENT_RECORDED</option>
                  <option value="COMPLETED">COMPLETED</option>
                </select>
              </div>
            </div>
            <div class="flex justify-end space-x-2">
              <button (click)="clearPaymentErrorsSearch()"
                class="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors duration-200">
                <i class="fas fa-times mr-2"></i>
                Limpiar
              </button>
              <button (click)="performPaymentErrorsSearch()"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200">
                <i class="fas fa-search mr-2"></i>
                Buscar
              </button>
            </div>
          </div>
        </div>

        <!-- Payment Errors Table -->
        <div *ngIf="currentPaymentErrorsData?.data?.length > 0; else noPaymentErrorsTemplate" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Código
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nro. Póliza
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Asegurado
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th class="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aseg.
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Error
                </th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr *ngFor="let policy of currentPaymentErrorsData.data" class="hover:bg-gray-50">
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="text-xs font-mono text-gray-900">
                    {{ policy.params?.code || policy.code || 'N/A' }}
                  </div>
                  <div class="text-xs text-gray-500 mt-1">
                    {{ policy.params?.insurance_product?.name || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="text-xs text-gray-900">
                    {{ policy.params?.number || policy.policy_number || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="text-sm text-gray-900">
                    {{ (policy.params?.insured?.name || '') + ' ' + (policy.params?.insured?.apellidos || '') || 'N/A' }}
                  </div>
                  <div class="text-xs text-gray-500">
                    {{ policy.params?.insured?.identity || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="flex flex-col items-start">
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" [ngClass]="{
                      'bg-gray-100 text-gray-800': (policy.params?.policy_status || policy.policy_status) === 'CREATED' || !(policy.params?.policy_status || policy.policy_status),
                      'bg-blue-100 text-blue-800': (policy.params?.policy_status || policy.policy_status) === 'PLAN_SELECTED',
                      'bg-cyan-100 text-cyan-800': (policy.params?.policy_status || policy.policy_status) === 'DATA_CLIENT_CONFIRMED',
                      'bg-purple-100 text-purple-800': (policy.params?.policy_status || policy.policy_status) === 'LICENSE_UPLOADED',
                      'bg-indigo-100 text-indigo-800': (policy.params?.policy_status || policy.policy_status) === 'VERIFIED',
                      'bg-orange-100 text-orange-800': (policy.params?.policy_status || policy.policy_status) === 'ISSUED',
                      'bg-yellow-100 text-yellow-800': (policy.params?.policy_status || policy.policy_status) === 'PAYMENT_RECORDED',
                      'bg-green-100 text-green-800': (policy.params?.policy_status || policy.policy_status) === 'COMPLETED',
                      'bg-red-100 text-red-800': (policy.params?.policy_status || policy.policy_status) === 'ANNULLED'
                    }">
                      {{ translatePolicyStatus(policy.params?.policy_status || policy.policy_status || 'Sin Estado') }}
                    </span>
                    <span class="text-xs text-gray-500 mt-1">{{ policy.params?.policy_status || policy.policy_status || 'N/A' }}</span>
                  </div>
                </td>
                <td class="px-2 py-3 whitespace-nowrap">
                  <div class="text-sm text-gray-900" [title]="policy.params?.insurance?.name || policy.insurance?.name || 'N/A'">
                    {{ policy.params?.insurance?.code || policy.insurance?.code || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3">
                  <div class="text-xs text-gray-900">
                    {{ getPaymentErrorMessage(policy) }}
                  </div>
                  <div class="text-xs text-gray-400 mt-1">
                    Fecha: {{ policy.params?.payment_notification_error_date | date:'short' }}
                  </div>
                </td>
                <td class="px-2 py-3 whitespace-nowrap">
                  <div class="flex flex-col space-y-1">
                    <button (click)="openFinancialPaymentDetail(policy)"
                      [disabled]="!canOpenFinancialPaymentDetail(policy)"
                      [title]="canOpenFinancialPaymentDetail(policy) ? 'Abre las cuotas y el detalle del pago en el perfil financiero' : 'Falta enlace al cliente o factura — no se puede abrir el detalle'"
                      class="text-xs text-blue-700 hover:text-blue-900 text-left font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                      <i class="fas fa-external-link-alt mr-1"></i>
                      Ver detalle del pago
                    </button>
                    <button (click)="viewPolicyDetail(policy._id)" class="text-xs text-blue-600 hover:text-blue-900 text-left">
                      <i class="fas fa-eye mr-1"></i>
                      Ver póliza
                    </button>
                    <button (click)="retryPaymentNotification(policy)"
                      [disabled]="policy._retrying"
                      class="text-xs text-orange-600 hover:text-orange-900 text-left disabled:opacity-50 disabled:cursor-not-allowed">
                      <i class="fas" [ngClass]="policy._retrying ? 'fa-spinner fa-spin' : 'fa-redo'" class="mr-1"></i>
                      {{ policy._retrying ? 'Reintentando...' : 'Reintentar Pago' }}
                    </button>
                    <button (click)="openResolveErrorModal(policy, 'payment')" class="text-xs text-green-600 hover:text-green-900 text-left">
                      <i class="fas fa-check-circle mr-1"></i>
                      Marcar Resuelto
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Payment Errors Pagination -->
        <div *ngIf="currentPaymentErrorsData?.pagination" class="mt-6 flex items-center justify-between">
          <div class="text-sm text-gray-700">
            Mostrando {{ (currentPaymentErrorsData.pagination.currentPage - 1) *
            currentPaymentErrorsData.pagination.limit + 1 }} -
            {{ Math.min(currentPaymentErrorsData.pagination.currentPage * currentPaymentErrorsData.pagination.limit,
            currentPaymentErrorsData.pagination.totalItems) }}
            de {{ currentPaymentErrorsData.pagination.totalItems }} errores
          </div>
          <div class="flex space-x-2">
            <button [disabled]="currentPaymentErrorsData.pagination.currentPage <= 1"
              (click)="loadPaymentErrorsPage(currentPaymentErrorsData.pagination.currentPage - 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="px-3 py-2 bg-orange-600 text-white rounded-md">
              {{ currentPaymentErrorsData.pagination.currentPage }}
            </span>
            <button
              [disabled]="currentPaymentErrorsData.pagination.currentPage >= currentPaymentErrorsData.pagination.totalPages"
              (click)="loadPaymentErrorsPage(currentPaymentErrorsData.pagination.currentPage + 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <!-- No Payment Errors Template -->
        <ng-template #noPaymentErrorsTemplate>
          <div class="text-center py-12 text-gray-400">
            <i class="fas fa-check-circle text-4xl mb-4 text-green-400"></i>
            <p class="text-lg">No hay errores de pagos</p>
            <p class="text-sm">Todos los pagos se han procesado correctamente</p>
          </div>
        </ng-template>
      </div>
    </div>

    <!-- Verification Errors Tab -->
    <div *ngIf="activeTab === 'verification_errors'" class="space-y-6">
      <div class="bg-white rounded-xl shadow-lg p-6">
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-lg font-semibold text-gray-900">Gestión de Errores de Verificación</h3>
          <div class="flex items-center space-x-4">
            <span class="text-sm text-gray-500">
              Total: {{ currentVerificationErrorsData?.pagination?.totalItems || 0 }} errores
            </span>
            <button (click)="exportVerificationErrorsData()"
              [disabled]="isExportingVerificationErrors"
              class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
              <i [class]="isExportingVerificationErrors ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'"></i>
              <span class="text-sm font-medium">{{ isExportingVerificationErrors ? 'Exportando...' : 'Exportar' }}</span>
            </button>
          </div>
        </div>

        <!-- Search Section -->
        <div class="mb-6">
          <div class="flex gap-4">
            <div class="flex-1">
              <input type="text" 
                [(ngModel)]="verificationErrorsSearchQuery"
                (keyup.enter)="performVerificationErrorsSearch()"
                placeholder="Buscar por código, cédula, nombre..."
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            </div>
            <button (click)="performVerificationErrorsSearch()"
              class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Buscar
            </button>
            <button *ngIf="isVerificationErrorsSearchActive" 
              (click)="clearVerificationErrorsSearch()"
              class="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors">
              <i class="fas fa-times mr-2"></i>Limpiar
            </button>
          </div>
        </div>

        <!-- Verification Errors Table -->
        <div *ngIf="currentVerificationErrorsData?.data?.length > 0; else noVerificationErrorsTemplate" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nro. Póliza</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asegurado</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th class="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aseg.</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error de Verificación</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Error</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr *ngFor="let policy of currentVerificationErrorsData.data" class="hover:bg-gray-50">
                <td class="px-3 py-3">
                  <div class="text-xs font-mono text-gray-900">
                    {{ policy.params?.code || policy.code || 'N/A' }}
                  </div>
                  <div class="text-xs text-gray-500 mt-1">
                    {{ policy.params?.insurance_product?.name || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="text-xs text-gray-900">
                    {{ policy.params?.number || policy.policy_number || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="text-sm text-gray-900">
                    {{ (policy.params?.insured?.name || '') + ' ' + (policy.params?.insured?.apellidos || '') || 'N/A' }}
                  </div>
                  <div class="text-xs text-gray-500">
                    {{ policy.params?.insured?.identity || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="flex flex-col items-start">
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" [ngClass]="{
                      'bg-gray-100 text-gray-800': policy.params?.policy_status === 'CREATED' || !policy.params?.policy_status,
                      'bg-blue-100 text-blue-800': policy.params?.policy_status === 'PLAN_SELECTED',
                      'bg-cyan-100 text-cyan-800': policy.params?.policy_status === 'DATA_CLIENT_CONFIRMED',
                      'bg-purple-100 text-purple-800': policy.params?.policy_status === 'LICENSE_UPLOADED',
                      'bg-indigo-100 text-indigo-800': policy.params?.policy_status === 'VERIFIED',
                      'bg-orange-100 text-orange-800': policy.params?.policy_status === 'ISSUED',
                      'bg-yellow-100 text-yellow-800': policy.params?.policy_status === 'PAYMENT_RECORDED',
                      'bg-green-100 text-green-800': policy.params?.policy_status === 'COMPLETED',
                      'bg-red-100 text-red-800': policy.params?.policy_status === 'ANNULLED'
                    }">
                      {{ translatePolicyStatus(policy.params?.policy_status || 'Sin Estado') }}
                    </span>
                    <span class="text-xs text-gray-500 mt-1">{{ policy.params?.policy_status || 'N/A' }}</span>
                  </div>
                </td>
                <td class="px-2 py-3 whitespace-nowrap">
                  <div class="text-sm text-gray-900" [title]="policy.params?.insurance?.name || 'N/A'">
                    {{ policy.params?.insurance?.code || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3">
                  <div class="text-xs text-red-600">
                    <div class="font-semibold">{{ policy.params?.verification_error_info?.code || policy.verification_error_info?.code || 'N/A' }}</div>
                    <div class="text-gray-700 mt-1">{{ policy.params?.verification_error_info?.msg || policy.verification_error_info?.msg || 'Error de verificación' }}</div>
                    
                    <!-- Mostrar información de serial corregido si existe -->
                    <div *ngIf="policy.params?.verification_error_info?.special_case === 'different_serial_sc'" class="mt-2 text-gray-700">
                      <div>Serial que colocó el usuario/OCR: <span class="font-mono">{{ policy.params.verification_error_info.original_serial_user || 'N/A' }}</span></div>
                      <div>Serial en la Aseguradora: <span class="font-mono text-green-700">{{ policy.params.verification_error_info.corrected_serial_sc || 'N/A' }}</span></div>
                    </div>
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                  {{ policy.params?.verification_error_info?.date ? formatDateTime(policy.params.verification_error_info.date) : (policy.verification_error_info?.date ? formatDateTime(policy.verification_error_info.date) : 'N/A') }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Verification Errors Pagination -->
        <div *ngIf="currentVerificationErrorsData?.pagination" class="mt-6 flex items-center justify-between">
          <div class="text-sm text-gray-700">
            Mostrando {{ (currentVerificationErrorsData.pagination.currentPage - 1) *
            currentVerificationErrorsData.pagination.limit + 1 }} -
            {{ Math.min(currentVerificationErrorsData.pagination.currentPage * currentVerificationErrorsData.pagination.limit,
            currentVerificationErrorsData.pagination.totalItems) }}
            de {{ currentVerificationErrorsData.pagination.totalItems }} errores
          </div>
          <div class="flex space-x-2">
            <button [disabled]="currentVerificationErrorsData.pagination.currentPage <= 1"
              (click)="loadVerificationErrorsPage(currentVerificationErrorsData.pagination.currentPage - 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="px-3 py-2 bg-blue-600 text-white rounded-md">
              {{ currentVerificationErrorsData.pagination.currentPage }}
            </span>
            <button
              [disabled]="currentVerificationErrorsData.pagination.currentPage >= currentVerificationErrorsData.pagination.totalPages"
              (click)="loadVerificationErrorsPage(currentVerificationErrorsData.pagination.currentPage + 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <!-- No Verification Errors Template -->
        <ng-template #noVerificationErrorsTemplate>
          <div class="text-center py-12 text-gray-400">
            <i class="fas fa-check-circle text-4xl mb-4 text-green-400"></i>
            <p class="text-lg">No hay errores de verificación</p>
            <p class="text-sm">Todas las verificaciones se completaron correctamente</p>
          </div>
        </ng-template>
      </div>
    </div>

    <!-- Vehicle Use Errors Tab -->
    <div *ngIf="activeTab === 'vehicle_use_errors'" class="space-y-6">
      <div class="bg-white rounded-xl shadow-lg p-6">
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-lg font-semibold text-gray-900">Gestión de Errores de Uso de Vehículo</h3>
          <div class="flex items-center space-x-4">
            <span class="text-sm text-gray-500">
              Total: {{ currentVehicleUseErrorsData?.pagination?.totalItems || 0 }} errores
            </span>
            <button (click)="exportVehicleUseErrorsData()"
              [disabled]="isExportingVehicleUseErrors"
              class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
              <i [class]="isExportingVehicleUseErrors ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'"></i>
              <span class="text-sm font-medium">{{ isExportingVehicleUseErrors ? 'Exportando...' : 'Exportar' }}</span>
            </button>
          </div>
        </div>

        <!-- Search Section -->
        <div class="mb-6">
          <div class="flex gap-4">
            <div class="flex-1">
              <input type="text" 
                [(ngModel)]="vehicleUseErrorsSearchQuery"
                (keyup.enter)="performVehicleUseErrorsSearch()"
                placeholder="Buscar por código, cédula, nombre..."
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            </div>
            <button (click)="performVehicleUseErrorsSearch()"
              class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Buscar
            </button>
            <button *ngIf="isVehicleUseErrorsSearchActive" 
              (click)="clearVehicleUseErrorsSearch()"
              class="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors">
              <i class="fas fa-times mr-2"></i>Limpiar
            </button>
          </div>
        </div>

        <!-- Vehicle Use Errors Table -->
        <div *ngIf="currentVehicleUseErrorsData?.data?.length > 0; else noVehicleUseErrorsTemplate" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nro. Póliza</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asegurado</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th class="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aseg.</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Error de Uso</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Error</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr *ngFor="let policy of currentVehicleUseErrorsData.data" class="hover:bg-gray-50">
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="text-sm text-gray-900">
                    {{ policy.params?.code || policy.code || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="text-xs text-gray-900">
                    {{ policy.params?.number || policy.policy_number || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="text-sm text-gray-900">
                    {{ (policy.params?.insured?.name || '') + ' ' + (policy.params?.insured?.apellidos || '') || 'N/A' }}
                  </div>
                  <div class="text-xs text-gray-500">
                    {{ policy.params?.insured?.identity || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="flex flex-col items-start">
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" [ngClass]="{
                      'bg-gray-100 text-gray-800': policy.params?.policy_status === 'CREATED' || !policy.params?.policy_status,
                      'bg-blue-100 text-blue-800': policy.params?.policy_status === 'PLAN_SELECTED',
                      'bg-cyan-100 text-cyan-800': policy.params?.policy_status === 'DATA_CLIENT_CONFIRMED',
                      'bg-purple-100 text-purple-800': policy.params?.policy_status === 'LICENSE_UPLOADED',
                      'bg-indigo-100 text-indigo-800': policy.params?.policy_status === 'VERIFIED',
                      'bg-orange-100 text-orange-800': policy.params?.policy_status === 'ISSUED',
                      'bg-yellow-100 text-yellow-800': policy.params?.policy_status === 'PAYMENT_RECORDED',
                      'bg-green-100 text-green-800': policy.params?.policy_status === 'COMPLETED',
                      'bg-red-100 text-red-800': policy.params?.policy_status === 'ANNULLED'
                    }">
                      {{ translatePolicyStatus(policy.params?.policy_status || 'Sin Estado') }}
                    </span>
                    <span class="text-xs text-gray-500 mt-1">{{ policy.params?.policy_status || 'N/A' }}</span>
                  </div>
                </td>
                <td class="px-2 py-3 whitespace-nowrap">
                  <div class="text-sm text-gray-900" [title]="policy.params?.insurance?.name || 'N/A'">
                    {{ policy.params?.insurance?.code || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3">
                  <div class="text-xs">
                    <div class="font-bold mb-1">
                      {{ policy.params?.vehicle_use_validation_error_info?.code || 'N/A' }}
                    </div>
                    <div class="text-gray-700 mb-2">
                      {{ policy.params?.vehicle_use_validation_error_info?.msg || 'Error de uso de vehículo' }}
                    </div>
                    
                    <!-- Detalles específicos por tipo de error -->
                    <div class="mt-2 space-y-1 text-gray-600">
                      <div><strong>Uso Usuario:</strong> {{ policy.params?.vehicle_use_validation_error_info?.userUse || 'N/A' }}</div>
                      
                      <!-- VS20: Uso vs Marca/Modelo -->
                      <div *ngIf="policy.params?.vehicle_use_validation_error_info?.code === 'VS20'">
                        <div><strong>Uso SC:</strong> {{ policy.params?.vehicle_use_validation_error_info?.scUse || 'N/A' }}</div>
                        <div><strong>Marca:</strong> {{ policy.params?.vehicle_use_validation_error_info?.brand?.name || 'N/A' }}</div>
                        <div><strong>Modelo:</strong> {{ policy.params?.vehicle_use_validation_error_info?.model?.name || 'N/A' }}</div>
                        <div><strong>Año:</strong> {{ policy.params?.vehicle_use_validation_error_info?.year || 'N/A' }}</div>
                      </div>
                      
                      <!-- VS22: Uso vs Plan -->
                      <div *ngIf="policy.params?.vehicle_use_validation_error_info?.code === 'VS22'">
                        <div><strong>Uso del Plan:</strong> {{ policy.params?.vehicle_use_validation_error_info?.planUse || 'N/A' }}</div>
                        <div><strong>Propuesta:</strong> {{ policy.params?.vehicle_use_validation_error_info?.proposal || 'N/A' }}</div>
                      </div>
                    </div>
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                  {{ policy.params?.vehicle_use_validation_error_info?.date ? formatDateTime(policy.params.vehicle_use_validation_error_info.date) : 'N/A' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Vehicle Use Errors Pagination -->
        <div *ngIf="currentVehicleUseErrorsData?.pagination" class="mt-6 flex items-center justify-between">
          <div class="text-sm text-gray-700">
            Mostrando {{ (currentVehicleUseErrorsData.pagination.currentPage - 1) *
            currentVehicleUseErrorsData.pagination.limit + 1 }} -
            {{ Math.min(currentVehicleUseErrorsData.pagination.currentPage * currentVehicleUseErrorsData.pagination.limit,
            currentVehicleUseErrorsData.pagination.totalItems) }}
            de {{ currentVehicleUseErrorsData.pagination.totalItems }} errores
          </div>
          <div class="flex space-x-2">
            <button [disabled]="currentVehicleUseErrorsData.pagination.currentPage <= 1"
              (click)="loadVehicleUseErrorsPage(currentVehicleUseErrorsData.pagination.currentPage - 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="px-3 py-2 bg-blue-600 text-white rounded-md">
              {{ currentVehicleUseErrorsData.pagination.currentPage }}
            </span>
            <button
              [disabled]="currentVehicleUseErrorsData.pagination.currentPage >= currentVehicleUseErrorsData.pagination.totalPages"
              (click)="loadVehicleUseErrorsPage(currentVehicleUseErrorsData.pagination.currentPage + 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <!-- No Vehicle Use Errors Template -->
        <ng-template #noVehicleUseErrorsTemplate>
          <div class="text-center py-12 text-gray-400">
            <i class="fas fa-check-circle text-4xl mb-4 text-green-400"></i>
            <p class="text-lg">No hay errores de uso de vehículo</p>
            <p class="text-sm">Todos los usos de vehículo son correctos</p>
          </div>
        </ng-template>
      </div>
    </div>

    <!-- Default Address Tab -->
    <div *ngIf="activeTab === 'default_address'" class="space-y-6">
      <div class="bg-white rounded-xl shadow-lg p-6">
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-lg font-semibold text-gray-900">Pólizas Emitidas con Dirección Por Defecto</h3>
          <div class="flex items-center space-x-4">
            <span class="text-sm text-gray-500">
              Total: {{ currentDefaultAddressData?.pagination?.totalItems || 0 }} pólizas
            </span>
            <button (click)="exportDefaultAddressData()"
              [disabled]="isExportingDefaultAddress"
              class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
              <i [class]="isExportingDefaultAddress ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'"></i>
              <span class="text-sm font-medium">{{ isExportingDefaultAddress ? 'Exportando...' : 'Exportar' }}</span>
            </button>
          </div>
        </div>

        <!-- Search Section -->
        <div class="mb-6">
          <div class="flex gap-4">
            <div class="flex-1">
              <input type="text" 
                [(ngModel)]="defaultAddressSearchQuery"
                (keyup.enter)="performDefaultAddressSearch()"
                placeholder="Buscar por código, cédula, nombre..."
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            </div>
            <button (click)="performDefaultAddressSearch()"
              class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              Buscar
            </button>
            <button *ngIf="isDefaultAddressSearchActive" 
              (click)="clearDefaultAddressSearch()"
              class="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors">
              <i class="fas fa-times mr-2"></i>Limpiar
            </button>
          </div>
        </div>

        <!-- Default Address Table -->
        <div *ngIf="currentDefaultAddressData?.data?.length > 0; else noDefaultAddressTemplate" class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nro. Póliza</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asegurado</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th class="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aseg.</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">F. Emisión</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reintento</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detalles</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr *ngFor="let policy of currentDefaultAddressData.data" class="hover:bg-gray-50">
                <td class="px-3 py-3">
                  <div class="text-xs font-mono text-gray-900">
                    {{ policy.params?.code || 'N/A' }}
                  </div>
                  <div class="text-xs text-gray-500 mt-1">
                    {{ policy.params?.insurance_product?.name || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="text-xs text-gray-900">
                    {{ policy.params?.number || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="text-sm text-gray-900">
                    {{ (policy.params?.insured?.name || '') + ' ' + (policy.params?.insured?.apellidos || '') || 'N/A' }}
                  </div>
                  <div class="text-xs text-gray-500">
                    {{ policy.params?.insured?.identity || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="flex flex-col items-start">
                    <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" [ngClass]="{
                      'bg-gray-100 text-gray-800': policy.params?.policy_status === 'CREATED' || !policy.params?.policy_status,
                      'bg-blue-100 text-blue-800': policy.params?.policy_status === 'PLAN_SELECTED',
                      'bg-cyan-100 text-cyan-800': policy.params?.policy_status === 'DATA_CLIENT_CONFIRMED',
                      'bg-purple-100 text-purple-800': policy.params?.policy_status === 'LICENSE_UPLOADED',
                      'bg-indigo-100 text-indigo-800': policy.params?.policy_status === 'VERIFIED',
                      'bg-orange-100 text-orange-800': policy.params?.policy_status === 'ISSUED',
                      'bg-yellow-100 text-yellow-800': policy.params?.policy_status === 'PAYMENT_RECORDED',
                      'bg-green-100 text-green-800': policy.params?.policy_status === 'COMPLETED',
                      'bg-red-100 text-red-800': policy.params?.policy_status === 'ANNULLED'
                    }">
                      {{ translatePolicyStatus(policy.params?.policy_status || 'Sin Estado') }}
                    </span>
                    <span class="text-xs text-gray-500 mt-1">{{ policy.params?.policy_status || 'N/A' }}</span>
                  </div>
                </td>
                <td class="px-2 py-3 whitespace-nowrap">
                  <div class="text-sm text-gray-900" [title]="policy.params?.insurance?.name || 'N/A'">
                    {{ policy.params?.insurance?.code || 'N/A' }}
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap text-xs text-gray-500">
                  {{ policy.params?.issue_date ? formatDateTime(policy.params.issue_date) : 'N/A' }}
                </td>
                <td class="px-3 py-3">
                  <span *ngIf="policy.params?.default_address_info?.original_error"
                    class="px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">
                    <i class="fas fa-redo mr-1"></i>Sí
                  </span>
                  <span *ngIf="!policy.params?.default_address_info?.original_error"
                    class="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                    No
                  </span>
                </td>
                <td class="px-3 py-3">
                  <!-- Caso 1: Error de validación con reintento -->
                  <div *ngIf="policy.params?.default_address_info?.original_error">
                    <div class="flex items-start space-x-2">
                      <i class="fas fa-exclamation-triangle text-orange-500 mt-0.5"></i>
                      <div>
                        <div class="text-xs font-semibold text-orange-700">Error de Validación</div>
                        <div class="text-xs text-gray-700 mt-1">
                          {{ policy.params?.default_address_info?.original_error?.deError || 'Error desconocido' }}
                        </div>
                        <div class="text-xs text-gray-500 mt-1">
                          <span class="font-medium">Dirección original:</span><br>
                          {{ policy.params?.default_address_info?.insured?.original_city || 'N/A' }},
                          {{ policy.params?.default_address_info?.insured?.original_state || 'N/A' }},
                          {{ policy.params?.default_address_info?.insured?.original_country || 'N/A' }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Caso 2: Campos faltantes (sin error de validación) -->
                  <div *ngIf="!policy.params?.default_address_info?.original_error && policy.params?.default_address_info?.insured?.missing_fields">
                    <div class="flex items-start space-x-2">
                      <i class="fas fa-info-circle text-blue-500 mt-0.5"></i>
                      <div>
                        <div class="text-xs font-semibold text-blue-700">Campos Faltantes</div>
                        <div class="text-xs text-gray-700 mt-1">
                          {{ translateMissingFields(policy.params?.default_address_info?.insured?.missing_fields) }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Caso 3: Sin información -->
                  <div *ngIf="!policy.params?.default_address_info?.original_error && !policy.params?.default_address_info?.insured?.missing_fields">
                    <span class="text-xs text-gray-400">Sin detalles</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Default Address Pagination -->
        <div *ngIf="currentDefaultAddressData?.pagination" class="mt-6 flex items-center justify-between">
          <div class="text-sm text-gray-700">
            Mostrando {{ (currentDefaultAddressData.pagination.currentPage - 1) *
            currentDefaultAddressData.pagination.limit + 1 }} -
            {{ Math.min(currentDefaultAddressData.pagination.currentPage * currentDefaultAddressData.pagination.limit,
            currentDefaultAddressData.pagination.totalItems) }}
            de {{ currentDefaultAddressData.pagination.totalItems }} pólizas
          </div>
          <div class="flex space-x-2">
            <button [disabled]="currentDefaultAddressData.pagination.currentPage <= 1"
              (click)="loadDefaultAddressPage(currentDefaultAddressData.pagination.currentPage - 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="px-3 py-2 bg-blue-600 text-white rounded-md">
              {{ currentDefaultAddressData.pagination.currentPage }}
            </span>
            <button
              [disabled]="currentDefaultAddressData.pagination.currentPage >= currentDefaultAddressData.pagination.totalPages"
              (click)="loadDefaultAddressPage(currentDefaultAddressData.pagination.currentPage + 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <!-- No Default Address Template -->
        <ng-template #noDefaultAddressTemplate>
          <div class="text-center py-12 text-gray-400">
            <i class="fas fa-check-circle text-4xl mb-4 text-green-400"></i>
            <p class="text-lg">No hay pólizas con dirección por defecto</p>
            <p class="text-sm">Todas las pólizas se emitieron con direcciones completas</p>
          </div>
        </ng-template>
      </div>
    </div>

  </div>

  <!-- Loading Overlay -->
  <div *ngIf="currentDashboardData?.loading || currentAnalyticsData?.loading"
    class="loading-overlay fixed inset-0 bg-white bg-opacity-70 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg shadow-lg p-6 flex items-center space-x-4">
      <i class="fas fa-spinner fa-spin text-blue-600 text-xl"></i>
      <span class="text-gray-900 font-medium">Cargando datos...</span>
    </div>
  </div>

  <!-- Configuration Tab -->
  <div *ngIf="activeTab === 'config'" class="space-y-6">
    
    <!-- Header Section -->
    <div class="bg-white rounded-lg shadow-md p-6">
      <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div class="mb-4 lg:mb-0">
          <h2 class="text-2xl font-bold text-gray-900 mb-2">
            <i class="fas fa-building text-blue-600 mr-2"></i>
            Configuración de Aseguradoras
          </h2>
          <p class="text-gray-600">
            Gestiona las aseguradoras disponibles en el sistema
          </p>
        </div>
        
        <button (click)="loadInsurersConfig()"
          class="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
          [disabled]="isLoadingInsurersConfig">
          <i class="fas fa-sync-alt" [class.fa-spin]="isLoadingInsurersConfig"></i>
          <span class="text-sm font-medium">Actualizar</span>
        </button>
      </div>
    </div>

    <!-- Filters Section -->
    <div class="bg-white rounded-lg shadow-md p-6">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
        
        <!-- Search -->
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fas fa-search mr-1"></i>
            Buscar aseguradora
          </label>
          <div class="flex gap-2">
            <input type="text" 
              [(ngModel)]="insurersConfigSearch"
              (keyup.enter)="onInsurersConfigSearch()"
              placeholder="Buscar por nombre, código o descripción..."
              class="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            <button (click)="onInsurersConfigSearch()"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <i class="fas fa-search"></i>
            </button>
            <button *ngIf="insurersConfigSearch" (click)="clearInsurersConfigSearch()"
              class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Status Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fas fa-filter mr-1"></i>
            Filtrar por estado
          </label>
          <select [(ngModel)]="insurersConfigStatusFilter" 
            (change)="onInsurersConfigStatusFilterChange(insurersConfigStatusFilter)"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            <option [ngValue]="null">Todas</option>
            <option [ngValue]="true">Activas</option>
            <option [ngValue]="false">Inactivas</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Insurers Table -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden">
      
      <!-- Loading State -->
      <div *ngIf="isLoadingInsurersConfig" class="p-12 text-center">
        <i class="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
        <p class="text-gray-600">Cargando aseguradoras...</p>
      </div>

      <!-- Table -->
      <div *ngIf="!isLoadingInsurersConfig && insurersConfigData?.data?.length > 0" class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Código
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nombre
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Descripción
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr *ngFor="let insurer of insurersConfigData.data" class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap">
                <span class="text-sm font-mono font-semibold text-gray-900">{{ insurer.code }}</span>
              </td>
              <td class="px-6 py-4">
                <div class="text-sm font-medium text-gray-900">{{ insurer.name }}</div>
              </td>
              <td class="px-6 py-4">
                <div class="text-sm text-gray-600" [title]="insurer.description">
                  {{ insurer.description || 'Sin descripción' }}
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center gap-2">
                  <button (click)="toggleInsurerStatus(insurer)"
                    [class]="insurer.status 
                      ? 'relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                      : 'relative inline-flex h-6 w-11 items-center rounded-full bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2'"
                    [title]="insurer.status ? 'Desactivar aseguradora' : 'Activar aseguradora'">
                    <span [class]="insurer.status 
                      ? 'inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6'
                      : 'inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-1'">
                    </span>
                  </button>
                  <span class="text-sm text-gray-700">{{ insurer.status ? 'Activa' : 'Inactiva' }}</span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm">
                <button (click)="openEditInsurerModal(insurer)"
                  class="text-blue-600 hover:text-blue-900 mr-3"
                  title="Editar aseguradora">
                  <i class="fas fa-edit"></i>
                  Editar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Empty State -->
      <div *ngIf="!isLoadingInsurersConfig && (!insurersConfigData?.data || insurersConfigData.data.length === 0)" 
        class="p-12 text-center">
        <i class="fas fa-building text-4xl text-gray-400 mb-4"></i>
        <p class="text-gray-600 mb-2">No se encontraron aseguradoras</p>
        <p class="text-sm text-gray-500">Intenta ajustar los filtros de búsqueda</p>
      </div>

      <!-- Pagination -->
      <div *ngIf="!isLoadingInsurersConfig && insurersConfigData?.pagination" class="mt-6 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          Mostrando {{ (insurersConfigData.pagination.currentPage - 1) * insurersConfigData.pagination.limit + 1 }}
          -
          {{ Math.min(insurersConfigData.pagination.currentPage * insurersConfigData.pagination.limit,
          insurersConfigData.pagination.totalItems) }}
          de {{ insurersConfigData.pagination.totalItems }} resultados
        </div>
        <div class="flex space-x-2">
          <button [disabled]="insurersConfigData.pagination.currentPage <= 1"
            (click)="onInsurersConfigPageChange(insurersConfigData.pagination.currentPage - 1)"
            class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
            <i class="fas fa-chevron-left"></i>
          </button>
          <span class="px-3 py-2 bg-blue-600 text-white rounded-md">
            {{ insurersConfigData.pagination.currentPage }}
          </span>
          <button [disabled]="insurersConfigData.pagination.currentPage >= insurersConfigData.pagination.totalPages"
            (click)="onInsurersConfigPageChange(insurersConfigData.pagination.currentPage + 1)"
            class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
            <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Edit Insurer Modal -->
  <div *ngIf="showInsurerEditModal"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
    <div class="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
      
      <!-- Modal Header -->
      <div class="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
        <div class="flex items-center justify-between">
          <h3 class="text-xl font-bold text-gray-900">
            <i class="fas fa-edit text-blue-600 mr-2"></i>
            Editar Aseguradora: {{ selectedInsurerForEdit?.name }}
          </h3>
          <button (click)="closeEditInsurerModal()"
            class="text-gray-400 hover:text-gray-600 transition-colors">
            <i class="fas fa-times text-2xl"></i>
          </button>
        </div>
      </div>

      <!-- Modal Body -->
      <div class="p-6 space-y-6">
        
        <!-- Basic Information -->
        <div class="space-y-4">
          <h4 class="text-lg font-semibold text-gray-900 border-b pb-2">
            Información Básica
          </h4>
          
          <!-- Code (Read-only) -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Código <span class="text-gray-500">(No editable)</span>
            </label>
            <input type="text" 
              [value]="selectedInsurerForEdit?.code"
              disabled
              class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed">
          </div>

          <!-- Name -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Nombre <span class="text-red-500">*</span>
            </label>
            <input type="text" 
              [(ngModel)]="editInsurerForm.name"
              placeholder="Nombre de la aseguradora"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          </div>

          <!-- Description -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Descripción
            </label>
            <textarea 
              [(ngModel)]="editInsurerForm.description"
              rows="3"
              placeholder="Descripción de la aseguradora"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"></textarea>
          </div>

          <!-- Logo -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Logo (URL o nombre de archivo)
            </label>
            <input type="text" 
              [(ngModel)]="editInsurerForm.logo"
              placeholder="seguros_logo.png"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          </div>
        </div>

        <!-- Parameters Section -->
        <div class="space-y-4">
          <div class="flex items-center justify-between border-b pb-2">
            <h4 class="text-lg font-semibold text-gray-900">
              Parámetros de Configuración
            </h4>
            <div class="flex gap-2">
              <button (click)="toggleParamsJsonView()"
                class="px-3 py-1 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors">
                <i [class]="showParamsJson ? 'fas fa-list mr-1' : 'fas fa-code mr-1'"></i>
                {{ showParamsJson ? 'Vista Formulario' : 'Vista JSON' }}
              </button>
              <button *ngIf="!showParamsJson" (click)="addNewParam()"
                class="px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
                <i class="fas fa-plus mr-1"></i>
                Agregar Parámetro
              </button>
            </div>
          </div>

          <!-- Form View -->
          <div *ngIf="!showParamsJson" class="space-y-3">
            <div *ngFor="let key of getParamKeys(editInsurerForm.params)" 
              class="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <div class="flex-1">
                <label class="block text-sm font-medium text-gray-700 mb-1">{{ getParamLabel(key) }}</label>
                <input 
                  [type]="getParamType(editInsurerForm.params[key])"
                  [(ngModel)]="editInsurerForm.params[key]"
                  (ngModelChange)="updateParamValue(key, $event)"
                  class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              </div>
              <button (click)="removeParam(key)"
                class="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                title="Eliminar parámetro">
                <i class="fas fa-trash"></i>
              </button>
            </div>
            
            <div *ngIf="getParamKeys(editInsurerForm.params).length === 0" 
              class="text-center py-8 text-gray-500">
              <i class="fas fa-info-circle text-2xl mb-2"></i>
              <p>No hay parámetros configurados</p>
              <p class="text-sm">Haz clic en "Agregar Parámetro" para añadir uno nuevo</p>
            </div>
          </div>

          <!-- JSON View -->
          <div *ngIf="showParamsJson">
            <textarea 
              [(ngModel)]="paramsJsonString"
              rows="15"
              class="w-full px-4 py-2 font-mono text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder='{"key": "value"}'></textarea>
            <p class="text-xs text-gray-500 mt-2">
              <i class="fas fa-info-circle mr-1"></i>
              Edita el JSON directamente. Asegúrate de que el formato sea válido antes de guardar.
            </p>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
        <button (click)="closeEditInsurerModal()"
          class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          [disabled]="isUpdatingInsurer">
          Cancelar
        </button>
        <button (click)="saveInsurerChanges()"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          [disabled]="isUpdatingInsurer">
          <i class="fas fa-save mr-2" [class.fa-spin]="isUpdatingInsurer"></i>
          {{ isUpdatingInsurer ? 'Guardando...' : 'Guardar Cambios' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Products Configuration Tab -->
  <div *ngIf="activeTab === 'products'" class="space-y-6">
    
    <!-- Header Section -->
    <div class="bg-white rounded-lg shadow-md p-6">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h2 class="text-2xl font-bold text-gray-900 flex items-center">
            <i class="fas fa-box text-blue-600 mr-3"></i>
            Gestión de Productos
          </h2>
          <p class="text-gray-600 mt-1">Administra los productos de seguros disponibles en el sistema</p>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
        <!-- Search -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fas fa-search mr-1"></i>
            Buscar
          </label>
          <input type="text" [(ngModel)]="productsConfigSearch" (ngModelChange)="onProductsConfigSearchChange()"
            placeholder="Buscar por nombre, código o descripción..."
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
        </div>

        <!-- Status Filter -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fas fa-filter mr-1"></i>
            Estado
          </label>
          <select [(ngModel)]="productsConfigStatusFilter" (ngModelChange)="onProductsConfigStatusFilterChange($event)"
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            <option [ngValue]="null">Todos los estados</option>
            <option [ngValue]="true">Activos</option>
            <option [ngValue]="false">Inactivos</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Products Table -->
    <div class="bg-white rounded-lg shadow-md overflow-hidden">
      <!-- Loading State -->
      <div *ngIf="isLoadingProductsConfig" class="flex items-center justify-center py-12">
        <div class="text-center">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p class="text-gray-600">Cargando productos...</p>
        </div>
      </div>

      <!-- Products Table -->
      <div *ngIf="!isLoadingProductsConfig && productsConfigData">
        <!-- Table Header with Stats -->
        <div class="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <div class="text-sm text-gray-600">
              Mostrando <span class="font-semibold text-gray-900">{{ productsConfigData.data?.length || 0 }}</span>
              de <span class="font-semibold text-gray-900">{{ productsConfigData.pagination?.totalItems || 0 }}</span>
              productos
            </div>
            <div class="text-sm text-gray-600">
              Página <span class="font-semibold text-gray-900">{{ productsConfigData.pagination?.currentPage || 1 }}</span>
              de <span class="font-semibold text-gray-900">{{ productsConfigData.pagination?.totalPages || 1 }}</span>
            </div>
          </div>
        </div>

        <!-- Table Content -->
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Código
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nombre
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descripción
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr *ngFor="let product of productsConfigData.data" class="hover:bg-gray-50 transition-colors">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ product.code }}</div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm font-medium text-gray-900">{{ product.name }}</div>
                </td>
                <td class="px-6 py-4">
                  <div class="text-sm text-gray-600">{{ product.description || 'Sin descripción' }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center gap-2">
                    <button (click)="toggleProductStatus(product)"
                      [class]="product.status 
                        ? 'relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
                        : 'relative inline-flex h-6 w-11 items-center rounded-full bg-gray-300 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2'"
                      [title]="product.status ? 'Desactivar producto' : 'Activar producto'">
                      <span [class]="product.status 
                        ? 'inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6'
                        : 'inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-1'">
                      </span>
                    </button>
                    <span class="text-sm text-gray-700">{{ product.status ? 'Activo' : 'Inactivo' }}</span>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <button (click)="openEditProductModal(product)"
                    class="text-blue-600 hover:text-blue-900 mr-3"
                    title="Editar producto">
                    <i class="fas fa-edit"></i>
                    Editar
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div *ngIf="productsConfigData?.pagination" class="mt-6 flex items-center justify-between">
          <div class="text-sm text-gray-700">
            Mostrando {{ (productsConfigData.pagination.currentPage - 1) * productsConfigData.pagination.limit + 1 }}
            -
            {{ Math.min(productsConfigData.pagination.currentPage * productsConfigData.pagination.limit,
            productsConfigData.pagination.totalItems) }}
            de {{ productsConfigData.pagination.totalItems }} resultados
          </div>
          <div class="flex space-x-2">
            <button [disabled]="productsConfigData.pagination.currentPage <= 1"
              (click)="onProductsConfigPageChange(productsConfigData.pagination.currentPage - 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="px-3 py-2 bg-blue-600 text-white rounded-md">
              {{ productsConfigData.pagination.currentPage }}
            </span>
            <button [disabled]="productsConfigData.pagination.currentPage >= productsConfigData.pagination.totalPages"
              (click)="onProductsConfigPageChange(productsConfigData.pagination.currentPage + 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>

        <!-- Empty State -->
        <div *ngIf="productsConfigData.data?.length === 0" class="text-center py-12">
          <i class="fas fa-box text-gray-300 text-6xl mb-4"></i>
          <p class="text-gray-600 text-lg">No se encontraron productos</p>
          <p class="text-gray-500 text-sm mt-2">Intenta ajustar los filtros de búsqueda</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Product Edit Modal -->
  <div *ngIf="showProductEditModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    (click)="closeEditProductModal()">
    <div class="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
      (click)="$event.stopPropagation()">
      
      <!-- Modal Header -->
      <div class="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
        <h3 class="text-xl font-bold text-gray-900 flex items-center">
          <i class="fas fa-edit text-blue-600 mr-2"></i>
          Editar Producto: {{ selectedProductForEdit?.name }}
        </h3>
        <button (click)="closeEditProductModal()"
          class="text-gray-400 hover:text-gray-600 transition-colors">
          <i class="fas fa-times text-2xl"></i>
        </button>
      </div>

      <!-- Modal Content -->
      <div class="p-6 space-y-6">
        
        <!-- Basic Information -->
        <div class="bg-blue-50 rounded-lg p-4">
          <h4 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <i class="fas fa-info-circle text-blue-600 mr-2"></i>
            Información Básica
          </h4>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Código (No editable)</label>
              <input type="text" [value]="selectedProductForEdit?.code" disabled
                class="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed">
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Nombre</label>
              <input type="text" [(ngModel)]="editProductForm.name"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            </div>
            <div class="md:col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
              <textarea [(ngModel)]="editProductForm.description" rows="3"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"></textarea>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Icono</label>
              <input type="text" [(ngModel)]="editProductForm.icon"
                placeholder="Ej: accidentes_personales.ico"
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            </div>
          </div>
        </div>

        <!-- Mapping Configuration -->
        <div class="bg-purple-50 rounded-lg p-4">
          <div class="flex items-center justify-between mb-4">
            <h4 class="text-lg font-semibold text-gray-900 flex items-center">
              <i class="fas fa-map text-purple-600 mr-2"></i>
              Configuración de Mapping
            </h4>
            <button (click)="toggleMappingMode()"
              class="px-3 py-1.5 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm">
              <i [class]="mappingVisualMode ? 'fas fa-code mr-1' : 'fas fa-table mr-1'"></i>
              {{ mappingVisualMode ? 'Modo JSON' : 'Modo Visual' }}
            </button>
          </div>

          <!-- Visual Mode -->
          <div *ngIf="mappingVisualMode" class="space-y-4">
            <div class="flex items-center justify-between">
              <p class="text-sm text-gray-600">
                Configura las aseguradoras que ofrecen este producto
              </p>
              <button (click)="addInsurerToMapping()"
                class="px-3 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm">
                <i class="fas fa-plus mr-1"></i>
                Agregar Aseguradora
              </button>
            </div>

            <!-- Empty State -->
            <div *ngIf="editProductForm.mapping.length === 0" class="text-center py-8 bg-white rounded-lg border-2 border-dashed border-purple-300">
              <i class="fas fa-building text-purple-300 text-4xl mb-3"></i>
              <p class="text-gray-600 mb-2">No hay aseguradoras configuradas</p>
              <p class="text-sm text-gray-500">Haz clic en "Agregar Aseguradora" para comenzar</p>
            </div>

            <!-- Insurers List -->
            <div *ngFor="let mapping of editProductForm.mapping; let i = index" 
              class="bg-white rounded-lg border border-purple-200 p-4 space-y-4">
              
              <!-- Header -->
              <div class="flex items-center justify-between border-b border-gray-200 pb-3">
                <h5 class="font-semibold text-gray-900 flex items-center">
                  <i class="fas fa-building text-purple-600 mr-2"></i>
                  Aseguradora {{ i + 1 }}
                </h5>
                <button (click)="removeInsurerFromMapping(i)"
                  class="text-red-600 hover:text-red-800 text-sm">
                  <i class="fas fa-trash mr-1"></i>
                  Eliminar
                </button>
              </div>

              <!-- Basic Fields -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Aseguradora <span class="text-red-500">*</span>
                  </label>
                  
                  <!-- Select for new mappings (when clave is empty) -->
                  <select *ngIf="!mapping.clave" 
                    [(ngModel)]="mapping.clave" (ngModelChange)="onMappingInsurerChange(i)"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                    <option value="">Seleccione una aseguradora</option>
                    <option *ngFor="let insurer of getAvailableInsurersForSelect()" [value]="insurer.code">
                      {{ insurer.code }} - {{ insurer.name }}
                    </option>
                  </select>
                  
                  <!-- Read-only text for all mappings with a selected insurer -->
                  <div *ngIf="mapping.clave"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700">
                    {{ mapping.clave }} - {{ getInsurerName(mapping.clave) }}
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    ID del Producto en Aseguradora <span class="text-red-500">*</span>
                  </label>
                  <input type="text" [(ngModel)]="mapping.value" (ngModelChange)="onMappingValueChange(i)"
                    placeholder="Ej: cm23o270j0922ujop2ik7ryq1"
                    class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
                </div>
              </div>

              <!-- Frequencies -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  <i class="fas fa-calendar-alt mr-1"></i>
                  Frecuencias de Pago Disponibles
                </label>
                <div class="grid grid-cols-2 gap-3">
                  <div *ngFor="let freq of mapping.frequencies; let fi = index"
                    class="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span class="text-sm font-medium text-gray-700">{{ freq.label }}</span>
                    <button (click)="onFrequencyToggle(i, fi)"
                      [class]="freq.active 
                        ? 'relative inline-flex h-6 w-11 items-center rounded-full bg-blue-600 transition-colors'
                        : 'relative inline-flex h-6 w-11 items-center rounded-full bg-gray-300 transition-colors'">
                      <span [class]="freq.active 
                        ? 'inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-6'
                        : 'inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-1'">
                      </span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Params -->
              <div>
                <div class="flex items-center justify-between mb-2">
                  <label class="block text-sm font-medium text-gray-700">
                    <i class="fas fa-cog mr-1"></i>
                    Parámetros Adicionales
                  </label>
                  <button (click)="addParamToMapping(i)"
                    class="text-purple-600 hover:text-purple-800 text-sm">
                    <i class="fas fa-plus-circle mr-1"></i>
                    Agregar Parámetro
                  </button>
                </div>
                
                <div *ngIf="getParamKeys(mapping.params).length === 0" 
                  class="text-sm text-gray-500 italic p-3 bg-gray-50 rounded border border-gray-200">
                  No hay parámetros configurados
                </div>

                <div *ngIf="getParamKeys(mapping.params).length > 0" class="space-y-2">
                  <div *ngFor="let paramKey of getParamKeys(mapping.params)" 
                    class="flex items-center gap-2 p-2 bg-gray-50 rounded border border-gray-200">
                    <div class="flex-1 grid grid-cols-2 gap-2">
                      <input type="text" [value]="paramKey" disabled
                        class="px-2 py-1 text-sm bg-white border border-gray-300 rounded">
                      <input type="text" [(ngModel)]="mapping.params[paramKey]" (ngModelChange)="updateMappingJson()"
                        class="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-purple-500">
                    </div>
                    <button (click)="removeParamFromMapping(i, paramKey)"
                      class="text-red-600 hover:text-red-800">
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>

          <!-- JSON Mode -->
          <div *ngIf="!mappingVisualMode" class="space-y-3">
            <div class="flex items-center justify-between">
              <label class="block text-sm font-medium text-gray-700">JSON del Mapping</label>
              <div class="flex gap-2">
                <button (click)="copyMappingJson()"
                  class="px-3 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600 transition-colors">
                  <i class="fas fa-copy mr-1"></i>
                  Copiar
                </button>
              </div>
            </div>
            <textarea [(ngModel)]="mappingJsonString" rows="12"
              class="w-full px-4 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder='[{"clave": "SM", "value": "...", "frequencies": [...]}]'></textarea>
            <p class="text-xs text-gray-600">
              <i class="fas fa-info-circle mr-1"></i>
              Edita el JSON directamente. Al cambiar a modo visual, se validará la sintaxis.
            </p>
          </div>
        </div>

      </div>

      <!-- Modal Footer -->
      <div class="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
        <button (click)="closeEditProductModal()"
          class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          [disabled]="isUpdatingProduct">
          Cancelar
        </button>
        <button (click)="saveProductChanges()"
          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          [disabled]="isUpdatingProduct">
          <i class="fas fa-save mr-2" [class.fa-spin]="isUpdatingProduct"></i>
          {{ isUpdatingProduct ? 'Guardando...' : 'Guardar Cambios' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Policy Detail Modal -->
  <div *ngIf="showPolicyModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    (click)="closePolicyModal()">
    <div class="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto"
      (click)="$event.stopPropagation()">
      <!-- Modal Header -->
      <div class="flex items-center justify-between p-6 border-b border-gray-200">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
            <i class="fas fa-file-alt text-blue-600"></i>
          </div>
          <div>
            <h2 class="text-xl font-semibold text-gray-900">Detalle de Póliza</h2>
            <p class="text-sm text-gray-500">Información completa de la póliza</p>
          </div>
        </div>
        <button (click)="closePolicyModal()"
          class="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors">
          <i class="fas fa-times text-gray-600"></i>
        </button>
      </div>

      <!-- Sub-header con controles masivos de expand/collapse -->
      <div *ngIf="!isLoadingPolicyDetail && selectedPolicyDetail"
        class="flex items-center justify-between px-6 py-2 bg-gray-50 border-b border-gray-200 text-xs">
        <div class="flex items-center gap-2">
          <button (click)="expandAllPolicySections()" type="button"
            class="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-200 transition-colors">
            <i class="fas fa-chevron-down text-blue-500"></i>
            Expandir todo
          </button>
          <button (click)="collapseAllPolicySections()" type="button"
            class="flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-200 transition-colors">
            <i class="fas fa-chevron-up text-blue-500"></i>
            Colapsar todo
          </button>
        </div>
        <span class="text-gray-500">
          {{ getExpandedPolicySectionsCount() }} de {{ getAvailablePolicySectionsCount() }} secciones abiertas
        </span>
      </div>

      <!-- Modal Content -->
      <div class="p-6">
        <!-- Loading State -->
        <div *ngIf="isLoadingPolicyDetail" class="flex items-center justify-center py-12">
          <div class="text-center">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p class="text-gray-600">Cargando detalle de póliza...</p>
          </div>
        </div>

        <!-- Policy Details -->
        <div *ngIf="!isLoadingPolicyDetail && selectedPolicyDetail" class="space-y-6">

          <!-- Policy Basic Information -->
          <div class="bg-blue-50 rounded-lg p-4">
            <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <i class="fas fa-file-contract text-blue-600 mr-2"></i>
              Información de la Póliza
            </h3>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">ID de Póliza</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border break-all">{{ selectedPolicyDetail._id ||
                  'N/A' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Código de Póliza</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border break-all">{{
                  selectedPolicyDetail.policy_code || selectedPolicyDetail.params?.code || 'N/A' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                <div class="flex flex-col items-start">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" [ngClass]="{
                          'bg-gray-100 text-gray-800': (selectedPolicyDetail.policy_status || selectedPolicyDetail.params?.policy_status) === 'CREATED' || !(selectedPolicyDetail.policy_status || selectedPolicyDetail.params?.policy_status),
                          'bg-blue-100 text-blue-800': (selectedPolicyDetail.policy_status || selectedPolicyDetail.params?.policy_status) === 'PLAN_SELECTED',
                          'bg-cyan-100 text-cyan-800': (selectedPolicyDetail.policy_status || selectedPolicyDetail.params?.policy_status) === 'DATA_CLIENT_CONFIRMED',
                          'bg-purple-100 text-purple-800': (selectedPolicyDetail.policy_status || selectedPolicyDetail.params?.policy_status) === 'LICENSE_UPLOADED',
                          'bg-indigo-100 text-indigo-800': (selectedPolicyDetail.policy_status || selectedPolicyDetail.params?.policy_status) === 'VERIFIED',
                          'bg-orange-100 text-orange-800': (selectedPolicyDetail.policy_status || selectedPolicyDetail.params?.policy_status) === 'ISSUED',
                          'bg-yellow-100 text-yellow-800': (selectedPolicyDetail.policy_status || selectedPolicyDetail.params?.policy_status) === 'PAYMENT_RECORDED',
                          'bg-green-100 text-green-800': (selectedPolicyDetail.policy_status || selectedPolicyDetail.params?.policy_status) === 'COMPLETED'
                        }">
                    {{ translatePolicyStatus(selectedPolicyDetail.policy_status || selectedPolicyDetail.params?.policy_status || 'Sin Estado') }}
                  </span>
                  <span class="text-xs text-gray-500 mt-1">{{ selectedPolicyDetail.policy_status || selectedPolicyDetail.params?.policy_status || 'N/A' }}</span>
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Número de Póliza</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{ selectedPolicyDetail.params?.number ||
                  selectedPolicyDetail.params?.integration_data?.number || selectedPolicyDetail.number || 'N/A' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Número de Recibo</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params?.receipt_number ||
                  selectedPolicyDetail.params?.integration_data?.receipt_number || selectedPolicyDetail.receipt_number
                  || 'N/A' }}</p>
              </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Fecha de Creación</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{ formatDateTime(selectedPolicyDetail.date
                  || selectedPolicyDetail.created_at) }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Fecha de Emisión</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{ selectedPolicyDetail.params?.issue_date
                  ? formatDateTime(selectedPolicyDetail.params.issue_date) :
                  (selectedPolicyDetail.params?.integration_data?.issue_date ?
                  formatDateTime(selectedPolicyDetail.params.integration_data.issue_date) :
                  (selectedPolicyDetail.issue_date ? formatDateTime(selectedPolicyDetail.issue_date) : 'N/A')) }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Fecha de Actualización</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{ selectedPolicyDetail.params?.update_date
                  ? formatDateTime(selectedPolicyDetail.params.update_date) : 'N/A' }}</p>
              </div>
            </div>
          </div>

          <!-- Insured Information (colapsable) -->
          <div *ngIf="selectedPolicyDetail.params?.insured || selectedPolicyDetail.insured"
            class="bg-green-50 rounded-lg overflow-hidden">
            <button (click)="togglePolicySection('insured')" type="button"
              class="w-full flex items-center justify-between p-4 hover:bg-green-100 transition-colors text-left">
              <h3 class="text-lg font-semibold text-gray-900 flex items-center">
                <i class="fas fa-user text-green-600 mr-2"></i>
                Información del Asegurado
              </h3>
              <i class="fas modal-section-chevron"
                [class.fa-chevron-right]="!isPolicySectionExpanded('insured')"
                [class.fa-chevron-down]="isPolicySectionExpanded('insured')"
                aria-hidden="true"></i>
            </button>
            <div *ngIf="isPolicySectionExpanded('insured')" class="px-4 pb-4 border-t border-green-100">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              <!-- Información Personal -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Nombre Completo</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{ getInsuredFullName(selectedPolicyDetail)
                  }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Cédula/Identidad</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getInsuredFieldValue(selectedPolicyDetail, 'identity') }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Fecha de Nacimiento</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getInsuredBirthDate(selectedPolicyDetail) }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Género</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getInsuredFieldValue(selectedPolicyDetail, 'gender') === 'M' ? 'Masculino' :
                  (getInsuredFieldValue(selectedPolicyDetail, 'gender') === 'F' ? 'Femenino' :
                  getInsuredFieldValue(selectedPolicyDetail, 'gender')) }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Estado Civil</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getInsuredFieldValue(selectedPolicyDetail, 'marital_status') === 'S' ? 'Soltero(a)' :
                  (getInsuredFieldValue(selectedPolicyDetail, 'marital_status') === 'M' ? 'Casado(a)' :
                  getInsuredFieldValue(selectedPolicyDetail, 'marital_status')) }}</p>
              </div>

              <!-- Información de Contacto -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getInsuredFieldValue(selectedPolicyDetail, 'email') }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Teléfono</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getInsuredFieldValue(selectedPolicyDetail, 'phone') }}</p>
              </div>

              <!-- Información Profesional -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Profesión</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getInsuredFieldValue(selectedPolicyDetail, 'profesion') || getInsuredFieldValue(selectedPolicyDetail,
                  'profession') || 'N/A' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Ocupación</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getInsuredFieldValue(selectedPolicyDetail, 'occupation') }}</p>
              </div>

              <!-- Información de Dirección -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">País</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getInsuredFieldValue(selectedPolicyDetail, 'country') }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getInsuredFieldValue(selectedPolicyDetail, 'state') }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Ciudad</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getInsuredFieldValue(selectedPolicyDetail, 'city') }}</p>
              </div>

              <!-- Dirección - Campo expandido que ocupa las 3 columnas -->
              <div class="md:col-span-3">
                <label class="block text-sm font-medium text-gray-700 mb-1">Dirección</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getInsuredFieldValue(selectedPolicyDetail, 'address') }}</p>
              </div>
            </div>
            </div>
          </div>

          <!-- Payment History (colapsable) -->
          <div class="bg-blue-50 rounded-lg overflow-hidden">
            <button (click)="togglePolicySection('payments')" type="button"
              class="w-full flex items-center justify-between p-4 hover:bg-blue-100 transition-colors text-left">
              <h3 class="text-lg font-semibold text-gray-900 flex items-center">
                <i class="fas fa-money-bill-wave text-blue-600 mr-2"></i>
                Pagos realizados a la póliza
              </h3>
              <i class="fas modal-section-chevron"
                [class.fa-chevron-right]="!isPolicySectionExpanded('payments')"
                [class.fa-chevron-down]="isPolicySectionExpanded('payments')"
                aria-hidden="true"></i>
            </button>
            <div *ngIf="isPolicySectionExpanded('payments')" class="px-4 pb-4 border-t border-blue-100 mt-3">

            <!-- Cuando SÍ hay pagos -->
            <div *ngIf="getPayments(selectedPolicyDetail) && getPayments(selectedPolicyDetail).length > 0">
              <div class="text-sm text-gray-600 mb-3">
                Total de pagos registrados: <span class="font-semibold">{{ getPayments(selectedPolicyDetail).length
                  }}</span>
              </div>
              <div class="overflow-x-auto">
                <table class="min-w-full bg-white rounded-lg overflow-hidden">
                  <thead class="bg-blue-100">
                    <tr>
                      <th class="px-2 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider w-16">
                        Cuota</th>
                      <th class="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Recibo</th>
                      <th class="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">Monto
                      </th>
                      <th class="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">Tasa
                        de Cambio</th>
                      <th class="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">Fecha
                        de Pago</th>
                      <th class="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Referencia</th>
                      <th class="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">API
                      </th>
                      <th class="px-3 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                        Estado</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    <ng-container *ngFor="let payment of getPayments(selectedPolicyDetail); let i = index">
                      <!-- Fila principal del pago -->
                      <tr [class.bg-gray-50]="i % 2 === 1"
                          [class.bg-red-50]="payment.error"
                          [class.hover:bg-blue-50]="!payment.error"
                          [class.hover:bg-red-100]="payment.error"
                          [class.cursor-pointer]="payment.error"
                          (click)="payment.error && togglePaymentError(i)"
                          class="transition-colors">
                        <td class="px-2 py-3 text-sm text-gray-900 font-medium text-center">
                          {{ payment.quote_number || 'N/A' }}
                        </td>
                        <td class="px-3 py-3 text-sm text-gray-600">
                          {{ payment.receipt_number || 'N/A' }}
                        </td>
                        <td class="px-3 py-3 text-sm text-gray-900 font-semibold text-right">
                          {{ formatBsCurrency(payment.total_amount) }}
                        </td>
                        <td class="px-3 py-3 text-sm text-gray-900 text-right">
                          {{ payment.change_rate || 'N/A' }}
                        </td>
                        <td class="px-3 py-2 text-sm text-gray-900">
                          <div class="flex flex-col">
                            <span class="font-medium">{{ formatPaymentDateShort(payment.payment_date) }}</span>
                            <span class="text-xs text-gray-500">{{ formatPaymentTimeShort(payment.payment_date) }}</span>
                          </div>
                        </td>
                        <td class="px-3 py-3 text-sm text-gray-600">
                          {{ payment.reference || 'N/A' }}
                        </td>
                        <td class="px-3 py-3 text-center">
                          <span
                            class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {{ payment.api || 'N/A' }}
                          </span>
                        </td>
                        <td class="px-3 py-3 text-center">
                          <span *ngIf="!payment.error"
                            class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <i class="fas fa-check-circle mr-1"></i>
                            Exitoso
                          </span>
                          <button *ngIf="payment.error"
                            class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200 transition-colors">
                            <i class="fas fa-exclamation-circle mr-1"></i>
                            Error
                            <i class="fas ml-1" [class.fa-chevron-down]="!expandedPaymentErrors[i]" [class.fa-chevron-up]="expandedPaymentErrors[i]"></i>
                          </button>
                        </td>
                      </tr>

                      <!-- Fila expandible con detalles del error -->
                      <tr *ngIf="payment.error && expandedPaymentErrors[i]" class="bg-red-50">
                        <td colspan="8" class="px-4 py-4">
                          <div class="bg-white rounded-lg p-4 border-l-4 border-red-500">
                            <h4 class="text-sm font-semibold text-red-900 mb-3 flex items-center">
                              <i class="fas fa-exclamation-triangle mr-2"></i>
                              Detalles del Error de Notificación de Pago
                            </h4>
                            <div class="grid grid-cols-2 gap-3 text-sm">
                              <div *ngIf="payment.error.code || payment.error.cdError">
                                <span class="font-medium text-gray-700">Código:</span>
                                <span class="ml-2 text-gray-900">{{ payment.error.code || payment.error.cdError || 'N/A' }}</span>
                              </div>
                              <div *ngIf="payment.error.type">
                                <span class="font-medium text-gray-700">Tipo:</span>
                                <span class="ml-2 text-gray-900">{{ payment.error.type }}</span>
                              </div>
                              <div *ngIf="payment.error.status || payment.error.details?.status">
                                <span class="font-medium text-gray-700">Status HTTP:</span>
                                <span class="ml-2 text-gray-900">{{ payment.error.status || payment.error.details?.status }}</span>
                              </div>
                              <div class="col-span-2">
                                <span class="font-medium text-gray-700">Mensaje:</span>
                                <p class="ml-2 text-gray-900 mt-1">{{ payment.error.message || payment.error.msg || payment.error.deError || payment.error.title || 'Error desconocido' }}</p>
                              </div>
                              <div class="col-span-2" *ngIf="payment.error.cause || payment.error.details?.cause">
                                <span class="font-medium text-gray-700">Causa:</span>
                                <p class="ml-2 text-gray-900 mt-1">{{ payment.error.cause || payment.error.details?.cause }}</p>
                              </div>
                              <div class="col-span-2" *ngIf="payment.error_date">
                                <span class="font-medium text-gray-700">Fecha del Error:</span>
                                <span class="ml-2 text-gray-900">{{ formatPaymentDateShort(payment.error_date) }} {{ formatPaymentTimeShort(payment.error_date) }}</span>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </ng-container>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Cuando NO hay pagos -->
            <div *ngIf="!getPayments(selectedPolicyDetail) || getPayments(selectedPolicyDetail).length === 0"
              class="text-center py-8">
              <i class="fas fa-receipt text-gray-300 text-4xl mb-3"></i>
              <p class="text-gray-500 text-sm">No hay pagos registrados para esta póliza</p>
              <p class="text-gray-400 text-xs mt-1">Los pagos aparecerán aquí cuando se registren</p>
            </div>
            </div>
          </div>

          <!-- Insurance Information (colapsable) -->
          <div *ngIf="selectedPolicyDetail.params?.insurance || selectedPolicyDetail.insurance"
            class="bg-indigo-50 rounded-lg overflow-hidden">
            <button (click)="togglePolicySection('insurance')" type="button"
              class="w-full flex items-center justify-between p-4 hover:bg-indigo-100 transition-colors text-left">
              <h3 class="text-lg font-semibold text-gray-900 flex items-center">
                <i class="fas fa-shield-alt text-indigo-600 mr-2"></i>
                Información de la Aseguradora
              </h3>
              <i class="fas modal-section-chevron"
                [class.fa-chevron-right]="!isPolicySectionExpanded('insurance')"
                [class.fa-chevron-down]="isPolicySectionExpanded('insurance')"
                aria-hidden="true"></i>
            </button>
            <div *ngIf="isPolicySectionExpanded('insurance')" class="px-4 pb-4 border-t border-indigo-100">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Aseguradora</label>
                  <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                    selectedPolicyDetail.params?.insurance?.name || selectedPolicyDetail.insurance?.name || 'N/A' }}</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Código</label>
                  <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                    selectedPolicyDetail.params?.insurance?.code || selectedPolicyDetail.insurance?.code || 'N/A' }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Plan Information (colapsable) -->
          <div *ngIf="selectedPolicyDetail.params?.plan || selectedPolicyDetail.plan"
            class="bg-yellow-50 rounded-lg overflow-hidden">
            <button (click)="togglePolicySection('product')" type="button"
              class="w-full flex items-center justify-between p-4 hover:bg-yellow-100 transition-colors text-left">
              <h3 class="text-lg font-semibold text-gray-900 flex items-center">
                <i class="fas fa-file-invoice-dollar text-yellow-600 mr-2"></i>
                Información del Producto/Plan
              </h3>
              <i class="fas modal-section-chevron"
                [class.fa-chevron-right]="!isPolicySectionExpanded('product')"
                [class.fa-chevron-down]="isPolicySectionExpanded('product')"
                aria-hidden="true"></i>
            </button>
            <div *ngIf="isPolicySectionExpanded('product')" class="px-4 pb-4 border-t border-yellow-100">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              <!-- Campos del Producto (movidos desde Información de la Aseguradora) -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Producto</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params?.insurance_product?.name || selectedPolicyDetail.insurance_product?.name
                  || 'N/A' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">ID del Producto (Aseguradora)</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params?.insurance_product?.insurance_code ||
                  selectedPolicyDetail.insurance_product?.insurance_code || 'N/A' }}</p>
              </div>

              <!-- Campos del Plan -->
              <div *ngIf="selectedPolicyDetail.params?.plan?.name || selectedPolicyDetail.plan?.name">
                <label class="block text-sm font-medium text-gray-700 mb-1">Nombre del Plan</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{ selectedPolicyDetail.params?.plan?.name
                  || selectedPolicyDetail.plan?.name || 'N/A' }}</p>
              </div>
              <div *ngIf="selectedPolicyDetail.params?.plan?.code || selectedPolicyDetail.plan?.code">
                <label class="block text-sm font-medium text-gray-700 mb-1">Código del Plan</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{ selectedPolicyDetail.params?.plan?.code
                  || selectedPolicyDetail.plan?.code || 'N/A' }}</p>
              </div>
              <div *ngIf="selectedPolicyDetail.params?.plan?.premium || selectedPolicyDetail.plan?.premium">
                <label class="block text-sm font-medium text-gray-700 mb-1">Prima</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params?.plan?.premium || selectedPolicyDetail.plan?.premium || 'N/A' }}</p>
              </div>
              <div *ngIf="selectedPolicyDetail.params?.plan?.sum_insured || selectedPolicyDetail.plan?.sum_insured">
                <label class="block text-sm font-medium text-gray-700 mb-1">Suma Asegurada</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params?.plan?.sum_insured || selectedPolicyDetail.plan?.sum_insured || 'N/A' }}
                </p>
              </div>
              <div *ngIf="selectedPolicyDetail.params?.plan?.deductible || selectedPolicyDetail.plan?.deductible">
                <label class="block text-sm font-medium text-gray-700 mb-1">Deducible</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params?.plan?.deductible || selectedPolicyDetail.plan?.deductible || 'N/A' }}</p>
              </div>
              <div *ngIf="selectedPolicyDetail.params?.plan?.validity || selectedPolicyDetail.plan?.validity">
                <label class="block text-sm font-medium text-gray-700 mb-1">Vigencia</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params?.plan?.validity || selectedPolicyDetail.plan?.validity || 'N/A' }}</p>
              </div>

              <!-- Campos adicionales solicitados -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Frecuencia</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{ selectedPolicyDetail.params?.frequency
                  || selectedPolicyDetail.frequency || 'N/A' }}</p>
              </div>
              <div *ngIf="selectedPolicyDetail.params?.plan?.initial || selectedPolicyDetail.plan?.initial">
                <label class="block text-sm font-medium text-gray-700 mb-1">¿Tiene Inicial?</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params?.plan?.initial || selectedPolicyDetail.plan?.initial || 'N/A' }}</p>
              </div>
              <div
                *ngIf="selectedPolicyDetail.params?.plan?.initial_amount || selectedPolicyDetail.plan?.initial_amount">
                <label class="block text-sm font-medium text-gray-700 mb-1">Monto de Inicial</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params?.plan?.initial_amount || selectedPolicyDetail.plan?.initial_amount ||
                  'N/A' }}</p>
              </div>
              <div *ngIf="selectedPolicyDetail.params?.plan?.initial_type || selectedPolicyDetail.plan?.initial_type">
                <label class="block text-sm font-medium text-gray-700 mb-1">Tipo de Inicial</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params?.plan?.initial_type || selectedPolicyDetail.plan?.initial_type || 'N/A' }}
                </p>
              </div>
              <div *ngIf="selectedPolicyDetail.params?.plan?.product_type || selectedPolicyDetail.plan?.product_type">
                <label class="block text-sm font-medium text-gray-700 mb-1">product_type</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params?.plan?.product_type || selectedPolicyDetail.plan?.product_type || 'N/A' }}
                </p>
              </div>
            </div>
            </div>
          </div>

          <!-- Proposal Information — Plan de la Aseguradora (colapsable) -->
          <div *ngIf="hasProposal(selectedPolicyDetail)" class="bg-orange-50 rounded-lg overflow-hidden">
            <button (click)="togglePolicySection('proposal')" type="button"
              class="w-full flex items-center justify-between p-4 hover:bg-orange-100 transition-colors text-left">
              <h3 class="text-lg font-semibold text-gray-900 flex items-center">
                <i class="fas fa-file-contract text-orange-600 mr-2"></i>
                Propuesta (Plan de la Aseguradora)
              </h3>
              <i class="fas modal-section-chevron"
                [class.fa-chevron-right]="!isPolicySectionExpanded('proposal')"
                [class.fa-chevron-down]="isPolicySectionExpanded('proposal')"
                aria-hidden="true"></i>
            </button>
            <div *ngIf="isPolicySectionExpanded('proposal')" class="px-4 pb-4 border-t border-orange-100">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              <!-- ID al inicio -->
              <div *ngIf="getProposalData(selectedPolicyDetail)?.id">
                <label class="block text-sm font-medium text-gray-700 mb-1">ID</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getProposalData(selectedPolicyDetail)?.id }}</p>
              </div>

              <!-- Suma Asegurada -->
              <div *ngIf="getProposalData(selectedPolicyDetail)?.assuredSum">
                <label class="block text-sm font-medium text-gray-700 mb-1">Suma Asegurada</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getProposalData(selectedPolicyDetail)?.assuredSum }}</p>
              </div>

              <!-- Monto Prima -->
              <div *ngIf="getProposalData(selectedPolicyDetail)?.mtPrima">
                <label class="block text-sm font-medium text-gray-700 mb-1">Monto Prima</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getProposalData(selectedPolicyDetail)?.mtPrima }}</p>
              </div>

              <!-- Monto de Pago -->
              <div *ngIf="getProposalData(selectedPolicyDetail)?.payment_amount">
                <label class="block text-sm font-medium text-gray-700 mb-1">Monto de Pago</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getProposalData(selectedPolicyDetail)?.payment_amount }}</p>
              </div>

              <!-- Tasa de Cambio seguida de Fecha Tasa -->
              <div *ngIf="getProposalData(selectedPolicyDetail)?.taCambio">
                <label class="block text-sm font-medium text-gray-700 mb-1">Tasa de Cambio</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getProposalData(selectedPolicyDetail)?.taCambio }}</p>
              </div>
              <div *ngIf="getProposalData(selectedPolicyDetail)?.fe_tasa">
                <label class="block text-sm font-medium text-gray-700 mb-1">Fecha de la Tasa</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  getProposalData(selectedPolicyDetail)?.fe_tasa }}</p>
              </div>
            </div>
            </div>
          </div>

          <!-- Invoice Information (colapsable) -->
          <div *ngIf="hasInvoice(selectedPolicyDetail)" class="bg-green-50 rounded-lg overflow-hidden">
            <button (click)="togglePolicySection('invoice')" type="button"
              class="w-full flex items-center justify-between p-4 hover:bg-green-100 transition-colors text-left">
              <h3 class="text-lg font-semibold text-gray-900 flex items-center">
                <i class="fas fa-file-invoice text-green-600 mr-2"></i>
                Información de Facturación
              </h3>
              <i class="fas modal-section-chevron"
                [class.fa-chevron-right]="!isPolicySectionExpanded('invoice')"
                [class.fa-chevron-down]="isPolicySectionExpanded('invoice')"
                aria-hidden="true"></i>
            </button>
            <div *ngIf="isPolicySectionExpanded('invoice')" class="px-4 pb-4 border-t border-green-100">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                <!-- Show invoice fields dynamically based on what exists -->
                <div *ngFor="let field of getInvoiceFields(selectedPolicyDetail)">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ field.label }}</label>
                  <!-- Special styling for status field -->
                  <div *ngIf="field.isStatus; else normalField">
                    <span [class]="getInvoiceStatusClass(field.value)">{{ field.value }}</span>
                  </div>
                  <ng-template #normalField>
                    <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{ field.value }}</p>
                  </ng-template>
                </div>
              </div>
            </div>
          </div>

          <!-- Vehicle Information — Only for RCV (colapsable) -->
          <div
            *ngIf="isRCVProduct(selectedPolicyDetail) && (selectedPolicyDetail.params?.vehicle || selectedPolicyDetail.vehicle)"
            class="bg-purple-50 rounded-lg overflow-hidden">
            <button (click)="togglePolicySection('vehicle')" type="button"
              class="w-full flex items-center justify-between p-4 hover:bg-purple-100 transition-colors text-left">
              <h3 class="text-lg font-semibold text-gray-900 flex items-center">
                <i class="fas fa-car text-purple-600 mr-2"></i>
                Información del Vehículo
              </h3>
              <i class="fas modal-section-chevron"
                [class.fa-chevron-right]="!isPolicySectionExpanded('vehicle')"
                [class.fa-chevron-down]="isPolicySectionExpanded('vehicle')"
                aria-hidden="true"></i>
            </button>
            <div *ngIf="isPolicySectionExpanded('vehicle')" class="px-4 pb-4 border-t border-purple-100 mt-3">

            <div class="space-y-4">
              <!-- Línea 1: Año y OCR Año -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Año</label>
                  <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                    getVehicleFieldValue(selectedPolicyDetail, 'year') || 'N/A' }}</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-purple-600 mb-1">OCR Año</label>
                  <p class="text-sm text-purple-800 bg-purple-100 p-2 rounded border border-purple-200">{{
                    getVehicleOcrValue(selectedPolicyDetail, 'year') || 'N/A' }}</p>
                </div>
              </div>

              <!-- Línea 2: Marca y OCR Marca -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Marca</label>
                  <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                    getVehicleFieldValue(selectedPolicyDetail, 'brand') || 'N/A' }}</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-purple-600 mb-1">OCR Marca</label>
                  <p class="text-sm text-purple-800 bg-purple-100 p-2 rounded border border-purple-200">{{
                    getVehicleOcrValue(selectedPolicyDetail, 'brand') || 'N/A' }}</p>
                </div>
              </div>

              <!-- Línea 3: Modelo y OCR Modelo -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Modelo</label>
                  <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                    getVehicleFieldValue(selectedPolicyDetail, 'model') || 'N/A' }}</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-purple-600 mb-1">OCR Modelo</label>
                  <p class="text-sm text-purple-800 bg-purple-100 p-2 rounded border border-purple-200">{{
                    getVehicleOcrValue(selectedPolicyDetail, 'model') || 'N/A' }}</p>
                </div>
              </div>

              <!-- Línea 4: Serial y OCR Serial -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Serial</label>
                  <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                    getVehicleFieldValue(selectedPolicyDetail, 'serial') || 'N/A' }}</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-purple-600 mb-1">OCR Serial</label>
                  <p class="text-sm text-purple-800 bg-purple-100 p-2 rounded border border-purple-200">{{
                    getVehicleOcrValue(selectedPolicyDetail, 'serial') || 'N/A' }}</p>
                </div>
              </div>

              <!-- Línea 5: Plate Number y OCR Plate Number -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Plate Number</label>
                  <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                    getVehicleFieldValue(selectedPolicyDetail, 'plate') || 'N/A' }}</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-purple-600 mb-1">OCR Plate Number</label>
                  <p class="text-sm text-purple-800 bg-purple-100 p-2 rounded border border-purple-200">{{
                    getVehicleOcrValue(selectedPolicyDetail, 'plate') || 'N/A' }}</p>
                </div>
              </div>

              <!-- Línea 6: Use y OCR Use -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Use</label>
                  <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                    getVehicleFieldValue(selectedPolicyDetail, 'usage') || 'N/A' }}</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-purple-600 mb-1">OCR Use</label>
                  <p class="text-sm text-purple-800 bg-purple-100 p-2 rounded border border-purple-200">{{
                    getVehicleOcrValue(selectedPolicyDetail, 'usage') || 'N/A' }}</p>
                </div>
              </div>

              <!-- Línea 7: Versión y Color -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Versión</label>
                  <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                    getVehicleFieldValue(selectedPolicyDetail, 'version') || 'N/A' }}</p>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                    getVehicleFieldValue(selectedPolicyDetail, 'color') || 'N/A' }}</p>
                </div>
              </div>

              <!-- Otros campos adicionales sin título -->
              <div *ngIf="getOtherVehicleFields(selectedPolicyDetail).length > 0"
                class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div *ngFor="let field of getOtherVehicleFields(selectedPolicyDetail)">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ field.label }}</label>
                  <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{ field.value }}</p>
                </div>
              </div>

              <!-- Fecha de actualización del vehículo -->
              <div *ngIf="getVehicleUpdateDate(selectedPolicyDetail)" class="mt-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">Fecha Actualización Vehículo</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  formatDateTime(getVehicleUpdateDate(selectedPolicyDetail)!) }}</p>
              </div>
            </div>
            </div>
          </div>

          <!-- Integration Data (colapsable) -->
          <div *ngIf="selectedPolicyDetail.params?.integration_data" class="bg-red-50 rounded-lg overflow-hidden">
            <button (click)="togglePolicySection('integration')" type="button"
              class="w-full flex items-center justify-between p-4 hover:bg-red-100 transition-colors text-left">
              <h3 class="text-lg font-semibold text-gray-900 flex items-center">
                <i class="fas fa-cogs text-red-600 mr-2"></i>
                Datos de Integración
              </h3>
              <i class="fas modal-section-chevron"
                [class.fa-chevron-right]="!isPolicySectionExpanded('integration')"
                [class.fa-chevron-down]="isPolicySectionExpanded('integration')"
                aria-hidden="true"></i>
            </button>
            <div *ngIf="isPolicySectionExpanded('integration')" class="px-4 pb-4 border-t border-red-100">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Estado de Emisión</label>
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" [ngClass]="{
                        'bg-yellow-100 text-yellow-800': selectedPolicyDetail.params.integration_data.emission_status === 'pending' || selectedPolicyDetail.params.integration_data.emission_status === 'PENDING',
                        'bg-blue-100 text-blue-800': selectedPolicyDetail.params.integration_data.emission_status === 'in_process' || selectedPolicyDetail.params.integration_data.emission_status === 'IN_PROCESS',
                        'bg-green-100 text-green-800': selectedPolicyDetail.params.integration_data.emission_status === 'emitted' || selectedPolicyDetail.params.integration_data.emission_status === 'SUCCESS',
                        'bg-emerald-100 text-emerald-800': selectedPolicyDetail.params.integration_data.emission_status === 'paid' || selectedPolicyDetail.params.integration_data.emission_status === 'PAID',
                        'bg-red-100 text-red-800': selectedPolicyDetail.params.integration_data.emission_status === 'emission_error' || selectedPolicyDetail.params.integration_data.emission_status === 'FAILED',
                        'bg-gray-100 text-gray-800': selectedPolicyDetail.params.integration_data.emission_status === 'cancelled' || selectedPolicyDetail.params.integration_data.emission_status === 'CANCELLED' || !selectedPolicyDetail.params.integration_data.emission_status
                      }">
                  {{ selectedPolicyDetail.params.integration_data.emission_status || 'Sin Estado' }}
                </span>
              </div>
              <!-- Nuevos campos solicitados -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">clientId</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params.integration_data.clientId || 'N/A' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">shopcartId</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params.integration_data.shopcartId || 'N/A' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">mt_pendiente_sin_igtf</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params.integration_data.mt_pendiente_sin_igtf || 'N/A' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">certificateNumber</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params.integration_data.certificateNumber || 'N/A' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">nu_cuota</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params.integration_data.nu_cuota || 'N/A' }}</p>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Fecha de Emisión</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params.integration_data.issue_date ?
                  formatDateTime(selectedPolicyDetail.params.integration_data.issue_date) : 'N/A' }}</p>
              </div>
              <div *ngIf="selectedPolicyDetail.params.integration_data.error_id">
                <label class="block text-sm font-medium text-gray-700 mb-1">ID de Error</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params.integration_data.error_id }}</p>
              </div>
              <div *ngIf="selectedPolicyDetail.params.integration_data.error_type">
                <label class="block text-sm font-medium text-gray-700 mb-1">Tipo de Error</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params.integration_data.error_type }}</p>
              </div>
              <div *ngIf="selectedPolicyDetail.params.integration_data.error_msg" class="md:col-span-3">
                <label class="block text-sm font-medium text-gray-700 mb-1">Mensaje de Error</label>
                <p class="text-sm text-gray-900 bg-white p-2 rounded border">{{
                  selectedPolicyDetail.params.integration_data.error_msg }}</p>
              </div>
              <div *ngIf="selectedPolicyDetail.params.integration_data.extra_data" class="md:col-span-3">
                <label class="block text-sm font-medium text-gray-700 mb-1">Datos Adicionales</label>
                <pre
                  class="text-xs text-gray-900 bg-white p-2 rounded border overflow-x-auto max-h-32">{{ selectedPolicyDetail.params.integration_data.extra_data }}</pre>
              </div>
            </div>
            </div>
          </div>

          <!-- Additional Fields — Dynamic (colapsable) -->
          <div class="bg-gray-50 rounded-lg overflow-hidden">
            <button (click)="togglePolicySection('additional')" type="button"
              class="w-full flex items-center justify-between p-4 hover:bg-gray-100 transition-colors text-left">
              <h3 class="text-lg font-semibold text-gray-900 flex items-center">
                <i class="fas fa-list text-gray-600 mr-2"></i>
                Información Adicional
              </h3>
              <i class="fas modal-section-chevron"
                [class.fa-chevron-right]="!isPolicySectionExpanded('additional')"
                [class.fa-chevron-down]="isPolicySectionExpanded('additional')"
                aria-hidden="true"></i>
            </button>
            <div *ngIf="isPolicySectionExpanded('additional')" class="px-4 pb-4 border-t border-gray-200">
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                <!-- Show any additional fields that might exist -->
                <div *ngFor="let item of getAdditionalFields(selectedPolicyDetail)">
                  <label class="block text-sm font-medium text-gray-700 mb-1">{{ item.label }}</label>
                  <p class="text-sm text-gray-900 bg-white p-2 rounded border" [innerHTML]="item.value"></p>
                </div>
              </div>
            </div>
          </div>

          <!-- Raw Data — JSON (colapsable, unificado con el patrón general) -->
          <div class="bg-gray-50 rounded-lg overflow-hidden">
            <button (click)="togglePolicySection('json')" type="button"
              class="w-full flex items-center justify-between p-4 hover:bg-gray-100 transition-colors text-left">
              <h3 class="text-lg font-semibold text-gray-900 flex items-center">
                <i class="fas fa-code text-gray-600 mr-2"></i>
                Datos Completos (JSON)
              </h3>
              <i class="fas modal-section-chevron"
                [class.fa-chevron-right]="!isPolicySectionExpanded('json')"
                [class.fa-chevron-down]="isPolicySectionExpanded('json')"
                aria-hidden="true"></i>
            </button>
            <div *ngIf="isPolicySectionExpanded('json')" class="px-4 pb-4 border-t border-gray-200">
              <div class="flex items-center justify-end mt-3 mb-2">
                <button (click)="copyJsonToClipboard()"
                  class="flex items-center px-3 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200"
                  title="Copiar JSON al portapapeles">
                  <i class="fas fa-copy mr-1"></i>
                  Copiar
                </button>
              </div>
              <div class="bg-white rounded border">
                <div class="flex items-center justify-between p-2 bg-gray-100 rounded-t border-b">
                  <span class="text-xs text-gray-600 font-medium">Datos JSON de la póliza</span>
                  <span class="text-xs text-gray-500">{{ (selectedPolicyDetail | json).length }} caracteres</span>
                </div>
                <pre
                  class="text-xs text-gray-900 p-3 overflow-x-auto max-h-96 whitespace-pre-wrap">{{ selectedPolicyDetail | json }}</pre>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Footer -->
      <div class="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
        <button (click)="closePolicyModal()"
          class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
          Cerrar
        </button>
      </div>
    </div>
  </div>

  <!-- Cuotas Mercantil Tab -->
  <div *ngIf="activeTab === 'sync_mercantil'" class="space-y-6">
    
    <!-- Header Section -->
    <div class="bg-white rounded-lg shadow-md p-6">
      <div class="flex items-center justify-between">
        <div>
          <h2 class="text-2xl font-bold text-gray-900 flex items-center">
            <i class="fas fa-university text-blue-600 mr-3"></i>
            Sincronización de Cuotas con Mercantil
          </h2>
          <p class="text-gray-600 mt-2">
            Visualiza los resultados de la última sincronización automática de cuotas de seguros con Mercantil
          </p>
        </div>
      </div>
    </div>
      <!-- Action Buttons -->
      <div class="bg-white rounded-lg shadow-md p-4">
        <div class="flex items-center justify-end space-x-3">
          <button (click)="exportSyncLogToExcel()" 
            [disabled]="isLoadingSyncLog || !syncLogData"
            class="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <i class="fas fa-file-excel mr-2"></i>
            Exportar
          </button>
          <button (click)="loadSyncLog()" 
            [disabled]="isLoadingSyncLog"
            class="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <i class="fas fa-sync-alt mr-2" [class.fa-spin]="isLoadingSyncLog"></i>
            Actualizar
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div *ngIf="isLoadingSyncLog" class="bg-white rounded-lg shadow-md p-12 text-center">
        <i class="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
        <p class="text-gray-600">Cargando datos de sincronización...</p>
      </div>

      <!-- Error State -->
      <div *ngIf="!isLoadingSyncLog && syncLogError" class="bg-white rounded-lg shadow-md p-8">
        <div class="text-center">
          <i class="fas fa-exclamation-circle text-5xl text-yellow-500 mb-4"></i>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">{{ syncLogError }}</h3>
          <p class="text-gray-600 mb-4">No se encontraron logs de sincronización disponibles</p>
          <button (click)="loadSyncLog()" 
            class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
            <i class="fas fa-sync-alt mr-2"></i>
            Reintentar
          </button>
        </div>
      </div>

      <!-- Data Display -->
      <div *ngIf="!isLoadingSyncLog && !syncLogError && syncLogData" class="space-y-6">
      
      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        
        <!-- Total Invoices -->
        <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs font-medium text-gray-600">Total Compras</p>
              <p class="text-2xl font-bold text-gray-900">
                {{ syncLogData.summary?.total_invoices || 0 }}
              </p>
            </div>
            <div class="p-3 bg-blue-100 rounded-full">
              <i class="fas fa-file-invoice text-blue-600 text-lg"></i>
            </div>
          </div>
        </div>

        <!-- Processed Invoices -->
        <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-green-500">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs font-medium text-gray-600">Procesadas</p>
              <p class="text-2xl font-bold text-gray-900">
                {{ syncLogData.summary?.processed_invoices || 0 }}
              </p>
            </div>
            <div class="p-3 bg-green-100 rounded-full">
              <i class="fas fa-check-circle text-green-600 text-lg"></i>
            </div>
          </div>
        </div>

        <!-- Updated Installments -->
        <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-purple-500">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs font-medium text-gray-600">Cuotas Actualizadas</p>
              <p class="text-2xl font-bold text-gray-900">
                {{ syncLogData.summary?.total_installments_updated || 0 }}
              </p>
            </div>
            <div class="p-3 bg-purple-100 rounded-full">
              <i class="fas fa-sync-alt text-purple-600 text-lg"></i>
            </div>
          </div>
        </div>

        <!-- Errors -->
        <div class="bg-white rounded-lg shadow-md p-4 border-l-4 border-red-500">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs font-medium text-gray-600">Errores</p>
              <p class="text-2xl font-bold text-gray-900">
                {{ syncLogData.summary?.errors || 0 }}
              </p>
            </div>
            <div class="p-3 bg-red-100 rounded-full">
              <i class="fas fa-exclamation-triangle text-red-600 text-lg"></i>
            </div>
          </div>
        </div>

      </div>

      <!-- Execution Info -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <i class="fas fa-info-circle text-blue-600 mr-2"></i>
          Información de Ejecución
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <p class="text-xs text-gray-600 mb-1">Fecha de Ejecución</p>
            <p class="text-sm font-medium text-gray-900">
              {{ syncLogData.execution_date | date:'dd/MM/yyyy HH:mm:ss' }}
            </p>
          </div>
          <div>
            <p class="text-xs text-gray-600 mb-1">Archivo</p>
            <p class="text-sm font-medium text-gray-900">
              {{ syncLogData.file_info?.filename }}
            </p>
          </div>
          <div>
            <p class="text-xs text-gray-600 mb-1">Tamaño del Archivo</p>
            <p class="text-sm font-medium text-gray-900">
              {{ (syncLogData.file_info?.file_size / 1024).toFixed(2) }} KB
            </p>
          </div>
        </div>
      </div>

      <!-- Details Table -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-gray-900 flex items-center">
            <i class="fas fa-list text-gray-600 mr-2"></i>
            Detalle de Compras Procesadas
          </h3>
          
          <!-- Status Filter -->
          <div class="flex items-center space-x-2">
            <label class="text-sm text-gray-600">Filtrar por estado:</label>
            <select [(ngModel)]="syncLogStatusFilter" 
              (change)="setSyncLogStatusFilter(syncLogStatusFilter)"
              class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="all">Todos</option>
              <option value="updated">Actualizadas</option>
              <option value="error">Con Errores</option>
              <option value="warning">Con Alertas</option>
              <option value="no_changes">Sin Cambios</option>
            </select>
          </div>
        </div>

        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Compra
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Póliza
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cuotas Actualizadas
                </th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Detalles
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr *ngFor="let detail of getFilteredSyncLogDetails()" class="hover:bg-gray-50">
                <td class="px-4 py-3 whitespace-nowrap">
                  <div class="text-sm font-medium text-gray-900">{{ detail.invoice_identifier }}</div>
                  <div class="text-xs text-gray-500">{{ detail.shopcart_id }}</div>
                </td>
                <td class="px-4 py-3 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ detail.policy_number }}</div>
                  <div class="text-xs text-gray-500">
                    <span [class]="detail.policy_status === 'active' ? 'text-green-600' : 
                                   detail.policy_status === 'in_debt' ? 'text-yellow-600' : 'text-gray-600'">
                      {{ detail.policy_status }}
                    </span>
                  </div>
                </td>
                <td class="px-4 py-3 whitespace-nowrap">
                  <span *ngIf="detail.installments_updated > 0" 
                    class="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    <i class="fas fa-check mr-1"></i>
                    Actualizada
                  </span>
                  <span *ngIf="detail.installments_updated === 0 && detail.installments?.length > 0 && hasErrorInDetail(detail)" 
                    class="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                    <i class="fas fa-times-circle mr-1"></i>
                    Error
                  </span>
                  <span *ngIf="detail.installments_updated === 0 && detail.installments?.length > 0 && !hasErrorInDetail(detail)" 
                    class="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                    <i class="fas fa-exclamation-triangle mr-1"></i>
                    Con Alertas
                  </span>
                  <span *ngIf="detail.installments_updated === 0 && (!detail.installments || detail.installments?.length === 0)" 
                    class="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                    <i class="fas fa-minus mr-1"></i>
                    Sin Cambios
                  </span>
                </td>
                <td class="px-4 py-3 whitespace-nowrap text-center">
                  <span class="text-sm font-medium text-gray-900">
                    {{ detail.installments_updated }}
                  </span>
                </td>
                <td class="px-4 py-3">
                  <div *ngIf="detail.installments && detail.installments.length > 0" class="space-y-1">
                    <div *ngFor="let installment of detail.installments" 
                      class="text-xs p-2 rounded"
                      [class]="installment.status === 'error' ? 'bg-red-50 text-red-700' : 
                               installment.status === 'warning' ? 'bg-yellow-50 text-yellow-700' : 
                               'bg-blue-50 text-blue-700'">
                      <div class="font-medium mb-1">
                        <i [class]="installment.status === 'error' ? 'fas fa-times-circle' : 
                                    installment.status === 'warning' ? 'fas fa-exclamation-circle' : 
                                    'fas fa-info-circle'"></i>
                        Cuota #{{ installment.installment_order }}
                        <span *ngIf="installment.receipt_number"> - Recibo: {{ installment.receipt_number }}</span>
                      </div>
                      <div>{{ installment.message }}</div>
                    </div>
                  </div>
                  <div *ngIf="!detail.installments || detail.installments.length === 0" class="text-xs text-gray-500">
                    Sin novedades
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div *ngIf="getFilteredSyncLogDetails().length === 0" 
          class="text-center py-12">
          <i class="fas fa-inbox text-4xl text-gray-400 mb-4"></i>
          <p class="text-gray-600" *ngIf="syncLogStatusFilter === 'all'">No hay detalles de sincronización disponibles</p>
          <p class="text-gray-600" *ngIf="syncLogStatusFilter !== 'all'">No hay compras con el estado seleccionado</p>
        </div>

        <!-- Pagination -->
        <div *ngIf="syncLogPagination" class="mt-6 flex items-center justify-between">
          <div class="text-sm text-gray-700">
            Mostrando {{ (syncLogPagination.currentPage - 1) * syncLogPagination.itemsPerPage + 1 }}
            -
            {{ Math.min(syncLogPagination.currentPage * syncLogPagination.itemsPerPage, syncLogPagination.totalItems) }}
            de {{ syncLogPagination.totalItems }} compras
          </div>
          <div class="flex space-x-2">
            <button [disabled]="syncLogPagination.currentPage <= 1"
              (click)="goToSyncLogPage(syncLogPagination.currentPage - 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="px-3 py-2 bg-blue-600 text-white rounded-md">
              {{ syncLogPagination.currentPage }}
            </span>
            <button [disabled]="syncLogPagination.currentPage >= syncLogPagination.totalPages"
              (click)="goToSyncLogPage(syncLogPagination.currentPage + 1)"
              class="px-3 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>

  <!-- Relaciones Caracas Tab -->
  <div *ngIf="activeTab === 'sync_caracas'" class="space-y-6">
      <!-- Date Selector and Actions -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-4">
            <label class="text-sm font-medium text-gray-700">Fecha de relación:</label>
            <input #scRelationDateInput type="date" [(ngModel)]="scRelationDate"
              (change)="scRelationDateInput.blur()"
              class="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
            <button type="button"
              (pointerdown)="scRelationDateInput.blur()"
              (click)="loadSCRelation()"
              [disabled]="isLoadingSCRelation || !scRelationDate"
              class="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <i class="fas fa-search mr-2" [class.fa-spin]="isLoadingSCRelation"></i>
              Buscar
            </button>
          </div>
          <button (click)="openAddDepositModal()"
            *ngIf="scRelationData && canEditDeposits"
            [disabled]="!scRelationData"
            class="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <i class="fas fa-plus mr-2"></i>
            Agregar Depósito
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div *ngIf="isLoadingSCRelation" class="bg-white rounded-lg shadow-md p-12 text-center">
        <i class="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
        <p class="text-gray-600">Cargando relación...</p>
      </div>

      <!-- Error State -->
      <div *ngIf="!isLoadingSCRelation && scRelationError" class="bg-white rounded-lg shadow-md p-8">
        <div class="text-center">
          <i class="fas fa-exclamation-circle text-5xl text-yellow-500 mb-4"></i>
          <h3 class="text-xl font-semibold text-gray-900 mb-2">{{ scRelationError }}</h3>
          <p class="text-gray-600 mb-4">No se encontró relación para la fecha seleccionada</p>
          <button (click)="fetchSCRelationFromAPI()" 
            [disabled]="isConsultingSC || !scRelationDate"
            class="mt-4 flex items-center justify-center mx-auto px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <i class="fas fa-cloud-download-alt mr-2" [class.fa-spin]="isConsultingSC"></i>
            {{ isConsultingSC ? 'Consultando Seguros Caracas...' : 'Consultar en Seguros Caracas' }}
          </button>
        </div>
      </div>

      <!-- SC Relation Data -->
      <div *ngIf="!isLoadingSCRelation && !scRelationError && scRelationData" class="space-y-6">

        <!-- Tabs Navigation -->
        <div class="bg-white rounded-lg shadow-md">
          <div class="border-b border-gray-200">
            <nav class="flex -mb-px">
              <button 
                (click)="setSCActiveTab('summary')"
                [class]="scActiveTab === 'summary' 
                  ? 'border-blue-500 text-blue-600 px-6 py-3 border-b-2 font-medium text-sm flex items-center'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 px-6 py-3 border-b-2 font-medium text-sm flex items-center'">
                <i class="fas fa-clipboard-check mr-2"></i>
                Resumen
              </button>
              <button 
                (click)="setSCActiveTab('sc-details')"
                [class]="scActiveTab === 'sc-details' 
                  ? 'border-blue-500 text-blue-600 px-6 py-3 border-b-2 font-medium text-sm flex items-center'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 px-6 py-3 border-b-2 font-medium text-sm flex items-center'">
                <i class="fas fa-file-invoice mr-2"></i>
                Pagos SC
                <span class="ml-2 px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">{{ scRelationData.total_records || 0 }}</span>
              </button>
              <button 
                (click)="setSCActiveTab('payments')"
                [class]="scActiveTab === 'payments' 
                  ? 'border-blue-500 text-blue-600 px-6 py-3 border-b-2 font-medium text-sm flex items-center'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 px-6 py-3 border-b-2 font-medium text-sm flex items-center'">
                <i class="fas fa-receipt mr-2"></i>
                Pagos Creditotal
                <span class="ml-2 px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800">{{ scPaymentsData?.count || 0 }}</span>
              </button>
              <button 
                (click)="setSCActiveTab('deposits')"
                [class]="scActiveTab === 'deposits' 
                  ? 'border-blue-500 text-blue-600 px-6 py-3 border-b-2 font-medium text-sm flex items-center'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 px-6 py-3 border-b-2 font-medium text-sm flex items-center'">
                <i class="fas fa-money-check-alt mr-2"></i>
                Depósitos
                <span class="ml-2 px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">{{ scRelationData.total_deposits || 0 }}</span>
              </button>
            </nav>
          </div>

          <!-- Tab Content -->
          <div class="p-6">

            <!-- SUMMARY TAB -->
            <div *ngIf="scActiveTab === 'summary'" class="space-y-6">
              
              <!-- Validation Panel -->
              <div class="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200">
                <div class="flex items-center justify-between mb-3">
                  <h4 class="text-lg font-semibold text-gray-900 flex items-center">
                    <i class="fas fa-tasks mr-2 text-blue-600"></i>
                    Validación de Montos
                  </h4>
                  <button
                    (click)="exportSCRelationToExcel()"
                    class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 shadow-sm">
                    <i class="fas fa-file-excel"></i>
                    <span class="text-sm font-medium">Exportar Excel</span>
                  </button>
                </div>

                <!-- Franja: Número de relación + Estado SC -->
                <div class="flex items-center gap-6 px-4 py-3 rounded-lg border mb-4"
                     [ngClass]="{
                       'bg-green-50  border-green-200':  scRelationData.header?.stRelacion === 'INGRESADA',
                       'bg-yellow-50 border-yellow-200': scRelationData.header?.stRelacion === 'INCOMPLETA',
                       'bg-red-50    border-red-200':    scRelationData.header?.stRelacion === 'ANULADA',
                       'bg-orange-50 border-orange-200': scRelationData.header?.stRelacion === 'PENDIENTE',
                       'bg-gray-50   border-gray-200':  !scRelationData.header?.stRelacion
                     }">
                  <div class="flex items-center gap-2">
                    <i class="fas fa-hashtag text-gray-400 text-sm"></i>
                    <span class="text-sm text-gray-500">Relación:</span>
                    <span class="text-xl font-bold text-blue-700">{{ scRelationData.header?.nuRelacion }}</span>
                  </div>
                  <div class="w-px h-6 bg-gray-300"></div>
                  <div class="flex items-center gap-2">
                    <span class="text-sm text-gray-500">Estado SC:</span>
                    <span class="px-3 py-1 text-sm font-bold rounded-full"
                          [ngClass]="{
                            'bg-green-100  text-green-800':  scRelationData.header?.stRelacion === 'INGRESADA',
                            'bg-yellow-100 text-yellow-800': scRelationData.header?.stRelacion === 'INCOMPLETA',
                            'bg-red-100    text-red-800':    scRelationData.header?.stRelacion === 'ANULADA',
                            'bg-orange-100 text-orange-800': scRelationData.header?.stRelacion === 'PENDIENTE',
                            'bg-gray-100   text-gray-500':  !scRelationData.header?.stRelacion
                          }">
                      <i class="fas mr-1"
                         [ngClass]="{
                           'fa-check-circle':       scRelationData.header?.stRelacion === 'INGRESADA',
                           'fa-exclamation-circle': scRelationData.header?.stRelacion === 'INCOMPLETA',
                           'fa-times-circle':       scRelationData.header?.stRelacion === 'ANULADA',
                           'fa-clock':              scRelationData.header?.stRelacion === 'PENDIENTE',
                           'fa-question-circle':   !scRelationData.header?.stRelacion
                         }"></i>
                      {{ scRelationData.header?.stRelacion || 'Sin consultar' }}
                    </span>
                  </div>
                  <div *ngIf="scRelationData.header?.stRelacion_query_date" class="ml-auto text-xs text-gray-400">
                    <i class="fas fa-sync-alt mr-1"></i>
                    {{ scRelationData.header?.stRelacion_query_date | date:'dd/MM/yyyy HH:mm' }}
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="bg-white rounded-lg p-4 shadow-sm">
                    <p class="text-xs text-gray-600 mb-1">Total Relación SC</p>
                    <p class="text-lg font-bold text-blue-700">{{ scRelationData.header?.total_mtPago_ves | number:'1.2-2' }} Bs</p>
                    <p class="text-xs text-gray-500 mt-1">{{ scRelationData.total_records || 0 }} pagos</p>
                  </div>
                  <div class="bg-white rounded-lg p-4 shadow-sm">
                    <p class="text-xs text-gray-600 mb-1">
                      Total Pagos Creditotal
                      <span class="text-gray-400"
                            title="Dinero recibido por CrediTotal ese día (por fecha de cobro).">
                        <i class="fas fa-money-bill-wave text-xs"></i>
                      </span>
                    </p>
                    <p class="text-lg font-bold text-purple-700">
                      {{ scPaymentsData?.total_ves | number:'1.2-2' }} Bs
                    </p>
                    <p class="text-xs text-gray-500 mt-1">
                      {{ scPaymentsData?.count ?? 0 }} pagos
                    </p>
                    <p *ngIf="scPaymentsData?.diffSummary?.missingCount > 0"
                       class="text-xs text-gray-500 mt-1">
                      <i class="fas fa-hourglass-half text-gray-400 mr-1"></i>
                      {{ scPaymentsData.diffSummary.missingCount }} recibo<span *ngIf="scPaymentsData.diffSummary.missingCount !== 1">s</span> de la relación aún sin cobrar
                    </p>
                  </div>
                  <div class="bg-white rounded-lg p-4 shadow-sm">
                    <p class="text-xs text-gray-600 mb-1">Total Depósitos</p>
                    <p class="text-lg font-bold" [class.text-green-700]="isTotalMatching()" [class.text-red-700]="!isTotalMatching()">
                      {{ getTotalDeposits() | number:'1.2-2' }} Bs
                    </p>
                    <p class="text-xs text-gray-500 mt-1">{{ scRelationData.total_deposits || 0 }} depósitos</p>
                  </div>
                </div>
                <!-- Validación Relación SC vs Dinero recibido en CT ese día (Tabla A) -->
                <div class="mt-4 p-4 rounded-lg"
                     [class.bg-green-100]="(scRelationData.header?.total_mtPago_ves || 0) === (scPaymentsData?.total_ves || 0)"
                     [class.bg-yellow-100]="(scRelationData.header?.total_mtPago_ves || 0) !== (scPaymentsData?.total_ves || 0)">
                  <p class="font-semibold flex items-center"
                     [class.text-green-800]="(scRelationData.header?.total_mtPago_ves || 0) === (scPaymentsData?.total_ves || 0)"
                     [class.text-yellow-800]="(scRelationData.header?.total_mtPago_ves || 0) !== (scPaymentsData?.total_ves || 0)">
                    <i class="fas mr-2"
                       [class.fa-check-circle]="(scRelationData.header?.total_mtPago_ves || 0) === (scPaymentsData?.total_ves || 0)"
                       [class.fa-exclamation-triangle]="(scRelationData.header?.total_mtPago_ves || 0) !== (scPaymentsData?.total_ves || 0)"></i>
                    <span *ngIf="(scRelationData.header?.total_mtPago_ves || 0) === (scPaymentsData?.total_ves || 0)">✅ La relación SC coincide con el dinero recibido en CrediTotal ese día</span>
                    <span *ngIf="(scRelationData.header?.total_mtPago_ves || 0) !== (scPaymentsData?.total_ves || 0)">⚠️ Diferencia entre lo reportado a SC ({{ scRelationData.header?.total_mtPago_ves | number:'1.2-2' }} Bs) y el dinero recibido en CT ese día ({{ scPaymentsData?.total_ves | number:'1.2-2' }} Bs) — Diferencia: {{ ((scPaymentsData?.total_ves || 0) - (scRelationData.header?.total_mtPago_ves || 0)) | number:'1.2-2' }} Bs</span>
                  </p>
                </div>

                <!-- Validación Depósitos vs dinero recibido en CT ese día (Tabla A).
                     Se oculta cuando aún no hay depósitos registrados. -->
                <div *ngIf="getTotalDeposits() > 0"
                     class="mt-4 p-4 rounded-lg" [class.bg-green-100]="isTotalMatching()" [class.bg-red-100]="!isTotalMatching()">
                  <p class="font-semibold flex items-center" [class.text-green-800]="isTotalMatching()" [class.text-red-800]="!isTotalMatching()">
                    <i [class.fas]="true" [class.fa-check-circle]="isTotalMatching()" [class.fa-exclamation-triangle]="!isTotalMatching()" class="mr-2"></i>
                    <span *ngIf="isTotalMatching()">✅ Depósitos coinciden con el dinero recibido ese día</span>
                    <span *ngIf="!isTotalMatching()">⚠️ Los depósitos no coinciden con lo cobrado ese día (Diferencia: {{ (getTotalDeposits() - (scPaymentsData?.total_ves || 0)) | number:'1.2-2' }} Bs)</span>
                  </p>
                </div>
              </div>
            </div>

            <!-- SC DETAILS TAB -->
            <div *ngIf="scActiveTab === 'sc-details'" class="space-y-6">
        <div class="bg-white rounded-lg shadow-md p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <i class="fas fa-list text-gray-600 mr-2"></i>
            Pagos Seguros Caracas
          </h3>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Póliza</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recibo</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto Recibo</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto Pago</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tasa</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr *ngFor="let detail of scRelationData.details" class="hover:bg-gray-50">
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ detail.nuPoliza }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ detail.nuRecibo }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {{ detail.mtRecibo | number:'1.2-2' }} {{ getCurrencySymbol(detail.cdMonedaRec) }}
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {{ detail.mtPago | number:'1.2-2' }} {{ getCurrencySymbol(detail.cdMonedaPgo) }}
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ detail.taCambio | number:'1.2-2' }}</td>
                </tr>
              </tbody>
              <tfoot class="bg-gray-100">
                <tr>
                  <td colspan="2" class="px-4 py-3 text-right text-sm font-semibold text-gray-900">Totales:</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-bold text-blue-700">
                    {{ scRelationData.header?.total_mtRecibo_usd | currency:'USD':'symbol':'1.2-2' }}
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-bold text-blue-700">
                    {{ scRelationData.header?.total_mtPago_ves | number:'1.2-2' }} Bs
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
            </div>

            <!-- PAYMENTS TAB -->
            <div *ngIf="scActiveTab === 'payments'" class="space-y-6">
              <div *ngIf="isLoadingSCPayments" class="bg-white rounded-lg shadow-md p-12 text-center">
                <i class="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
                <p class="text-gray-600">Cargando pagos registrados...</p>
              </div>

              <div *ngIf="!isLoadingSCPayments && scPaymentsError" class="bg-white rounded-lg shadow-md p-8 text-center">
                <i class="fas fa-exclamation-circle text-4xl text-yellow-500 mb-4"></i>
                <p class="text-gray-600">{{ scPaymentsError }}</p>
              </div>

              <!-- ─── TABLA A — Pagos cobrados en CT ese día ─── -->
              <div *ngIf="!isLoadingSCPayments && !scPaymentsError && scPaymentsData" class="bg-white rounded-lg shadow-md p-6">

                <!-- Copy explicativo + badge de estado de comparación -->
                <div class="mb-4 pb-3 border-b border-gray-100">
                  <p class="text-sm text-gray-600 mb-2">
                    <i class="fas fa-info-circle text-gray-400 mr-1"></i>
                    <strong>Pagos que Creditotal recibió físicamente en esta fecha.</strong>
                    Es lo que entró a nuestras cuentas ese día.
                  </p>

                  <!-- ✅ Cuadra con relación -->
                  <span *ngIf="scPaymentsData.tablesMatch === true"
                        class="inline-flex items-center gap-2 px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200">
                    <i class="fas fa-check-circle"></i>
                    Cuadra con la relación SC
                    <span *ngIf="scPaymentsData.relation?.nuRelacion"
                          class="text-green-600">(#{{ scPaymentsData.relation.nuRelacion }})</span>
                  </span>

                  <!-- ⚠ Difiere de la relación -->
                  <span *ngIf="scPaymentsData.tablesMatch === false"
                        class="inline-flex items-center gap-2 px-3 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                    <i class="fas fa-exclamation-triangle"></i>
                    Difiere de la relación SC
                    <span *ngIf="scPaymentsData.relation?.nuRelacion"
                          class="text-amber-700">(#{{ scPaymentsData.relation.nuRelacion }})</span>
                    <span class="text-amber-700">— ver detalle abajo</span>
                  </span>

                  <!-- 🔍 Sin relación consultada -->
                  <div *ngIf="scPaymentsData.tablesMatch === null" class="flex flex-wrap items-center gap-2">
                    <span class="inline-flex items-center gap-2 px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                      <i class="fas fa-search"></i>
                      Relación SC no consultada para esta fecha
                    </span>
                    <button (click)="fetchSCRelationFromAPI()"
                            [disabled]="isConsultingSC || !scRelationDate"
                            class="text-xs px-3 py-1 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      <i class="fas fa-cloud-download-alt mr-1" [class.fa-spin]="isConsultingSC"></i>
                      Consultar en Seguros Caracas
                    </button>
                  </div>
                </div>

                <h3 class="text-lg font-semibold text-gray-900 mb-4 flex flex-wrap items-center gap-2">
                  <i class="fas fa-receipt text-purple-600 mr-2"></i>
                  <span>Pagos Registrados en Creditotal ({{ scPaymentsData.count || 0 }})</span>
                  <span *ngIf="scPaymentsData.notification_errors_count"
                        class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                        title="Pagos cobrados al cliente cuya notificación a Seguros Caracas falló. Aparecen en la conciliación pero requieren reintento de notificación.">
                    <i class="fas fa-exclamation-triangle"></i>
                    {{ scPaymentsData.notification_errors_count }} con error de notif.
                  </span>
                </h3>
                <div class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Póliza</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recibo</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto Recibo (USD)</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto Pago (Bs)</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tasa</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notif. SC</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                      <tr *ngFor="let payment of scPaymentsData.payments"
                          class="hover:bg-gray-50"
                          [class.bg-amber-50]="payment.notificationError">
                        <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ payment.policy }}</td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {{ payment.receipt }}
                          <span *ngIf="payment.isDuplicate"
                                class="ml-1 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-200"
                                title="Hay otro pago en CT con este mismo recibo.">
                            <i class="fas fa-copy"></i> Duplicado
                          </span>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {{ payment.receiptAmount | currency:'USD':'symbol':'1.2-2' }}
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {{ payment.paymentAmount | number:'1.2-2' }} Bs
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ payment.exchangeRate | number:'1.2-2' }}</td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm">
                          <span *ngIf="payment.notificationError; else notifOk"
                                class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                                [title]="payment.errorDate
                                  ? 'Notificación a SC falló el ' + (payment.errorDate | date:'dd/MM/yyyy HH:mm') + '. El cobro al cliente fue exitoso.'
                                  : 'Notificación a SC falló. El cobro al cliente fue exitoso.'">
                            <i class="fas fa-exclamation-triangle"></i> Error
                          </span>
                          <ng-template #notifOk>
                            <span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200">
                              <i class="fas fa-check"></i> OK
                            </span>
                          </ng-template>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot class="bg-gray-100">
                      <tr>
                        <td colspan="2" class="px-4 py-3 text-right text-sm font-semibold text-gray-900">Totales:</td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm font-bold text-purple-700">
                          {{ scPaymentsData.total_usd | currency:'USD':'symbol':'1.2-2' }}
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm font-bold text-purple-700">
                          {{ scPaymentsData.total_ves | number:'1.2-2' }} Bs
                        </td>
                        <td></td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <!-- ─── TABLA B — Recibos de la Relación SC (esperados vs cobrados) ───
                   Solo se muestra cuando Tabla A y Tabla B difieren (hay missing
                   o extraInTableACount). Si tablesMatch === true, Tabla A ya lo
                   cuenta todo y esta tabla sería redundante. -->
              <div *ngIf="!isLoadingSCPayments && !scPaymentsError && scPaymentsData
                          && scPaymentsData.byRelation
                          && scPaymentsData.tablesMatch === false"
                   class="bg-white rounded-lg shadow-md p-6">

                <!-- Copy explicativo de Tabla B -->
                <div class="mb-4 pb-3 border-b border-gray-100">
                  <p class="text-sm text-gray-600">
                    <i class="fas fa-link text-gray-400 mr-1"></i>
                    <strong>Recibos de la relación SC #{{ scPaymentsData.relation?.nuRelacion }}</strong>
                    (fePago: {{ scPaymentsData.relation?.fePago | date:'dd/MM/yyyy':'UTC' }}).
                    Cada recibo se anota con el pago que lo liquidó ese día en CrediTotal.
                    Los recibos aún sin cobrar quedan marcados como pendientes.
                  </p>
                </div>

                <!-- Banner ámbar con resumen de diferencias (solo si hay algo que reportar) -->
                <div *ngIf="scPaymentsData.diffSummary
                            && (scPaymentsData.diffSummary.missingCount > 0
                                || scPaymentsData.diffSummary.extraInTableACount > 0)"
                     class="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <p class="font-semibold flex items-center gap-2 mb-1">
                    <i class="fas fa-exclamation-triangle"></i>
                    Diferencias detectadas:
                  </p>
                  <ul class="ml-6 list-disc space-y-1">
                    <li *ngIf="scPaymentsData.diffSummary.missingCount > 0">
                      <strong>{{ scPaymentsData.diffSummary.missingCount }}</strong> recibo<span *ngIf="scPaymentsData.diffSummary.missingCount !== 1">s</span> de la relación SC sin cobrar todavía en CT.
                    </li>
                    <li *ngIf="scPaymentsData.diffSummary.extraInTableACount > 0">
                      <strong>{{ scPaymentsData.diffSummary.extraInTableACount }}</strong> pago<span *ngIf="scPaymentsData.diffSummary.extraInTableACount !== 1">s</span> cobrado<span *ngIf="scPaymentsData.diffSummary.extraInTableACount !== 1">s</span> ese día que NO pertenece<span *ngIf="scPaymentsData.diffSummary.extraInTableACount === 1">n</span> a esta relación.
                    </li>
                  </ul>
                </div>

                <h3 class="text-lg font-semibold text-gray-900 mb-4 flex flex-wrap items-center gap-2">
                  <i class="fas fa-balance-scale text-blue-600 mr-2"></i>
                  <span>Recibos de la Relación SC (esperados vs cobrados) — {{ scPaymentsData.byRelation.payments.length }}</span>
                </h3>
                <div class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Póliza</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recibo</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto Recibo (USD)</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto Pago (Bs)</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tasa</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha cobro</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notif. SC</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                      <tr *ngFor="let payment of scPaymentsData.byRelation.payments"
                          class="hover:bg-gray-50"
                          [class.bg-gray-50]="payment.isMissing"
                          [class.bg-amber-50]="!payment.isMissing && payment.notificationError">
                        <td class="px-4 py-3 whitespace-nowrap text-sm"
                            [class.text-gray-400]="payment.isMissing"
                            [class.text-gray-900]="!payment.isMissing">{{ payment.policy }}</td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm"
                            [class.text-gray-400]="payment.isMissing"
                            [class.text-gray-900]="!payment.isMissing">
                          {{ payment.receipt }}
                          <span *ngIf="payment.isMissing"
                                class="ml-1 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-200 text-gray-700 border border-gray-300"
                                title="Este recibo está en la relación SC pero CT aún no lo ha cobrado.">
                            <i class="fas fa-hourglass-half"></i> Pendiente de cobro
                          </span>
                          <span *ngIf="payment.isDuplicate"
                                class="ml-1 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-200"
                                title="Hay otro pago en CT con este mismo recibo.">
                            <i class="fas fa-copy"></i> Duplicado
                          </span>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm"
                            [class.text-gray-400]="payment.isMissing"
                            [class.text-gray-900]="!payment.isMissing">
                          {{ payment.receiptAmount | currency:'USD':'symbol':'1.2-2' }}
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm"
                            [class.text-gray-400]="payment.isMissing"
                            [class.text-gray-900]="!payment.isMissing">
                          <span *ngIf="!payment.isMissing">{{ payment.paymentAmount | number:'1.2-2' }} Bs</span>
                          <span *ngIf="payment.isMissing">—</span>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm"
                            [class.text-gray-400]="payment.isMissing"
                            [class.text-gray-900]="!payment.isMissing">
                          <span *ngIf="!payment.isMissing">{{ payment.exchangeRate | number:'1.2-2' }}</span>
                          <span *ngIf="payment.isMissing">—</span>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm">
                          <span *ngIf="payment.isMissing" class="text-gray-400">—</span>
                          <span *ngIf="!payment.isMissing" class="text-gray-900">{{ payment.paymentDate | date:'dd/MM/yyyy HH:mm' }}</span>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm">
                          <ng-container *ngIf="!payment.isMissing">
                            <span *ngIf="payment.notificationError; else byRelNotifOk"
                                  class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 border border-amber-200"
                                  [title]="payment.errorDate
                                    ? 'Notificación a SC falló el ' + (payment.errorDate | date:'dd/MM/yyyy HH:mm') + '. El cobro al cliente fue exitoso.'
                                    : 'Notificación a SC falló. El cobro al cliente fue exitoso.'">
                              <i class="fas fa-exclamation-triangle"></i> Error
                            </span>
                            <ng-template #byRelNotifOk>
                              <span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200">
                                <i class="fas fa-check"></i> OK
                              </span>
                            </ng-template>
                          </ng-container>
                          <span *ngIf="payment.isMissing" class="text-gray-400">—</span>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot class="bg-gray-100">
                      <tr>
                        <td colspan="2" class="px-4 py-3 text-right text-sm font-semibold text-gray-900">Totales (matched):</td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm font-bold text-blue-700">
                          {{ scPaymentsData.byRelation.total_usd | currency:'USD':'symbol':'1.2-2' }}
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-sm font-bold text-blue-700">
                          {{ scPaymentsData.byRelation.total_ves | number:'1.2-2' }} Bs
                        </td>
                        <td colspan="3" class="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                          {{ scPaymentsData.byRelation.count }} cobrados
                          <span *ngIf="scPaymentsData.diffSummary?.missingCount > 0">
                            · {{ scPaymentsData.diffSummary.missingCount }} pendiente<span *ngIf="scPaymentsData.diffSummary.missingCount !== 1">s</span> de cobro
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div *ngIf="!isLoadingSCPayments && !scPaymentsError && (!scPaymentsData || scPaymentsData.count === 0)" class="bg-white rounded-lg shadow-md p-12 text-center">
                <i class="fas fa-inbox text-4xl text-gray-300 mb-4"></i>
                <p class="text-gray-500">No hay pagos registrados para esta fecha</p>
              </div>
            </div>

            <!-- DEPOSITS TAB -->
            <div *ngIf="scActiveTab === 'deposits'" class="space-y-6">

          <!-- Aviso solo lectura cuando el estado no permite modificaciones -->
          <div *ngIf="!canEditDeposits"
               class="flex items-center gap-3 px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            <i class="fas fa-lock text-blue-500 text-base"></i>
            <span>
              La relación está en estado
              <strong>{{ scRelationData.header?.stRelacion }}</strong>
              — los depósitos son de solo lectura. No es posible agregar, editar, eliminar ni reportar depósitos.
            </span>
          </div>

        <div *ngIf="scRelationData.deposits && scRelationData.deposits.length > 0" class="bg-white rounded-lg shadow-md p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-semibold text-gray-900 flex items-center">
              <i class="fas fa-money-bill text-green-600 mr-2"></i>
              Depósitos Registrados ({{ scRelationData.deposits.length }})
            </h3>
            <div class="flex items-center space-x-3">
              <!-- Solo mostrar badge de éxito si fue reportado exitosamente -->
              <span *ngIf="scRelationData.header.deposits_reported_to_sc === true" class="px-3 py-1 text-sm font-medium bg-green-100 text-green-800 rounded-full">
                <i class="fas fa-check-circle mr-1"></i>
                Reportado a SC
              </span>
              <!-- Botón reportar: visible siempre que el estado permita modificaciones -->
              <button
                *ngIf="canEditDeposits"
                (click)="reportDepositsToSC()"
                [disabled]="isReportingDeposits"
                [class]="scRelationData.header.deposits_reported_to_sc === false && scRelationData.header.deposits_sc_response ?
                  'px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center' :
                  'px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center'">
                <i [class]="scRelationData.header.deposits_reported_to_sc === false && scRelationData.header.deposits_sc_response ? 'fas fa-redo mr-2' : 'fas fa-paper-plane mr-2'" [class.fa-spin]="isReportingDeposits"></i>
                {{ isReportingDeposits ? 'Reportando...' : (scRelationData.header.deposits_reported_to_sc === false && scRelationData.header.deposits_sc_response ? 'Reintentar Reporte' : 'Reportar al Seguro') }}
              </button>
            </div>
          </div>
          
          <!-- Alerta de error del reporte fallido -->
          <div *ngIf="scRelationData.header.deposits_reported_to_sc === false && scRelationData.header.deposits_sc_response" 
               class="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-4">
            <div class="flex items-start">
              <i class="fas fa-exclamation-triangle text-red-500 mr-3 mt-1"></i>
              <div class="flex-1">
                <h4 class="text-sm font-semibold text-red-900">
                  Error en el Reporte Anterior
                  <button 
                    (click)="showReportError()"
                    class="ml-3 text-xs text-red-600 hover:text-red-800 underline font-normal">
                    Ver detalles completos
                  </button>
                </h4>
              </div>
            </div>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Banco</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nro. Depósito</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr *ngFor="let deposit of scRelationData.deposits" class="hover:bg-gray-50">
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    <div class="font-medium">{{ getBankName(deposit.cdBanco) }}</div>
                    <div class="text-xs text-gray-500">Código: {{ deposit.cdBanco }}</div>
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ deposit.nuDeposito }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ deposit.feDeposito | date:'dd/MM/yyyy':'UTC' }}</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {{ deposit.mtDeposito | number:'1.2-2' }} {{ getCurrencySymbol(deposit.cdMoneda) }}
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                    {{ getOperationType(deposit.tpOperacion) }}
                  </td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900 text-center">
                    <div *ngIf="canEditDeposits" class="flex items-center justify-center gap-2">
                      <button
                        (click)="openEditDepositModal(deposit)"
                        class="text-blue-600 hover:text-blue-800 transition-colors"
                        title="Editar depósito">
                        <i class="fas fa-edit"></i>
                      </button>
                      <button
                        (click)="deleteDeposit(deposit)"
                        class="text-red-600 hover:text-red-800 transition-colors"
                        title="Eliminar depósito">
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                    <span *ngIf="!canEditDeposits"
                          class="text-gray-400"
                          [title]="'Relación ' + scRelationData.header?.stRelacion + ': solo lectura'">
                      <i class="fas fa-lock"></i>
                    </span>
                  </td>
                </tr>
              </tbody>
              <tfoot class="bg-gray-100">
                <tr>
                  <td colspan="3" class="px-4 py-3 text-right text-sm font-semibold text-gray-900">Total Depósitos:</td>
                  <td class="px-4 py-3 whitespace-nowrap text-sm font-bold" [class.text-green-700]="isTotalMatching()" [class.text-red-700]="!isTotalMatching()">
                    {{ getTotalDeposits() | number:'1.2-2' }} Bs
                  </td>
                  <td colspan="2" class="px-4 py-3 whitespace-nowrap text-xs" [class.text-green-700]="isTotalMatching()" [class.text-red-700]="!isTotalMatching()">
                    <span *ngIf="isTotalMatching()" class="flex items-center">
                      <i class="fas fa-check-circle mr-1"></i> Coincide
                    </span>
                    <span *ngIf="!isTotalMatching()" class="flex items-center">
                      <i class="fas fa-exclamation-triangle mr-1"></i> No coincide
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        <div *ngIf="!scRelationData.deposits || scRelationData.deposits.length === 0" class="bg-white rounded-lg shadow-md p-12 text-center">
          <i class="fas fa-inbox text-4xl text-gray-300 mb-4"></i>
          <p class="text-gray-500">No hay depósitos registrados para esta relación</p>
        </div>
            </div>

          </div>
        </div>

      </div>
    </div>

  <!-- Add Deposit Modal -->
  <div *ngIf="showAddDepositModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 class="text-xl font-semibold text-gray-900 mb-4">Agregar Depósito</h3>
        
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Banco *</label>
            <select [(ngModel)]="newDeposit.cdBanco" 
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
              <option value="">Seleccione un banco</option>
              <option *ngFor="let bank of banks" [value]="bank.bankCode">{{ bank.name }}</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Número de Depósito *</label>
            <input type="text" [(ngModel)]="newDeposit.nuDeposito" 
              maxlength="12"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Número de comprobante (máx. 12 dígitos)">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
            <input type="date" [(ngModel)]="newDeposit.feDeposito" 
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Monto *</label>
            <input type="number" step="0.01" [(ngModel)]="newDeposit.mtDeposito" 
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="0.00">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Moneda *</label>
            <select [(ngModel)]="newDeposit.cdMoneda" disabled
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-gray-100">
              <option value="01">Bs</option>
              <option value="02">USD</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Tipo de Operación *</label>
            <select [(ngModel)]="newDeposit.tpOperacion" 
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent">
              <option value="D">Depósito</option>
              <option value="T">Transferencia</option>
            </select>
          </div>
        </div>
        
        <div class="flex items-center justify-end space-x-3 mt-6">
          <button (click)="closeAddDepositModal()" 
            [disabled]="isSavingDeposit"
            class="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50">
            Cancelar
          </button>
          <button (click)="saveDeposit()" 
            [disabled]="isSavingDeposit"
            class="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center">
            <i *ngIf="isSavingDeposit" class="fas fa-spinner fa-spin mr-2"></i>
            {{ isSavingDeposit ? 'Guardando...' : 'Guardar' }}
          </button>
        </div>
      </div>
    </div>

  <!-- Edit Deposit Modal -->
  <div *ngIf="showEditDepositModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg shadow-xl p-6 w-full max-w-md">
        <h3 class="text-xl font-semibold text-gray-900 mb-4">Editar Depósito</h3>
        
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Banco *</label>
            <select [(ngModel)]="editingDeposit.cdBanco" 
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="">Seleccione un banco</option>
              <option *ngFor="let bank of banks" [value]="bank.bankCode">{{ bank.name }}</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Número de Depósito *</label>
            <input type="text" [(ngModel)]="editingDeposit.nuDeposito" 
              maxlength="12"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Número de comprobante (máx. 12 dígitos)">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
            <input type="date" [(ngModel)]="editingDeposit.feDeposito" 
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Monto *</label>
            <input type="number" step="0.01" [(ngModel)]="editingDeposit.mtDeposito" 
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="0.00">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Moneda *</label>
            <select [(ngModel)]="editingDeposit.cdMoneda" disabled
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100">
              <option value="01">Bs</option>
              <option value="02">USD</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Tipo de Operación *</label>
            <select [(ngModel)]="editingDeposit.tpOperacion" 
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option value="D">Depósito</option>
              <option value="T">Transferencia</option>
            </select>
          </div>
        </div>
        
        <div class="flex items-center justify-end space-x-3 mt-6">
          <button (click)="closeEditDepositModal()" 
            [disabled]="isUpdatingDeposit"
            class="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors disabled:opacity-50">
            Cancelar
          </button>
          <button (click)="updateDeposit()" 
            [disabled]="isUpdatingDeposit"
            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center">
            <i *ngIf="isUpdatingDeposit" class="fas fa-spinner fa-spin mr-2"></i>
            {{ isUpdatingDeposit ? 'Actualizando...' : 'Actualizar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Annulled Policies Tab -->
    <div *ngIf="activeTab === 'annulled_policies'" class="space-y-6">
      <div class="bg-white rounded-lg shadow-sm p-6">
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-xl font-semibold text-gray-900">Gestión de Pólizas Anuladas</h3>
          <div class="flex items-center space-x-4">
            <span class="text-sm text-gray-600">
              Total: {{currentAnnulledPoliciesData?.pagination?.totalItems || 0}} pólizas
            </span>
            <button (click)="exportAnnulledPoliciesData()" 
              [disabled]="isExportingAnnulledPolicies || !currentAnnulledPoliciesData?.policies || currentAnnulledPoliciesData.policies.length === 0"
              class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
              <i [class]="isExportingAnnulledPolicies ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'"></i>
              <span class="text-sm font-medium">{{ isExportingAnnulledPolicies ? 'Exportando...' : 'Exportar' }}</span>
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div *ngIf="isLoadingAnnulledPolicies" class="flex flex-col items-center justify-center py-12">
          <i class="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
          <p class="text-gray-600">Cargando pólizas anuladas...</p>
        </div>

        <!-- Empty State -->
        <div *ngIf="!isLoadingAnnulledPolicies && (!currentAnnulledPoliciesData?.policies || currentAnnulledPoliciesData.policies.length === 0)" 
          class="flex flex-col items-center justify-center py-12 bg-gray-50 rounded-lg">
          <i class="fas fa-info-circle text-6xl text-gray-400 mb-4"></i>
          <p class="text-gray-600 text-lg font-medium mb-2">No hay pólizas anuladas</p>
          <p class="text-gray-500 text-sm">No se encontraron pólizas anuladas en el período seleccionado</p>
        </div>

        <!-- Policies Table -->
        <div *ngIf="!isLoadingAnnulledPolicies && currentAnnulledPoliciesData?.policies && currentAnnulledPoliciesData.policies.length > 0">
          <table class="min-w-full divide-y divide-gray-200 text-xs">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Código</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nro. Póliza</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asegurado</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Anulación</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Razón</th>
                <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr *ngFor="let policy of currentAnnulledPoliciesData.policies" class="hover:bg-gray-50">
                <td class="px-3 py-3">
                  <div class="text-xs font-mono text-gray-900">
                    {{ policy.policy_code || 'N/A' }}
                  </div>
                  <div class="text-xs text-gray-500">{{ getProductName(policy.insurance_product) }}{{ policy.frequency ? ' - ' + policy.frequency : '' }}</div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="text-xs text-gray-900">{{ policy.policy_number || 'N/A' }}</div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{policy.policy_holder?.name || 'N/A'}}</div>
                  <div class="text-xs text-gray-500">{{policy.policy_holder?.identity || 'N/A'}}</div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <i class="fas fa-ban mr-1"></i>
                    ANULADA
                  </span>
                </td>
                <td class="px-3 py-3 whitespace-nowrap">
                  <div class="text-xs text-gray-900">
                    {{formatDateTime(policy.annulment_date)}}
                  </div>
                  <div class="text-xs text-gray-500" *ngIf="policy.issue_date">
                    Emitida: {{formatDateTime(policy.issue_date)}}
                  </div>
                </td>
                <td class="px-3 py-3" style="max-width: 300px;">
                  <div class="text-xs text-gray-700 line-clamp-2">
                    {{policy.annulment_reason || 'N/A'}}
                  </div>
                </td>
                <td class="px-3 py-3 whitespace-nowrap text-xs font-medium">
                  <button (click)="viewPolicyDetail(policy._id)" class="text-blue-600 hover:text-blue-900">
                    <i class="fas fa-eye mr-1"></i>
                    Ver Detalle
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Pagination -->
          <div class="mt-4 flex items-center justify-between border-t pt-4">
            <div class="text-sm text-gray-600">
              Mostrando {{(currentAnnulledPoliciesData.pagination.currentPage - 1) * currentAnnulledPoliciesData.pagination.itemsPerPage + 1}} - {{currentAnnulledPoliciesData.pagination.currentPage * currentAnnulledPoliciesData.pagination.itemsPerPage > currentAnnulledPoliciesData.pagination.totalItems ? currentAnnulledPoliciesData.pagination.totalItems : currentAnnulledPoliciesData.pagination.currentPage * currentAnnulledPoliciesData.pagination.itemsPerPage}} de {{currentAnnulledPoliciesData.pagination.totalItems}} pólizas
            </div>
            <div class="flex items-center space-x-2">
              <button [disabled]="currentAnnulledPoliciesData.pagination.currentPage <= 1"
                (click)="loadAnnulledPoliciesPage(currentAnnulledPoliciesData.pagination.currentPage - 1)"
                class="p-2 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed">
                <i class="fas fa-chevron-left"></i>
              </button>
              <span class="px-3 py-2 bg-red-600 text-white rounded-md">
                {{currentAnnulledPoliciesData.pagination.currentPage}}
              </span>
              <button [disabled]="currentAnnulledPoliciesData.pagination.currentPage >= currentAnnulledPoliciesData.pagination.totalPages"
                (click)="loadAnnulledPoliciesPage(currentAnnulledPoliciesData.pagination.currentPage + 1)"
                class="p-2 text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed">
                <i class="fas fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

<!-- Report Result Modal -->
<div *ngIf="showReportResultModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
  <div class="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-xl font-semibold text-gray-900">Resultado del Reporte</h3>
      <button (click)="closeReportResultModal()" class="text-gray-400 hover:text-gray-600">
        <i class="fas fa-times text-xl"></i>
      </button>
    </div>
    
    <div *ngIf="reportResult" class="space-y-4">
      
      <!-- Success Result -->
      <div *ngIf="reportResult.success" class="bg-green-50 rounded-lg p-4">
        <h4 class="font-semibold text-green-900 mb-3 flex items-center">
          <i class="fas fa-check-circle text-green-600 mr-2"></i>
          Reporte Exitoso
        </h4>
        <p class="text-sm text-gray-700 mb-3">{{ reportResult.message }}</p>
        <div class="grid grid-cols-2 gap-3 text-sm" *ngIf="reportResult.data">
          <div>
            <span class="text-gray-600">Relación:</span>
            <span class="ml-2 font-medium">{{ reportResult.data.nuRelacion }}</span>
          </div>
          <div>
            <span class="text-gray-600">Depósitos reportados:</span>
            <span class="ml-2 font-medium">{{ reportResult.data.total_deposits }}</span>
          </div>
        </div>
        <div *ngIf="reportResult.data?.sc_response" class="mt-3">
          <span class="text-gray-600 font-medium text-sm">Respuesta de SC:</span>
          <pre class="mt-1 text-xs bg-white p-3 rounded border border-green-200 overflow-x-auto">{{ reportResult.data.sc_response | json }}</pre>
        </div>
      </div>

      <!-- Error Result -->
      <div *ngIf="!reportResult.success" class="bg-red-50 rounded-lg p-4">
        <h4 class="font-semibold text-red-900 mb-3 flex items-center">
          <i class="fas fa-exclamation-triangle text-red-600 mr-2"></i>
          Error al Reportar
        </h4>
        <p class="text-sm text-gray-700 mb-3">{{ reportResult.message }}</p>
        <div *ngIf="reportResult.error?.sc_response" class="mt-3">
          <span class="text-red-600 font-medium text-sm">Respuesta de SC:</span>
          <pre class="mt-1 text-xs bg-white p-3 rounded border border-red-200 overflow-x-auto">{{ reportResult.error.sc_response | json }}</pre>
        </div>
      </div>

    </div>
    
    <div class="flex items-center justify-end space-x-3 mt-6">
      <button (click)="closeReportResultModal()" 
        class="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
        Cerrar
      </button>
    </div>
  </div>
</div>

<!-- Modal de Resolución de Errores -->
<div *ngIf="showResolveErrorModal" 
  class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
  style="z-index: 9999;"
  (click)="closeResolveErrorModal()">
  <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full" (click)="$event.stopPropagation()">
    
    <!-- Header -->
    <div class="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4 rounded-t-lg">
      <div class="flex items-center justify-between">
        <h3 class="text-xl font-bold text-white flex items-center">
          <i class="fas fa-check-circle mr-2"></i>
          Marcar Error como Resuelto
        </h3>
        <button 
          (click)="closeResolveErrorModal()"
          class="text-white hover:text-gray-200 transition-colors"
          [disabled]="isResolvingError">
          <i class="fas fa-times text-xl"></i>
        </button>
      </div>
    </div>

    <!-- Body -->
    <div class="p-6">
      <!-- Policy Info -->
      <div class="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <div class="flex items-center space-x-2 mb-2">
          <i class="fas fa-file-alt text-blue-600"></i>
          <span class="font-semibold text-blue-900">Póliza:</span>
          <span class="text-blue-700">{{ resolvingPolicyCode }}</span>
        </div>
        <div class="flex items-center space-x-2">
          <i class="fas fa-exclamation-triangle text-orange-600"></i>
          <span class="font-semibold text-gray-700">Tipo de Error:</span>
          <span class="text-gray-600">{{ resolveErrorType === 'emission' ? 'Error de Emisión' : 'Error de Pago' }}</span>
        </div>
      </div>

      <!-- Form -->
      <div class="space-y-4">
        <!-- Resolution Comment -->
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            <i class="fas fa-comment-alt mr-1"></i>
            ¿Cómo se resolvió el error? *
          </label>
          <textarea
            [(ngModel)]="resolutionComment"
            rows="5"
            placeholder="Describe detalladamente cómo se resolvió el error, qué acciones se tomaron, etc."
            class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
            [disabled]="isResolvingError"
          ></textarea>
          <p class="mt-1 text-xs text-gray-500">
            <i class="fas fa-info-circle mr-1"></i>
            Este comentario quedará registrado con tu usuario y fecha automáticamente
          </p>
        </div>
      </div>

      <!-- Info Message -->
      <div class="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
        <div class="flex items-start space-x-2">
          <i class="fas fa-info-circle text-yellow-600 mt-0.5"></i>
          <div class="text-sm text-yellow-800">
            <p class="font-semibold mb-1">Importante:</p>
            <p>Al marcar este error como resuelto, la póliza dejará de aparecer en la lista de errores. Esta acción quedará registrada con tu nombre y fecha.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer -->
    <div class="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end space-x-3">
      <button
        (click)="closeResolveErrorModal()"
        [disabled]="isResolvingError"
        class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <i class="fas fa-times mr-2"></i>
        Cancelar
      </button>
      <button
        (click)="submitResolveError()"
        [disabled]="isResolvingError || !resolutionComment.trim()"
        class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
      >
        <i class="fas fa-spinner fa-spin mr-2" *ngIf="isResolvingError"></i>
        <i class="fas fa-check-circle mr-2" *ngIf="!isResolvingError"></i>
        {{ isResolvingError ? 'Guardando...' : 'Marcar como Resuelto' }}
      </button>
    </div>
  </div>

</div>
</div>